import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getAuthToken } from '../../utils/auth';

const PreBillCreationModal = ({ billData, onConfirm, onCancel, onClose, onModify, token, workflowContext, runId: propRunId, workflowId: propWorkflowId, chatService, onViewPdf, hasUploadedPdf }) => {
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedItems, setSelectedItems] = useState({});
  const [loading, setLoading] = useState(false);
  const [billTranDate, setBillTranDate] = useState('');  // transaction date (YYYY-MM-DD), editable in modal
  const [billDueDate, setBillDueDate] = useState('');    // due date (YYYY-MM-DD), editable in modal
  // Track edits per-field so we only OVERRIDE a date the user actually changed.
  // Untouched dates keep existing behaviour (PO due date stays NetSuite-calculated).
  const [billTranDateTouched, setBillTranDateTouched] = useState(false);
  const [billDueDateTouched, setBillDueDateTouched] = useState(false);
  // Editable header Memo. Defaults to the PO number ONLY when PO-connected, else empty.
  const [billMemo, setBillMemo] = useState('');
  const [billMemoTouched, setBillMemoTouched] = useState(false);
  const [allNetSuiteItems, setAllNetSuiteItems] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [lineItemTaxCodes, setLineItemTaxCodes] = useState({}); // Local state for line item tax codes (lineNumber -> tax_code_id)
  const [lineItemLocations, setLineItemLocations] = useState({}); // Local state for line item locations (lineNumber -> location_id)
  const [lineItemDepartments, setLineItemDepartments] = useState({}); // Local state for line item departments (lineNumber -> department_id)
  const [lineItemClasses, setLineItemClasses] = useState({}); // Local state for line item classes (lineNumber -> class_id)
  const [allExpenseAccounts, setAllExpenseAccounts] = useState([]);
  const [expenseAccountSearch, setExpenseAccountSearch] = useState('');
  const [allLocations, setAllLocations] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [itemSearchTerms, setItemSearchTerms] = useState({});
  const [selectedTaxCode, setSelectedTaxCode] = useState('');
  const [allTaxCodes, setAllTaxCodes] = useState([]);
  const [taxCodeSearchTerms, setTaxCodeSearchTerms] = useState({}); // { lineNumber: searchTerm }
  const [nextExpenseId, setNextExpenseId] = useState(0);
  const [expensesInitialized, setExpensesInitialized] = useState(false);
  const [itemSelectionsInitialized, setItemSelectionsInitialized] = useState(false); // Track if item selections have been initialized
  const [lastBillDataRef, setLastBillDataRef] = useState(null);
  const [lastModifiedBillDataRef, setLastModifiedBillDataRef] = useState(null); // Track billData changes

  // State for per-line-item all items loading
  const [lineItemsAllItems, setLineItemsAllItems] = useState({}); // { lineNumber: [items] }
  const [loadingLineItems, setLoadingLineItems] = useState({}); // { lineNumber: boolean }

  // State for location/department/class loading
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [departmentsLoaded, setDepartmentsLoaded] = useState(false);
  const [classesLoaded, setClassesLoaded] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);

  // State for loading all vendors
  const [allVendors, setAllVendors] = useState([]); // All loaded vendors
  const [loadingAllVendors, setLoadingAllVendors] = useState(false); // Loading state
  const [vendorSearchTerm, setVendorSearchTerm] = useState(''); // Search within loaded vendors

  // 🏢 State for subsidiary selection
  const [allSubsidiaries, setAllSubsidiaries] = useState([]); // All loaded subsidiaries
  const [loadingSubsidiaries, setLoadingSubsidiaries] = useState(false); // Loading state
  const [selectedSubsidiary, setSelectedSubsidiary] = useState(null); // Selected subsidiary override

  // 💱 State for currency handling
  const [matchedCurrency, setMatchedCurrency] = useState(null); // Matched currency { code, symbol, id }

  // State for removed line items - using ref to persist across re-renders
  const [removedLineItems, setRemovedLineItems] = useState(new Set()); // Set of line_numbers to exclude
  const removedLineItemsRef = useRef(new Set()); // Ref backup to persist even if state is reset

  // ✏️ Per-PO-line quantity overrides for the review grid, keyed by po_line_number.
  // Empty by default. Mirrored into a ref (same idiom as removedLineItemsRef) so an
  // edit survives a state reset during submit.
  const [editedQuantities, setEditedQuantities] = useState({});
  const editedQuantitiesRef = useRef({});
  // ✏️ RATE EDITS (PO-linked). Same keying as editedQuantities (qtyKeyFor).
  const [editedRates, setEditedRates] = useState({});
  const editedRatesRef = useRef({});

  // State for AI-powered chat modification
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [modifiedBillData, setModifiedBillData] = useState(null);
  const [renderKey, setRenderKey] = useState(0); // Force re-render when AI modifies data
  const chatMessagesEndRef = useRef(null);

  // Normalize any date string to YYYY-MM-DD for <input type="date">; '' if unparseable.
  const toInputDate = (v) => {
    if (!v) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; // already correct
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  };

  // Seed the editable transaction date + due date from the bill data (until the user edits).
  // Re-source from modifiedBillData when the AI updates the bill via chat, so the input
  // fields reflect AI-applied date changes. A user-typed value still wins (billDatesTouched guard).
  useEffect(() => {
    const src = modifiedBillData || billData || {};
    const bd = src?.bill_data || src || {};
    // Transaction date default: invoice/bill date, falling back to the PO date.
    const tran = toInputDate(bd.invoice_date || bd.bill_date || bd.tranDate || bd.po_data?.po_date || '');
    if (!billTranDateTouched) setBillTranDate(tran);
    // Due date default: leave EMPTY for ALL bills so NetSuite derives the due date from
    // the vendor's payment terms unless the user explicitly overrides it in the modal.
    // The field shows an "override only" hint instead of a pre-filled auto date, and a
    // due date is only sent downstream when the user actually sets one (see submit payload
    // gating below and the backend, which sends dueDate only from user-confirmed dates).
    if (!billDueDateTouched) {
      setBillDueDate('');
    }
    // Memo default: PO number ONLY when PO-connected, else empty. User edit wins (touched guard).
    if (!billMemoTouched) {
      const poNum = (bd.po_data || bd.po_reference) ? (bd.po_data?.po_number || '') : '';
      setBillMemo(poNum);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billData, modifiedBillData]);

  // State for bill creation result
  const [billCreationStatus, setBillCreationStatus] = useState(null); // 'success', 'error', null
  const [billCreationMessage, setBillCreationMessage] = useState('');
  const [netSuiteBillUrl, setNetSuiteBillUrl] = useState('');

  // State for modal minimize
  const [isMinimized, setIsMinimized] = useState(false);

  // State to track if bill-creation-ready payload has been initialized
  const [billPayloadInitialized, setBillPayloadInitialized] = useState(false);

  // 🔒 State to freeze display data during bill creation
  const [frozenDisplayData, setFrozenDisplayData] = useState(null);

  // 🏢 Initialize selectedSubsidiary from billData when modal opens (for drafts)
  // ⚠️ Only set selectedSubsidiary if we have the FULL subsidiary object with name
  // Do NOT set it when we only have subsidiary_id - let getCurrentSubsidiary() handle that
  useEffect(() => {
    // Check if billData has FULL subsidiary info (from saved draft)
    const subsidiaryFromBillData = billData?.subsidiary;

    if (subsidiaryFromBillData && subsidiaryFromBillData.id && subsidiaryFromBillData.name) {
      // billData has a full subsidiary object with name - use it
      console.log('🏢 Initializing selectedSubsidiary from billData.subsidiary:', subsidiaryFromBillData);
      setSelectedSubsidiary({
        id: subsidiaryFromBillData.id,
        name: subsidiaryFromBillData.name || subsidiaryFromBillData.subsidiary_name,
        legalname: subsidiaryFromBillData.legalname || subsidiaryFromBillData.subsidiary_legal_name,
        country: subsidiaryFromBillData.country
      });
    }
    // ⚠️ Do NOT set selectedSubsidiary when only subsidiary_id exists
    // Let getCurrentSubsidiary() look up the name from workflowContext or allSubsidiaries
  }, [billData?.subsidiary]);

  // 🔧 Fetch locations, departments, classes, customers when billData AND subsidiary_id are available
  useEffect(() => {
    const authToken = getAuthToken() || token;
    if (!authToken || !billData) {
      console.log('📍 Skipping location/dept/class/customer fetch - no auth token or billData');
      return;
    }

    // 🏢 FIX: Check selectedSubsidiary FIRST (user override), then fall back to workflow context
    const subsidiaryId = selectedSubsidiary?.id
      || billData?.subsidiary_id
      || billData?.subsidiary?.id
      || workflowContext?.subsidiary_identification_result?.subsidiary_data?.subsidiary_id
      || workflowContext?.subsidiary_id;

    // 🔧 FIX: Wait for subsidiary_id to be available before fetching
    if (!subsidiaryId) {
      console.log('📍 Skipping location/dept/class/customer fetch - waiting for subsidiary_id');
      return;
    }

    const subsidiaryParam = `?subsidiary_id=${subsidiaryId}`;
    console.log('📍 Fetching locations, departments, classes, customers with subsidiary_id:', subsidiaryId, selectedSubsidiary ? '(USER OVERRIDE)' : '(from context)');

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';

    // Fetch locations
    fetch(`${API_BASE_URL}/api/bill-processing/locations${subsidiaryParam}`, {
      headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.locations) {
          console.log(`✅ Fetched ${data.locations.length} locations`);
          setAllLocations(data.locations);
        }
      })
      .catch(err => console.error('❌ Error fetching locations:', err));

    // Fetch departments
    fetch(`${API_BASE_URL}/api/bill-processing/departments${subsidiaryParam}`, {
      headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.departments) {
          console.log(`✅ Fetched ${data.departments.length} departments`);
          setAllDepartments(data.departments);
        }
      })
      .catch(err => console.error('❌ Error fetching departments:', err));

    // Fetch classes
    fetch(`${API_BASE_URL}/api/bill-processing/classes${subsidiaryParam}`, {
      headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.classes) {
          console.log(`✅ Fetched ${data.classes.length} classes`);
          setAllClasses(data.classes);
        }
      })
      .catch(err => console.error('❌ Error fetching classes:', err));

    // Fetch customers (with subsidiary_id)
    fetch(`${API_BASE_URL}/api/bill-processing/customers${subsidiaryParam}`, {
      headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.customers) {
          console.log(`✅ Fetched ${data.customers.length} customers`);
          setAllCustomers(data.customers);
        }
      })
      .catch(err => console.error('❌ Error fetching customers:', err));
  }, [token, billData, workflowContext, selectedSubsidiary]); // Re-fetch when billData or selectedSubsidiary changes

  // 🎯 Initialize line item AND expense item location/dept/class from AI-selected defaults
  useEffect(() => {
    // 🔧 FIX: Skip if user explicitly changed subsidiary (modifiedBillData will have default_location explicitly set to null)
    // This prevents re-applying old defaults after subsidiary change
    if (modifiedBillData && modifiedBillData.hasOwnProperty('default_location') && modifiedBillData.default_location === null) {
      console.log('⏭️ Skipping defaults initialization - subsidiary was changed, defaults were explicitly cleared');
      return;
    }

    const billDefaults = workflowContext?.bill_defaults_result?.bill_data;

    // 🎯 For PO-linked bills: Get defaults from PO items
    // For standalone bills: Get defaults from AI bill_defaults
    const poData = billData?.po_data;
    const poLineItems = poData?.line_items || [];
    const firstPoItem = poLineItems[0];

    // Use PO defaults if PO-linked, otherwise use AI defaults
    // PO items have location_id, class_id (IDs) - convert to object format {id: "..."}
    const poLocation = firstPoItem?.location_id ? { id: String(firstPoItem.location_id) } : firstPoItem?.location;
    const poClass = firstPoItem?.class_id ? { id: String(firstPoItem.class_id) } : firstPoItem?.class;
    const poDepartment = firstPoItem?.department_id ? { id: String(firstPoItem.department_id) } : firstPoItem?.department;

    // 🔧 FIX: Check modifiedBillData FIRST (may be null after subsidiary change), then billData, then workflowContext
    // When subsidiary changes, modifiedBillData.default_location is set to null to clear old defaults
    // If modifiedBillData exists and has null defaults, DON'T fall back to billData (user cleared them)
    const hasModifiedData = modifiedBillData !== null;
    const defaultLocation = (poData && poLocation?.id) ? poLocation : (
      hasModifiedData
        ? modifiedBillData?.default_location  // Use modified data (may be null after subsidiary change)
        : (billDefaults?.default_location || billData?.default_location)
    );
    const defaultDepartment = (poData && poDepartment?.id) ? poDepartment : (
      hasModifiedData
        ? modifiedBillData?.default_department
        : (billDefaults?.default_department || billData?.default_department)
    );
    const defaultClass = (poData && poClass?.id) ? poClass : (
      hasModifiedData
        ? modifiedBillData?.default_class
        : (billDefaults?.default_class || billData?.default_class)
    );

    console.log('🔍 PO Item fields:', firstPoItem ? {
      location_id: firstPoItem.location_id,
      class_id: firstPoItem.class_id,
      department_id: firstPoItem.department_id
    } : 'No PO item');

    // Exit early only if no defaults from either source
    if (!defaultLocation?.id && !defaultDepartment?.id && !defaultClass?.id) return;

    console.log('🎯 Defaults Found:', {
      source: poData ? 'PO' : 'AI',
      defaultLocation,
      defaultDepartment,
      defaultClass
    });

    // Get current bill data
    const currentData = modifiedBillData || billData;

    // === LINE ITEMS ===
    // For PO-linked: Use each line item's own location/dept/class OR fall back to first item's values
    const lineItems = poData ? poLineItems : (currentData?.line_items || []);
    if (lineItems.length > 0) {
      const newLocations = {};
      const newDepartments = {};
      const newClasses = {};

      lineItems.forEach(item => {
        const lineNum = item?.line_number || item?.orderLine;
        if (lineNum) {
          // 🎯 For PO items: Use item's own values first (location_id, class_id, department_id)
          // Then fall back to first item's values, then to defaults
          const itemLocation = item?.location_id || item?.location?.id || defaultLocation?.id;
          const itemDepartment = item?.department_id || item?.department?.id || defaultDepartment?.id;
          const itemClass = item?.class_id || item?.class?.id || defaultClass?.id;

          if (itemLocation && !lineItemLocations[lineNum]) {
            newLocations[lineNum] = String(itemLocation);
          }
          if (itemDepartment && !lineItemDepartments[lineNum]) {
            newDepartments[lineNum] = String(itemDepartment);
          }
          if (itemClass && !lineItemClasses[lineNum]) {
            newClasses[lineNum] = String(itemClass);
          }
        }
      });

      if (Object.keys(newLocations).length > 0) {
        setLineItemLocations(prev => ({ ...prev, ...newLocations }));
        console.log(`📍 Pre-selected line item locations from ${poData ? 'PO' : 'AI'}:`, newLocations);
      }
      if (Object.keys(newDepartments).length > 0) {
        setLineItemDepartments(prev => ({ ...prev, ...newDepartments }));
        console.log(`🏛️ Pre-selected line item departments from ${poData ? 'PO' : 'AI'}:`, newDepartments);
      }
      if (Object.keys(newClasses).length > 0) {
        setLineItemClasses(prev => ({ ...prev, ...newClasses }));
        console.log(`🏷️ Pre-selected line item classes from ${poData ? 'PO' : 'AI'}:`, newClasses);
      }
    }

    // === EXPENSE ITEMS ===
    // 🎯 FIX: Update the `expenses` state directly, not modifiedBillData
    // The UI renders from `expenses` state, so we need to update that
    setExpenses(prevExpenses => {
      if (prevExpenses.length === 0) return prevExpenses;

      let needsUpdate = false;
      const updatedExpenses = prevExpenses.map(expense => {
        const updated = { ...expense };

        // Only set if not already set
        if (defaultLocation?.id && !expense.location?.id) {
          updated.location = defaultLocation;
          needsUpdate = true;
        }
        if (defaultDepartment?.id && !expense.department?.id) {
          updated.department = defaultDepartment;
          needsUpdate = true;
        }
        if (defaultClass?.id && !expense.class?.id) {
          updated.class = defaultClass;
          needsUpdate = true;
        }

        return updated;
      });

      if (needsUpdate) {
        console.log(`💰 Applied ${poData ? 'PO' : 'AI'} defaults to expenses state:`, {
          location: defaultLocation?.id,
          department: defaultDepartment?.id,
          class: defaultClass?.id,
          expenseCount: updatedExpenses.length
        });
        return updatedExpenses;
      }
      return prevExpenses;
    });
  }, [workflowContext?.bill_defaults_result, billData, modifiedBillData]);

  // 🧹 Helper function to sanitize data by removing control characters
  // This handles corrupted JSON responses from AI when token limit is hit
  const sanitizeData = (data) => {
    if (!data) return data;

    // Helper to remove control characters from strings
    const sanitizeString = (str) => {
      if (typeof str !== 'string') return str;
      // Remove control characters: \u0000-\u001F (C0 controls) and \u007F-\u009F (DEL + C1 controls)
      return str.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
    };

    // Recursively sanitize object properties
    const sanitizeObject = (obj) => {
      if (obj === null || obj === undefined) return obj;
      if (typeof obj === 'string') return sanitizeString(obj);
      if (typeof obj !== 'object') return obj;

      if (Array.isArray(obj)) {
        return obj.map(item => sanitizeObject(item));
      }

      const sanitized = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          sanitized[key] = sanitizeObject(obj[key]);
        }
      }
      return sanitized;
    };

    return sanitizeObject(data);
  };

  // 🔄 CRITICAL FIX: Use useMemo to create derived state that triggers re-renders
  // MUST be declared AFTER modifiedBillData state above
  const currentBillData = useMemo(() => {
    // 🔒 If display is frozen during bill creation, use frozen data
    if (frozenDisplayData) {
      console.log('🔒 Using frozen display data (bill creation in progress)');
      return frozenDisplayData;
    }

    const rawData = modifiedBillData || billData;
    // 🧹 Sanitize data to remove control characters from corrupted AI responses
    const data = sanitizeData(rawData);

    console.log('🔄 currentBillData updated:', {
      hasModified: !!modifiedBillData,
      globalDiscount: {
        rate: data?.discount_rate,
        amount: data?.discount_amount
      },
      firstLineDiscount: data?.line_items?.[0] ? {
        rate: data.line_items[0].discount_rate,
        amount: data.line_items[0].discount_amount
      } : null,
      invoice_number: data?.invoice_number,
      line_items_count: data?.line_items?.length,
      expense_items_count: data?.expense?.items?.length,
      timestamp: new Date().toISOString()
    });
    return data;
  }, [modifiedBillData, billData, frozenDisplayData]);

  // Keep the function for backward compatibility in event handlers
  // ⚠️ NOTE: In JSX, use currentBillData directly, NOT this function!
  const getCurrentBillData = () => {
    // Safety check to prevent "Cannot access before initialization" errors
    if (typeof currentBillData === 'undefined') {
      console.warn('⚠️ getCurrentBillData called before currentBillData initialized, returning billData');
      return billData;
    }
    return currentBillData;
  };

  // 💱 Helper function to get currency symbol for display
  const getCurrencySymbol = () => {
    // Priority: matchedCurrency > currentBillData currency_symbol > PDF currency > default $
    if (matchedCurrency?.symbol) {
      return matchedCurrency.symbol + ' ';
    }
    if (currentBillData?.currency_symbol) {
      return currentBillData.currency_symbol + ' ';
    }
    // Map common currency codes to symbols
    const currencyCode = matchedCurrency?.code || currentBillData?.currency;
    const currencyMap = {
      'USD': '$ ',
      'SGD': 'S$ ',
      'EUR': '€ ',
      'GBP': '£ ',
      'JPY': '¥ ',
      'CNY': '¥ ',
      'INR': '₹ ',
      'AUD': 'A$ ',
      'CAD': 'C$ '
    };
    return currencyMap[currencyCode] || '$ ';
  };

  // 💱 Helper function to format amount with thousand separators
  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '0.00';
    const num = parseFloat(amount);
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Track previous message count to detect new messages
  const prevMessageCountRef = useRef(0);

  // ✅ FIX: Only auto-scroll when NEW messages arrive AND user is near bottom
  useEffect(() => {
    // Don't auto-scroll if there are no messages
    if (!chatMessages || chatMessages.length === 0) {
      prevMessageCountRef.current = 0;
      return;
    }

    // Check if this is a new message being added
    const isNewMessage = chatMessages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = chatMessages.length;

    // Only proceed if this is a new message
    if (!isNewMessage) return;

    // Check if user has manually scrolled up by checking scroll position
    const chatContainer = chatMessagesEndRef.current?.parentElement;
    if (!chatContainer) {
      // If container not ready, try to scroll anyway (initial load)
      setTimeout(() => scrollToBottom(), 100);
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = chatContainer;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Only auto-scroll if user is within 150px of the bottom (they haven't scrolled up)
    // This preserves user's scroll position when they're reading earlier messages
    if (distanceFromBottom < 150) {
      setTimeout(() => scrollToBottom(), 50);
    }
  }, [chatMessages]);

  // ✅ FIX: Initialize bill-creation-ready payload when modal opens
  // This ensures the modal displays the EXACT payload that will be sent to bill creation
  // Preprocessing phase (workflow) → User modification phase (modal) with consistent payload
  useEffect(() => {
    // Only initialize once when billData first arrives and hasn't been modified yet
    if (billData && !modifiedBillData && !billPayloadInitialized) {
      console.log('🔧 INIT: Preparing bill-creation-ready payload from workflow billData');
      console.log('🔧 INIT: This payload will be the single source of truth for display AND bill creation');

      // This payload will be displayed in modal AND sent to bill creation
      // Any user modifications (AI chat, manual edits) will update this same payload
      const billCreationPayload = {
        ...billData,
        // Metadata to track this is the preprocessed payload ready for bill creation
        _payload_status: 'bill_creation_ready',
        _initialized_at: new Date().toISOString()
      };

      // Set as the initial modifiedBillData so currentBillData returns this
      setModifiedBillData(billCreationPayload);
      setBillPayloadInitialized(true);

      console.log('✅ INIT: Bill-creation-ready payload initialized:', {
        invoice_number: billCreationPayload.invoice_number,
        line_items: billCreationPayload.line_items?.length,
        expenses: billCreationPayload.expense?.items?.length,
        total_amount: billCreationPayload.total_amount,
        source: 'workflow_preprocessing'
      });
    }
  }, [billData, modifiedBillData, billPayloadInitialized]);

  // ✅ FIX: Reset initialization when billData changes (new workflow run)
  useEffect(() => {
    // If billData changes to a new workflow run, reset initialization
    const newRunId = billData?.run_id || billData?.runId;
    const currentRunId = modifiedBillData?.run_id || modifiedBillData?.runId;

    if (newRunId && currentRunId && newRunId !== currentRunId) {
      console.log('🔄 INIT: New workflow run detected, resetting payload initialization');
      console.log(`   Old run: ${currentRunId}, New run: ${newRunId}`);
      setBillPayloadInitialized(false);
      setModifiedBillData(null);
    }
  }, [billData?.run_id, billData?.runId]);

  // Load existing chat messages when modal opens
  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        // ✅ FIX: Get runId from getCurrentBillData() to include AI modifications
        const runIdFromProps = propRunId;
        const runIdFromContext = workflowContext?.runId;
        const currentData = getCurrentBillData();
        const runIdFromBillData = currentData?.run_id;
        const effectiveRunId = runIdFromProps || runIdFromContext || runIdFromBillData;

        if (!effectiveRunId || !token) {
          console.log('⚠️ Cannot load chat history: missing runId or token');
          return;
        }

        console.log('📨 Loading chat history for runId:', effectiveRunId);

        const response = await fetch(`/api/v2/workflows/${effectiveRunId}/events`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.error('❌ Failed to load chat history:', response.statusText);
          return;
        }

        const data = await response.json();
        console.log('📨 Chat history loaded:', data);

        // Extract chat messages (only assistant messages with plain text content)
        const chatHistory = (data.updates || [])
          .filter(update =>
            update.type === 'chat_message' &&
            update.data.message_type === 'assistant' &&
            typeof update.data.content === 'string' &&
            !update.data.content.startsWith('{') // Exclude JSON messages
          )
          .map(update => ({
            role: 'assistant',
            content: update.data.content,
            timestamp: update.data.timestamp
          }));

        console.log('💬 Extracted chat messages:', chatHistory.length);

        if (chatHistory.length > 0) {
          // ✅ FIX: Merge workflow messages with existing user/AI chat messages
          // Don't replace - preserve user messages from AI assistant chat
          setChatMessages(prev => {
            // Keep user messages and AI responses from modify-bill-chat
            const userAndAiMessages = prev.filter(msg => msg.role === 'user' || msg.source === 'ai_chat');

            // Merge with workflow messages, avoiding duplicates
            const mergedMessages = [...userAndAiMessages];

            chatHistory.forEach(workflowMsg => {
              // Only add if not already present
              const isDuplicate = mergedMessages.some(m =>
                m.content === workflowMsg.content && m.timestamp === workflowMsg.timestamp
              );
              if (!isDuplicate) {
                mergedMessages.push(workflowMsg);
              }
            });

            // Sort by timestamp if available
            return mergedMessages.sort((a, b) => {
              if (!a.timestamp || !b.timestamp) return 0;
              return new Date(a.timestamp) - new Date(b.timestamp);
            });
          });
          console.log('✅ Chat messages merged (workflow + AI assistant)');
        }

      } catch (error) {
        console.error('❌ Error loading chat history:', error);
      }
    };

    loadChatHistory();

    // ✅ FIX: Poll for new messages every 2 seconds while modal is open
    // ⚠️ IMPORTANT: Stop polling once bill creation completes (success or failure)
    // This prevents workflow messages from overwriting AI assistant chat responses
    const shouldPoll = billCreationStatus === null || billCreationStatus === undefined;

    let pollInterval;
    if (shouldPoll) {
      pollInterval = setInterval(() => {
        loadChatHistory();
      }, 2000);
      console.log('🔄 Started polling for workflow messages');
    } else {
      console.log('⏸️ Skipping polling - bill creation completed with status:', billCreationStatus);
    }

    // Cleanup: stop polling when modal closes
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        console.log('🛑 Stopped polling for chat messages');
      }
    };
  }, [propRunId, workflowContext?.runId, billData?.run_id, token, billCreationStatus]); // Load when modal opens

  // Handle removing a line item
  const handleRemoveLineItem = (lineNumber) => {
    console.log(`🗑️ Removing line item: ${lineNumber}`);
    removedLineItemsRef.current.add(lineNumber); // Update ref immediately
    setRemovedLineItems(prev => {
      const newSet = new Set(prev);
      newSet.add(lineNumber);
      console.log(`📊 Removed items Set now has ${newSet.size} items:`, Array.from(newSet));
      return newSet;
    });
  };

  // Handle restoring a removed line item
  const handleRestoreLineItem = (lineNumber) => {
    console.log(`↩️ Restoring line item: ${lineNumber}`);
    removedLineItemsRef.current.delete(lineNumber); // Update ref immediately
    setRemovedLineItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(lineNumber);
      console.log(`📊 Removed items Set now has ${newSet.size} items:`, Array.from(newSet));
      return newSet;
    });
  };

  // Note: NetSuite items should be fetched through workflow, not direct API calls
  // The workflow should provide all available items in the bill_data when unmatched items exist

  // ✅ Backend sends FLAT structure directly - no need for actualData workaround
  // billData contains { line_items, vendor, po_data, ... } at root level

  // Determine bill type
  const isPOLinked = billData?.po_data || billData?.po_reference;

  // Helper to get the correct po_data structure - use AI-modified data if available
  const getPoData = () => {
    const currentData = modifiedBillData || billData;
    return currentData?.po_data;
  };

  // ✏️ QTY EDIT (PO-linked only). PO lines carry po_line_number; line_number is a
  // PDF-side field. Returns null when neither exists, in which case the user-edit
  // lookup is skipped entirely — no index fallback, so the grid and the payload can
  // never mis-key (the payload filters removed rows before mapping, so indexes differ).
  const qtyKeyFor = (item) => {
    const k = item?.po_line_number ?? item?.line_number;
    return k == null ? null : String(k);
  };

  // Invoiced (PDF) quantity for a PO line, or null when absent.
  const pdfQtyFor = (item) => {
    const q = parseFloat(item?.quantity);
    return Number.isFinite(q) && q > 0 ? q : null;
  };

  // Quantity that will actually be billed.
  // Precedence: user edit → PDF/invoiced qty → open qty → ordered qty.
  // The last two steps are the pre-change fallback chain, unchanged.
  const effectiveQtyFor = (item) => {
    const map = Object.keys(editedQuantitiesRef.current).length > 0
      ? editedQuantitiesRef.current : editedQuantities;
    const k = qtyKeyFor(item);
    if (k !== null) {
      const raw = map[k];
      if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
        const n = parseFloat(raw);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
    const pdf = pdfQtyFor(item);
    if (pdf !== null) return pdf;
    const open = parseFloat(item?.open_for_billing_quantity);
    if (Number.isFinite(open) && open > 0) return open;
    return parseFloat(item?.quantity_ordered) || parseFloat(item?.quantity) || 1;
  };

  // 🔗 The PDF line a PO line maps to, or null. Uses the strongest key available —
  // the SAME three-key chain as the expense detection, because runs that skip the
  // po_mismatch_resolution step carry NO pdf_line_number (e.g. PO01005660):
  //   1. pdf_line_number (exact, stamped by the resolution step)
  //   2. SKU: PDF item_search.extracted_sku_or_code === PO item_name (exact)
  //   3. description substring (last resort)
  const pdfLineFor = (item) => {
    const pdfLines = getCurrentBillData()?.original_pdf_line_items || [];
    if (!pdfLines.length) return null;

    const pln = item?.pdf_line_number;
    if (pln !== null && pln !== undefined) {
      const byNum = pdfLines.find(p => String(p?.line_number) === String(pln));
      if (byNum) return byNum;
    }

    const nrm = (s) => String(s === null || s === undefined ? '' : s).trim().toLowerCase();
    const name = nrm(item?.item_name);
    if (!name) return null;

    const bySku = pdfLines.find(p => nrm(p?.item_search?.extracted_sku_or_code) === name);
    if (bySku) return bySku;

    return pdfLines.find(p => {
      const d = nrm(p?.description);
      return d && (d.includes(name) || name.includes(d));
    }) || null;
  };

  // 💲 Unit price AS INVOICED ON THE PDF for a PO line, or null when unavailable.
  const pdfRateFor = (item) => {
    const match = pdfLineFor(item);
    if (!match) return null;
    const r = parseFloat(match.unit_price);
    return Number.isFinite(r) && r >= 0 ? r : null;
  };

  // Rate that will actually be billed.
  // Precedence: user edit → PDF/invoiced unit price → PO rate.
  // The PDF carries the price the vendor actually charged, so it wins over the PO rate.
  const effectiveRateFor = (item) => {
    const map = Object.keys(editedRatesRef.current).length > 0
      ? editedRatesRef.current : editedRates;
    const k = qtyKeyFor(item);
    if (k !== null) {
      const raw = map[k];
      if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
        const n = parseFloat(raw);
        if (Number.isFinite(n) && n >= 0) return n;
      }
    }
    const pdf = pdfRateFor(item);
    if (pdf !== null) return pdf;
    const po = parseFloat(item?.rate);
    return Number.isFinite(po) ? po : 0;
  };

  // Line total actually being billed = effective qty × effective rate.
  const effectiveAmountFor = (item) => {
    const q = effectiveQtyFor(item);
    const r = effectiveRateFor(item);
    if (!Number.isFinite(q) || !Number.isFinite(r)) return null;
    return q * r;
  };

  // Helper to detect actual mismatches between PO and PDF line items
  // Only show mismatch if item count is different
  const hasLineItemMismatch = () => {
    if (!isPOLinked || !getCurrentBillData().original_pdf_line_items || !getPoData()?.line_items) {
      return false;
    }

    const poItems = getPoData()?.line_items || [];
    const pdfItems = getCurrentBillData().original_pdf_line_items || [];

    // Only check if count is different
    return poItems.length !== pdfItems.length;
  };

  // Initialize selections
  useEffect(() => {
    // ✅ Use modifiedBillData if available (from AI chat), otherwise use billData
    const currentData = modifiedBillData || billData;

    if (currentData) {
      // ✅ Check if billData OR modifiedBillData has ACTUALLY CHANGED - reset expense initialization
      const billDataChanged = lastBillDataRef !== billData;
      const modifiedBillDataChanged = lastModifiedBillDataRef !== modifiedBillData;

      if (billDataChanged || modifiedBillDataChanged) {
        // 🔧 FIX: Do NOT reset expensesInitialized if this is a subsidiary change
        // Subsidiary change sets default_location to null explicitly
        const isSubsidiaryChange = modifiedBillData &&
          modifiedBillData.hasOwnProperty('default_location') &&
          modifiedBillData.default_location === null;

        if (isSubsidiaryChange) {
          console.log('🏢 Subsidiary change detected - NOT resetting expenses initialization');
          console.log('  (expenses were already cleared by handleSubsidiaryChange)');
        } else {
          // 🔧 FIX: Do NOT reset expensesInitialized here!
          // Resetting it causes expenses to be re-initialized from backend when user:
          // 1. Removes an expense (expenses.length becomes 0)
          // 2. Selects an item (modifiedBillData changes)
          // This would restore the removed expenses from backend data.
          // Only subsidiary change (handled above) should trigger expense reset.
          console.log('🔄 Data changed but NOT resetting expenses initialization (user may have modified expenses)');
          console.log('  billDataChanged:', billDataChanged, 'modifiedBillDataChanged:', modifiedBillDataChanged);
        }
        setLastBillDataRef(billData);
        setLastModifiedBillDataRef(modifiedBillData);
      }

      // ✅ Backend sends FLAT structure - use currentData directly

      // Set default vendor
      if (isPOLinked) {
        if (getPoData()?.vendor_name) {
          const vendorId = getPoData().vendor_name.split(' ')[0];
          setSelectedVendor(vendorId);
        }
      } else if (currentData.available_vendors && currentData.available_vendors.length > 0) {
        // If there are vendor candidates, pre-select the first one
        const firstVendor = currentData.available_vendors[0];
        // ✅ FIX: Use internal numeric ID (id) for bill creation, not entity_id string
        const vendorInternalId = firstVendor.id || firstVendor.netsuite_vendor_id;
        setSelectedVendor(vendorInternalId);
        console.log(`✅ Selected vendor internal ID: ${vendorInternalId} (entity_id: ${firstVendor.entityid || firstVendor.entity_id})`);
      } else if (currentData.vendor) {
        // Otherwise use the matched vendor
        // ✅ FIX: Prioritize internal numeric ID over entity_id string
        const vendorInternalId = currentData.vendor.id || currentData.vendor.netsuite_vendor_id || currentData.vendor.entity_id || currentData.vendor.entityid;
        setSelectedVendor(vendorInternalId);
        console.log(`✅ Selected vendor from currentData: ${vendorInternalId}`);
      }

      // Initialize item selections from CURRENT data (modifiedBillData or billData)
      // 🔧 FIX: Only initialize once to prevent resetting user selections
      if (!itemSelectionsInitialized) {
        const itemSelections = {};
        const lineItems = isPOLinked ? getPoData()?.line_items : currentData.line_items;

        if (lineItems) {
          // Filter out null/undefined items before iterating
          lineItems.filter(item => item !== null && item !== undefined).forEach(item => {
            console.log(`🔍 DEBUG - Line ${item.line_number}: available_items count = ${item.available_items ? item.available_items.length : 0}, matched = ${!!item.matched_netsuite_item}`);
            if (isPOLinked) {
              itemSelections[item.line_number] = item.item_name;
            } else if (item?.matched_netsuite_item) {
              itemSelections[item.line_number] = item.matched_netsuite_item.netsuite_item_id;
            } else {
              // Don't auto-select single available items - user must confirm
              itemSelections[item.line_number] = '';
            }
          });
        }
        setSelectedItems(itemSelections);
        setItemSelectionsInitialized(true);
        console.log('✅ Item selections initialized:', itemSelections);
      }

      // 🔧 Initialize expenses if they exist in bill data (for BOTH PO and standalone bills)
      // Only initialize once to prevent resetting user edits when useEffect reruns
      // ⚠️ IMPORTANT: Don't overwrite if expenses already have data (from AI chat sync)
      if (!expensesInitialized && expenses.length === 0) {
        console.log('🔄 useEffect: Expenses NOT initialized yet, starting initialization...');
        console.log('🔄 useEffect: currentData source:', modifiedBillData ? 'MODIFIED (from AI)' : 'ORIGINAL (from props)');
        const expenseItems = [];
        let expenseIdCounter = 0;

      // 1. Add existing expenses from bill_data.expense.items (if any)
      if (currentData.expense && currentData.expense.items) {
        console.log(`🔄 useEffect: Found ${currentData.expense.items.length} expenses in currentData`);
        currentData.expense.items.forEach(exp => {
          // Handle account field - convert string to object if needed
          let accountObj;
          if (typeof exp.account === 'object' && exp.account !== null) {
            accountObj = exp.account;
          } else if (typeof exp.account === 'string') {
            accountObj = { id: exp.account };
          } else if (exp.account_id) {
            accountObj = { id: exp.account_id };
          } else {
            accountObj = { id: '' };
          }

          const expenseItem = {
            id: expenseIdCounter++,
            account: accountObj,
            account_id: exp.account_id || exp.account || '',
            amount: exp.amount || '',
            memo: exp.memo || exp.description || '',
            name: exp.name || exp.account_name || '',
            account_name: exp.account_name || exp.name || '',
            tax_code_id: exp.tax_code_id || exp.taxcode?.id || '',  // 🔧 FIX: Also check taxcode.id from backend format
            tax_rate: exp.tax_rate || null,
            source: exp.source || 'existing', // ✅ Preserve source from backend (ai_added, pdf_detected, etc.)
            added_by_ai: exp.source === 'ai_added', // ✅ Set flag for AI-added expenses
            confidence: exp.confidence || null,
            original_description: exp.original_description || null,
            // 🎯 For PO-linked: Use PO line item's location/dept/class as defaults (IDs converted to object format)
            // For standalone: Use AI defaults from bill_defaults_result
            location: exp.location || (isPOLinked && getPoData()?.line_items?.[0]?.location_id ? { id: String(getPoData().line_items[0].location_id) } : null) || workflowContext?.bill_defaults_result?.bill_data?.default_location || null,
            department: exp.department || (isPOLinked && getPoData()?.line_items?.[0]?.department_id ? { id: String(getPoData().line_items[0].department_id) } : null) || workflowContext?.bill_defaults_result?.bill_data?.default_department || null,
            class: exp.class || (isPOLinked && getPoData()?.line_items?.[0]?.class_id ? { id: String(getPoData().line_items[0].class_id) } : null) || workflowContext?.bill_defaults_result?.bill_data?.default_class || null,
            customer: exp.customer || null
          };
          console.log(`🔄 useEffect: Initializing expense from backend data - source: ${exp.source}, will be set to: ${expenseItem.source}`);

          // 🔧 AUTO-APPLY PDF TAX: Only if expense doesn't already have tax code
          // Note: allTaxCodes will be populated asynchronously, so this may not work on first render
          // But it will work when user reopens the modal or if expenses are initialized after tax codes load
          if (!expenseItem.tax_code_id && currentData.tax_rate && allTaxCodes && allTaxCodes.length > 0) {
            const pdfTaxRate = parseFloat(currentData.tax_rate);
            const matchingTaxCode = allTaxCodes.find(tc => {
              if (tc.rate) {
                const tcRate = parseFloat(tc.rate) * 100; // Convert 0.09 to 9
                return Math.abs(tcRate - pdfTaxRate) < 0.1; // Match within 0.1%
              }
              return false;
            });

            if (matchingTaxCode) {
              expenseItem.tax_code_id = matchingTaxCode.id;
              expenseItem.tax_rate = pdfTaxRate;
              console.log(`✅ Auto-applied PDF tax to existing expense: ${pdfTaxRate}% (tax_code_id=${expenseItem.tax_code_id})`);
            }
          }
          // Tax code already preserved above at line 265-266

          expenseItems.push(expenseItem);
        });
        console.log(`✅ Found ${currentData.expense.items.length} existing expenses`);
      }

      // 2. For PO bills: Detect potential expenses from PDF that aren't in PO
      if (isPOLinked && currentData.original_pdf_line_items && Array.isArray(currentData.original_pdf_line_items)) {
        // Use original_pdf_line_items from backend (contains raw PDF extracted items)
        console.log('🔍 Debug - Checking for original_pdf_line_items:', currentData.original_pdf_line_items);
        console.log('🔍 Debug - PO data:', getPoData());

        const originalItems = currentData.original_pdf_line_items;
        const poItems = getPoData()?.line_items || [];

        // ✅ WHICH PDF LINES ARE MAPPED TO A PO LINE. Walk the PO lines and claim the PDF
        // line each one maps to; every PDF line left unclaimed becomes an expense.
        // Per PO line, use the strongest key available:
        //   1. `pdf_line_number` — stamped by the po_mismatch_resolution step (exact).
        //   2. SKU — PDF `item_search.extracted_sku_or_code` vs PO `item_name` (exact).
        //      Needed because runs that skip the mismatch-resolution step have NO
        //      pdf_line_number (e.g. PO01005660: line_number 1,2 / pdf_line_number null).
        //   3. Description substring — the original behaviour, last resort.
        // Key 3 alone is unreliable: PO item_name is a SKU ("HW1S-2T") while the PDF
        // description is prose ("HW 2 Position Selector Switch…"), so they share no
        // substring and genuinely-mapped items were being listed as expenses.
        const _norm = (s) => String(s === null || s === undefined ? '' : s).trim().toLowerCase();
        const matchedPdfLines = new Set();
        poItems.forEach(poItem => {
          if (poItem && poItem.pdf_line_number !== null && poItem.pdf_line_number !== undefined) {
            matchedPdfLines.add(String(poItem.pdf_line_number));
            return;
          }
          const poName = _norm(poItem && poItem.item_name);
          if (!poName) return;
          // 2. exact SKU match
          let hit = originalItems.find(p => _norm(p && p.item_search && p.item_search.extracted_sku_or_code) === poName);
          // 3. description substring, either direction
          if (!hit) {
            hit = originalItems.find(p => {
              const d = _norm(p && p.description);
              return d && (d.includes(poName) || poName.includes(d));
            });
          }
          if (hit && hit.line_number !== null && hit.line_number !== undefined) {
            matchedPdfLines.add(String(hit.line_number));
          }
        });
        console.log(`🔗 PDF lines mapped to PO lines: [${[...matchedPdfLines].join(', ')}] `
          + `of ${(originalItems || []).length} PDF line(s) — unmapped ones become expenses`);

        console.log(`📄 Found ${originalItems.length} original PDF line items`);
        console.log(`📋 Found ${poItems.length} PO line items`);
        console.log('🔍 Starting expense detection from PDF vs PO comparison...');

        // Keywords that suggest high-confidence expenses
        const expenseKeywords = [
          'freight', 'shipping', 'handling', 'delivery', 'transport',
          'surcharge', 'fee', 'charge', 'service',
          'tax', 'duty', 'customs', 'tariff',
          'insurance', 'setup', 'installation', 'consulting',
          'maintenance', 'rental', 'lease', 'subscription',
          'miscellaneous', 'misc', 'other'
        ];

        originalItems.forEach(pdfItem => {
          // Skip items without amount
          if (!pdfItem.amount && !pdfItem.extended_price) {
            return;
          }

          const description = (pdfItem.description || pdfItem.item_name || '').toLowerCase();
          const amount = pdfItem.amount || pdfItem.extended_price || 0;

          // Mapped to a PO line? Then it is a bill line, not an expense.
          const isInPO = matchedPdfLines.has(String(pdfItem.line_number));

          // If this item is NOT in the PO, it could be an expense
          if (!isInPO) {
            // Determine confidence level
            const isExpenseKeyword = expenseKeywords.some(keyword => description.includes(keyword));
            const confidence = isExpenseKeyword ? 'high' : 'medium';

            expenseItems.push({
              id: expenseIdCounter++,
              account: pdfItem.expense_account || {},
              amount: amount,
              memo: pdfItem.description || pdfItem.item_name || '',
              location: getPoData()?.line_items?.[0]?.location || {},
              class: getPoData()?.line_items?.[0]?.class || {},
              name: pdfItem.expense_account_name || '',
              source: 'pdf_detected',
              confidence: confidence, // 'high' or 'medium'
              original_description: pdfItem.description || pdfItem.item_name
            });
            console.log(`🔍 Detected ${confidence}-confidence expense from PDF: ${pdfItem.description} - $${amount}`);
          }
        });

        console.log(`✅ Expense detection complete - found ${expenseItems.filter(e => e.source === 'pdf_detected').length} PDF-detected expenses`);
      }

        // 🎯 Apply AI defaults to expenses BEFORE setting state
        const aiDefaults = workflowContext?.bill_defaults_result?.bill_data;
        if (aiDefaults && expenseItems.length > 0) {
          const defaultLoc = aiDefaults.default_location;
          const defaultDept = aiDefaults.default_department;
          const defaultCls = aiDefaults.default_class;

          expenseItems.forEach(exp => {
            if (defaultLoc && !exp.location?.id) {
              exp.location = defaultLoc;
            }
            if (defaultDept && !exp.department?.id) {
              exp.department = defaultDept;
            }
            if (defaultCls && !exp.class?.id) {
              exp.class = defaultCls;
            }
          });
          console.log('💰 Applied AI defaults to expenses during initialization');
        }

        if (expenseItems.length > 0) {
          console.log(`🔄 useEffect: About to call setExpenses with ${expenseItems.length} items`);
          console.log(`🔄 useEffect: Expense sources:`, expenseItems.map(e => `${e.id}:${e.source}`).join(', '));
          setExpenses(expenseItems);
          setNextExpenseId(expenseIdCounter); // Set next ID to continue sequence
          console.log(`✅ useEffect: Initialized ${expenseItems.length} total expense items (${expenseItems.filter(e => e.source === 'existing').length} existing, ${expenseItems.filter(e => e.source === 'pdf_detected').length} PDF-detected, ${expenseItems.filter(e => e.source === 'ai_added').length} AI-added)`);
        }
        setExpensesInitialized(true); // Mark as initialized to prevent resets
        console.log('🔄 useEffect: Set expensesInitialized = true');
      }

      // 🔧 Fetch expense accounts (for BOTH PO and standalone bills)
      if (token) {
        console.log('💰 Fetching expense accounts from NetSuite...');
        console.log('🔍 Debug - isPOLinked:', isPOLinked);
        console.log('🔍 Debug - token exists:', !!token);

        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';

        // 🔧 FIX: Get subsidiary_id for filtering expense accounts
        const subsidiaryId = billData?.subsidiary_id
          || workflowContext?.subsidiary_identification_result?.subsidiary_data?.subsidiary_id
          || workflowContext?.subsidiary_id;

        const url = subsidiaryId
          ? `${API_BASE_URL}/api/bill-processing/expense-accounts?subsidiary_id=${subsidiaryId}`
          : `${API_BASE_URL}/api/bill-processing/expense-accounts`;

        console.log('💰 Fetching expense accounts with subsidiary_id:', subsidiaryId);

        console.log('🔍 Debug - Calling URL:', url);
        console.log('🔍 Debug - With Authorization header:', `Bearer ${token.substring(0, 20)}...`);

        fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
          .then(res => {
            console.log('🔍 Debug - Response status:', res.status);
            console.log('🔍 Debug - Response ok:', res.ok);
            return res.json();
          })
          .then(data => {
            console.log('🔍 Debug - Response data:', data);
            if (data.success && data.accounts) {
              console.log(`✅ Fetched ${data.accounts.length} expense accounts`);
              setAllExpenseAccounts(data.accounts);
            } else {
              console.error('❌ Failed to fetch expense accounts:', data.error || data);
              setAllExpenseAccounts([]);
            }
          })
          .catch(err => {
            console.error('❌ Error fetching expense accounts:', err);
            console.error('❌ Error details:', err.message);
            setAllExpenseAccounts([]);
          });
      } else {
        console.log('⚠️ Skipping expense accounts fetch - isPOLinked:', isPOLinked, 'token:', !!token);
      }

      // 🔧 Fetch tax codes from NetSuite for both expense items and line items
      if (token) {
        console.log('🏷️ Fetching tax codes from NetSuite...');

        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';

        // ✅ FIX: Get subsidiary_id - check selectedSubsidiary FIRST (user override)
        const currentData = getCurrentBillData();
        const subsidiaryId = selectedSubsidiary?.id
          || currentData?.subsidiary_id
          || currentData?.subsidiary?.id
          || workflowContext?.subsidiary_identification_result?.subsidiary_data?.subsidiary_id
          || workflowContext?.subsidiary_id;

        const taxCodesUrl = subsidiaryId
          ? `${API_BASE_URL}/api/bill-processing/tax-codes?subsidiary_id=${subsidiaryId}`
          : `${API_BASE_URL}/api/bill-processing/tax-codes`;

        console.log('🔍 Fetching tax codes from:', taxCodesUrl);

        fetch(taxCodesUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
          .then(res => res.json())
          .then(data => {
            if (data.success && data.tax_codes) {
              console.log(`✅ Fetched ${data.tax_codes.length} tax codes`);
              setAllTaxCodes(data.tax_codes);
            } else {
              console.error('❌ Failed to fetch tax codes:', data.error || data);
              setAllTaxCodes([]);
            }
          })
          .catch(err => {
            console.error('❌ Error fetching tax codes:', err);
            setAllTaxCodes([]);
          });
      } else {
        console.log('⚠️ Skipping tax codes fetch - token:', !!token);
      }

      // 🔧 NOTE: Locations, departments, classes, customers are fetched in the first useEffect
      // with subsidiary_id filtering. See useEffect at line ~77.

      // 🔧 NOTE: We no longer pre-load all_netsuite_items globally
      // Instead, users click "Load All" button per line item to fetch all items on-demand
      // This prevents massive payloads in initial workflow response
      console.log(`✅ Items will be loaded on-demand per line using "Load All" button`);
    }
  }, [billData, modifiedBillData, token, workflowContext, selectedSubsidiary]);

  // 🧹 CRITICAL FIX: Clean null characters from initial billData to prevent modal crash
  useEffect(() => {
    if (!billData) return;

    // Helper function to clean null characters from strings
    const cleanNullChars = (str) => {
      if (typeof str === 'string') {
        return str.replace(/\u0000/g, '').trim();
      }
      return str;
    };

    // Recursive function to clean all string fields in an object
    const deepCleanObject = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;

      if (Array.isArray(obj)) {
        return obj.map(item => deepCleanObject(item));
      }

      const cleaned = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          if (typeof value === 'string') {
            cleaned[key] = cleanNullChars(value);
          } else if (typeof value === 'object' && value !== null) {
            cleaned[key] = deepCleanObject(value);
          } else {
            cleaned[key] = value;
          }
        }
      }
      return cleaned;
    };

    // Only clean if we haven't already cleaned this billData instance
    // (Check if it's a new billData by comparing reference)
    if (billData !== lastBillDataRef) {
      console.log('🧹 Cleaning null characters from initial billData...');

      // Check if cleaning is actually needed
      const billDataString = JSON.stringify(billData);
      const hasNullChars = billDataString.includes('\\u0000');

      if (hasNullChars) {
        console.log('⚠️ Found null characters in billData - cleaning now...');
        const cleanedBillData = deepCleanObject(billData);
        setModifiedBillData(cleanedBillData);
        console.log('✅ Cleaned billData and set as modifiedBillData');
      } else {
        console.log('✅ No null characters found in billData - no cleaning needed');
      }

      setLastBillDataRef(billData);
    }
  }, [billData, lastBillDataRef]);

  // 💱 Currency matching - handles all 3 cases: matched vendor, user selection, AI modification
  useEffect(() => {
    const currentData = getCurrentBillData();
    const pdfCurrency = currentData?.currency;

    console.log('💱 Currency matching check:', {
      selectedVendor,
      pdfCurrency,
      hasAllVendors: allVendors.length > 0,
      hasAvailableVendors: currentData?.available_vendors?.length > 0
    });

    // Case 1, 2, 3: Vendor is selected (matched, user selected, or AI modified)
    if (selectedVendor) {
      // Find vendor from available sources
      const vendorsSource = allVendors.length > 0 ? allVendors : (currentData?.available_vendors || []);
      let vendor = vendorsSource.find(v => String(v?.id || v?.vendor_id) === String(selectedVendor));

      // 🔧 FIX: If not found in arrays, check currentData.vendor directly
      // This handles the case where AI matched a vendor but didn't include it in available_vendors
      if (!vendor && currentData?.vendor) {
        const vendorId = currentData.vendor.netsuite_vendor_id || currentData.vendor.id;
        if (String(vendorId) === String(selectedVendor)) {
          vendor = currentData.vendor;
          console.log('💱 Using vendor from currentData.vendor (not in available_vendors array)');
        }
      }

      if (vendor) {
        // 💱 Check for currency_validation first (from validate_vendor_currency tool)
        const currencyValidation = vendor.currency_validation || currentData?.vendor?.currency_validation;

        if (currencyValidation && currencyValidation.currency_valid) {
          // Use validated currency ID from backend
          console.log('💱 Using VALIDATED currency from backend:', {
            vendorId: selectedVendor,
            vendorName: vendor.companyname || vendor.companyName || vendor.netsuite_vendor_name,
            currencyId: currencyValidation.currency_id,
            currencySymbol: currencyValidation.currency_symbol
          });

          setMatchedCurrency({
            code: currencyValidation.currency_symbol || currencyValidation.currency_name || pdfCurrency,
            symbol: currencyValidation.currency_symbol,
            id: currencyValidation.currency_id
          });

          console.log(`✅ Currency VALIDATED: ${currencyValidation.currency_symbol} (ID: ${currencyValidation.currency_id})`);
        }
        // Fallback: Use matched_currency from backend if available
        else if (vendor.matched_currency) {
          console.log('💱 Using MATCHED currency from backend:', {
            vendorId: selectedVendor,
            vendorName: vendor.companyname || vendor.companyName,
            matchedCurrency: vendor.matched_currency
          });

          setMatchedCurrency({
            code: vendor.matched_currency.name || vendor.matched_currency.symbol || pdfCurrency,
            symbol: vendor.matched_currency.symbol,
            id: vendor.matched_currency.id
          });

          console.log(`✅ Currency MATCHED by backend: ${vendor.matched_currency.symbol} (ID: ${vendor.matched_currency.id})`);
        }
        // Fallback: Use vendor default currency
        else {
          const vendorCurrencyId = vendor.currencyid || vendor.currencyId || vendor.currency || vendor.defaultCurrencyId || vendor.netsuite_currency_id;
          const vendorCurrencySymbol = vendor.currencysymbol || vendor.currencySymbol || vendor.defaultCurrencySymbol || '';

          console.log('💱 Using vendor DEFAULT currency (fallback):', {
            vendorId: selectedVendor,
            vendorName: vendor.companyname || vendor.companyName,
            currencyId: vendorCurrencyId,
            currencySymbol: vendorCurrencySymbol
          });

          const currencyMap = {
            'USD': '$', 'SGD': 'S$', 'EUR': '€', 'GBP': '£',
            'JPY': '¥', 'CNY': '¥', 'INR': '₹', 'AUD': 'A$', 'CAD': 'C$'
          };

          setMatchedCurrency({
            code: pdfCurrency || vendorCurrencySymbol || 'USD',
            symbol: vendorCurrencySymbol || currencyMap[pdfCurrency] || '$',
            id: vendorCurrencyId
          });

          if (pdfCurrency && vendorCurrencySymbol && pdfCurrency !== vendorCurrencySymbol) {
            console.warn(`⚠️ Currency mismatch: PDF=${pdfCurrency}, Vendor=${vendorCurrencySymbol}`);
          }
        }
      } else {
        console.warn(`⚠️ Currency matching: Vendor ${selectedVendor} not found in vendorsSource or currentData.vendor`);
      }
    }
    // Fallback: No vendor selected but PDF has currency
    else if (pdfCurrency) {
      // Check if currency_validation exists at currentData level
      const currencyValidation = currentData?.vendor?.currency_validation;

      const currencyMap = {
        'USD': '$', 'SGD': 'S$', 'EUR': '€', 'GBP': '£',
        'JPY': '¥', 'CNY': '¥', 'INR': '₹', 'AUD': 'A$', 'CAD': 'C$'
      };

      setMatchedCurrency({
        code: pdfCurrency,
        symbol: currencyValidation?.currency_symbol || currentData?.currency_symbol || currencyMap[pdfCurrency] || '$',
        id: currencyValidation?.currency_id || currentData?.currency_id || null
      });

      console.log(`✅ Currency set from PDF: ${pdfCurrency}`, {
        currencyId: currencyValidation?.currency_id || currentData?.currency_id
      });
    }
  }, [selectedVendor, billData, modifiedBillData, allVendors, currentBillData?.available_vendors]);

  // ✅ FIX: Auto-apply tax codes to expenses when tax codes finish loading
  useEffect(() => {
    if (allTaxCodes && allTaxCodes.length > 0 && expenses.length > 0 && currentBillData.tax_rate) {
      const pdfTaxRate = parseFloat(currentBillData.tax_rate);
      let hasUpdates = false;

      const updatedExpenses = expenses.map(exp => {
        // Only apply if expense doesn't have tax code yet
        if (!exp.tax_code_id && pdfTaxRate) {
          const matchingTaxCode = allTaxCodes.find(tc => {
            if (tc.rate) {
              const tcRate = parseFloat(tc.rate) * 100;
              return Math.abs(tcRate - pdfTaxRate) < 0.1;
            }
            return false;
          });

          if (matchingTaxCode) {
            hasUpdates = true;
            console.log(`✅ Auto-applied tax to expense "${exp.name}": ${pdfTaxRate}% (tax_code_id=${matchingTaxCode.id})`);
            return {
              ...exp,
              tax_code_id: matchingTaxCode.id,
              tax_rate: pdfTaxRate
            };
          }
        }
        return exp;
      });

      if (hasUpdates) {
        console.log('🔄 Auto-applying tax codes to expenses after tax codes loaded');
        setExpenses(updatedExpenses);
      }
    }
  }, [allTaxCodes, currentBillData.tax_rate]); // Run when tax codes load or tax rate changes

  // ✅ FIX: Auto-apply tax codes to line items (SAME LOGIC AS EXPENSES)
  useEffect(() => {
    if (allTaxCodes && allTaxCodes.length > 0 && currentBillData.line_items && currentBillData.line_items.length > 0) {
      console.log('🔍 Line Items Tax Auto-Apply:', {
        allTaxCodesCount: allTaxCodes.length,
        lineItemsCount: currentBillData.line_items.length,
        firstLineItem: currentBillData.line_items[0]
      });

      const newTaxCodes = {};

      currentBillData.line_items.forEach((item, idx) => {
        // Only apply if line item has tax_rate AND doesn't have valid tax_code_id in allTaxCodes
        if (item && item.tax_rate && item.line_number) {
          const itemTaxRate = parseFloat(item.tax_rate);

          // Check if current tax_code_id exists in allTaxCodes
          const currentTaxExists = item.tax_code_id &&
            allTaxCodes.some(tc => String(tc.id) === String(item.tax_code_id));

          console.log(`🔍 Line Item ${idx + 1}:`, {
            line_number: item.line_number,
            tax_rate: itemTaxRate,
            current_tax_code_id: item.tax_code_id,
            currentTaxExists,
            needsUpdate: !currentTaxExists
          });

          // If tax code doesn't exist in allTaxCodes, find matching one by rate (same as expenses)
          if (!currentTaxExists) {
            const matchingTaxCode = allTaxCodes.find(tc => {
              if (tc.rate) {
                const tcRate = parseFloat(tc.rate) * 100;
                return Math.abs(tcRate - itemTaxRate) < 0.1; // Match within 0.1%
              }
              return false;
            });

            console.log(`🔍 Matching tax code for ${itemTaxRate}%:`, matchingTaxCode);

            if (matchingTaxCode) {
              newTaxCodes[item.line_number] = matchingTaxCode.id;
              console.log(`✅ Auto-applied tax to line item ${idx + 1} (line ${item.line_number}): ${itemTaxRate}% (tax_code_id=${matchingTaxCode.id}, itemid=${matchingTaxCode.itemid})`);
            } else {
              console.warn(`⚠️ No matching tax code found for ${itemTaxRate}%`);
            }
          }
        }
      });

      if (Object.keys(newTaxCodes).length > 0) {
        console.log('🔄 Setting line item tax codes (LOCAL STATE like expenses):', newTaxCodes);
        setLineItemTaxCodes(prev => ({ ...prev, ...newTaxCodes }));
      }
    }
  }, [allTaxCodes, currentBillData.line_items?.length]); // Run when tax codes load OR line items first appear

  // ✅ FIX: Sync lineItemTaxCodes local state when AI modifies bill data
  useEffect(() => {
    if (currentBillData.line_items && currentBillData.line_items.length > 0 && allTaxCodes && allTaxCodes.length > 0) {
      const updatedTaxCodes = {};
      let hasChanges = false;

      currentBillData.line_items.forEach(item => {
        if (item?.line_number && item?.tax_code_id) {
          // Only sync if the tax code in billData is VALID (exists in allTaxCodes)
          // This prevents overriding auto-applied tax codes with invalid cross-subsidiary codes
          const taxCodeExistsInSubsidiary = allTaxCodes.some(tc => String(tc.id) === String(item.tax_code_id));

          if (taxCodeExistsInSubsidiary && lineItemTaxCodes[item.line_number] !== item.tax_code_id) {
            updatedTaxCodes[item.line_number] = item.tax_code_id;
            hasChanges = true;
            console.log(`🔄 Syncing VALID tax code for line ${item.line_number}: ${item.tax_code_id} (was: ${lineItemTaxCodes[item.line_number]})`);
          } else if (!taxCodeExistsInSubsidiary) {
            console.log(`⚠️ Skipping sync for line ${item.line_number}: tax code ${item.tax_code_id} not valid for this subsidiary (keeping auto-applied: ${lineItemTaxCodes[item.line_number]})`);
          }
        }
      });

      if (hasChanges) {
        console.log('✅ Syncing line item tax codes from AI modifications:', updatedTaxCodes);
        setLineItemTaxCodes(prev => ({ ...prev, ...updatedTaxCodes }));
      }
    }
  }, [currentBillData, lineItemTaxCodes, allTaxCodes]); // Run when currentBillData changes (e.g., AI modifications)

  // Function to load all NetSuite items for a specific line
  const handleLoadAllItemsForLine = async (lineNumber) => {
    // Get runId from multiple possible sources (in priority order)
    const runIdFromProps = propRunId; // Direct prop from parent
    const runIdFromContext = workflowContext?.runId; // From workflow context state
    const runIdFromBillData = billData?.run_id; // From bill data (FLAT structure)
    const effectiveRunId = runIdFromProps || runIdFromContext || runIdFromBillData;

    console.log('🔍 Load All Items Debug:', {
      token: token ? 'present' : 'missing',
      propRunId,
      propWorkflowId,
      workflowContext,
      runIdFromProps,
      runIdFromContext,
      runIdFromBillData,
      effectiveRunId,
      billDataKeys: Object.keys(billData || {})
    });

    if (!token || !effectiveRunId) {
      console.error('❌ Cannot load items: missing token or runId', {
        hasToken: !!token,
        hasRunId: !!effectiveRunId,
        checkedSources: {
          propRunId,
          runIdFromContext,
          runIdFromBillData
        }
      });
      alert('Cannot load items: Missing authentication or workflow ID. Please try reopening the workflow.');
      return;
    }

    setLoadingLineItems(prev => ({ ...prev, [lineNumber]: true }));

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';
      console.log(`📦 Fetching all items for line ${lineNumber} with run_id=${effectiveRunId}`);
      const response = await fetch(
        `${API_BASE_URL}/api/bill-processing/netsuite-items-all?run_id=${effectiveRunId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();

      if (data.success && data.items) {
        console.log(`✅ Loaded ${data.items.length} items for line ${lineNumber}`);
        setLineItemsAllItems(prev => ({
          ...prev,
          [lineNumber]: data.items
        }));
      } else {
        console.error(`❌ Failed to load items for line ${lineNumber}:`, data.error);
        alert('Failed to load all NetSuite items. Please try again.');
      }
    } catch (error) {
      console.error(`❌ Error loading items for line ${lineNumber}:`, error);
      alert('Error loading items. Please try again.');
    } finally {
      setLoadingLineItems(prev => ({ ...prev, [lineNumber]: false }));
    }
  };

  // Function to load all NetSuite vendors
  const handleLoadAllVendors = async () => {
    // Get runId from multiple possible sources
    const runIdFromProps = propRunId;
    const runIdFromContext = workflowContext?.runId;
    const currentData = getCurrentBillData();
    const runIdFromBillData = currentData?.run_id;
    const effectiveRunId = runIdFromProps || runIdFromContext || runIdFromBillData;

    // ✅ FIX: Get subsidiary ID - check selectedSubsidiary FIRST (user override)
    const subsidiaryId = selectedSubsidiary?.id
      || currentData?.subsidiary_id
      || currentData?.subsidiary?.id
      || workflowContext?.subsidiary_identification_result?.subsidiary_data?.subsidiary_id
      || workflowContext?.subsidiary_id;

    console.log('🔍 Load All Vendors Debug:', {
      token: token ? 'present' : 'missing',
      effectiveRunId,
      subsidiaryId
    });

    if (!token || !effectiveRunId) {
      console.error('❌ Cannot load vendors: missing token or runId');
      alert('Cannot load vendors: Missing authentication or workflow ID. Please try reopening the workflow.');
      return;
    }

    setLoadingAllVendors(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';
      const url = subsidiaryId
        ? `${API_BASE_URL}/api/bill-processing/netsuite-vendors-all?run_id=${effectiveRunId}&subsidiary_id=${subsidiaryId}`
        : `${API_BASE_URL}/api/bill-processing/netsuite-vendors-all?run_id=${effectiveRunId}`;

      console.log(`👥 Fetching all vendors from: ${url}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success && data.vendors) {
        console.log(`✅ Loaded ${data.vendors.length} vendors`);
        console.log('📦 First vendor sample:', data.vendors[0]);
        console.log('📦 Vendor fields:', data.vendors[0] ? Object.keys(data.vendors[0]) : 'No vendors');
        setAllVendors(data.vendors);
      } else {
        console.error(`❌ Failed to load vendors:`, data.error);
        alert('Failed to load all NetSuite vendors. Please try again.');
      }
    } catch (error) {
      console.error(`❌ Error loading vendors:`, error);
      alert('Error loading vendors. Please try again.');
    } finally {
      setLoadingAllVendors(false);
    }
  };

  // 🏢 Function to load all NetSuite subsidiaries
  const handleLoadAllSubsidiaries = async () => {
    // Get runId from multiple possible sources
    const runIdFromProps = propRunId;
    const runIdFromContext = workflowContext?.runId;
    const currentData = getCurrentBillData();
    const runIdFromBillData = currentData?.run_id;
    const effectiveRunId = runIdFromProps || runIdFromContext || runIdFromBillData;

    console.log('🏢 Load All Subsidiaries Debug:', {
      token: token ? 'present' : 'missing',
      effectiveRunId
    });

    if (!token || !effectiveRunId) {
      console.error('❌ Cannot load subsidiaries: missing token or runId');
      alert('Cannot load subsidiaries: Missing authentication or workflow ID. Please try reopening the workflow.');
      return;
    }

    setLoadingSubsidiaries(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';
      const url = `${API_BASE_URL}/api/bill-processing/netsuite-subsidiaries-all?run_id=${effectiveRunId}`;

      console.log(`🏢 Fetching all subsidiaries from: ${url}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success && data.subsidiaries) {
        console.log(`✅ Loaded ${data.subsidiaries.length} subsidiaries`);
        setAllSubsidiaries(data.subsidiaries);
      } else {
        console.error(`❌ Failed to load subsidiaries:`, data.error);
        alert('Failed to load all NetSuite subsidiaries. Please try again.');
      }
    } catch (error) {
      console.error(`❌ Error loading subsidiaries:`, error);
      alert('Error loading subsidiaries. Please try again.');
    } finally {
      setLoadingSubsidiaries(false);
    }
  };

  // 🏢 Function to handle subsidiary change
  const handleSubsidiaryChange = (e) => {
    const newSubsidiaryId = e.target.value;
    const selectedSub = allSubsidiaries.find(s => String(s.id) === String(newSubsidiaryId));

    if (selectedSub) {
      console.log('🏢 Subsidiary changed to:', selectedSub);
      setSelectedSubsidiary(selectedSub);

      // Clear cached data that depends on subsidiary
      setAllLocations([]);
      setAllDepartments([]);
      setAllClasses([]);
      setAllTaxCodes([]);
      setAllVendors([]);
      setAllCustomers([]); // Customers are also subsidiary-specific
      setLocationsLoaded(false);
      setDepartmentsLoaded(false);
      setClassesLoaded(false);

      // 🔧 FIX: Clear selected location/department/class values (they're invalid for new subsidiary)
      setLineItemLocations({});
      setLineItemDepartments({});
      setLineItemClasses({});
      setLineItemTaxCodes({}); // Tax codes are also subsidiary-specific
      console.log('🧹 Cleared location/department/class/taxcode selections (invalid for new subsidiary)');

      // 🔧 FIX: Also update the expenses state (separate from modifiedBillData)
      // Clear BOTH object format (location) AND ID format (location_id)
      setExpenses(prevExpenses => prevExpenses.map(exp => ({
        ...exp,
        location: null,
        location_id: null,
        department: null,
        department_id: null,
        class: null,
        class_id: null,
        customer: null,
        customer_id: null,
        tax_code_id: null // Tax codes are subsidiary-specific
      })));
      console.log('🧹 Cleared location/department/class/customer from expenses state (both object and ID formats)');

      // Update bill data with new subsidiary and clear old location/dept/class defaults
      // Create modifiedBillData from billData if it doesn't exist
      const baseData = modifiedBillData || billData || {};

      // Clear location/dept/class from line items and expenses
      // 🔧 FIX: Clear BOTH object format (location) AND ID format (location_id)
      const cleanedLineItems = (baseData.line_items || []).map(item => ({
        ...item,
        location: null,
        location_id: null,
        department: null,
        department_id: null,
        class: null,
        class_id: null,
        tax_code_id: null  // Tax codes are also subsidiary-specific
      }));

      const cleanedExpenseItems = (baseData.expense?.items || []).map(exp => ({
        ...exp,
        location: null,
        location_id: null,
        department: null,
        department_id: null,
        class: null,
        class_id: null,
        tax_code_id: null  // Tax codes are also subsidiary-specific
      }));

      setModifiedBillData({
        ...baseData,
        subsidiary_id: selectedSub.id,
        subsidiary: {
          id: selectedSub.id,
          name: selectedSub.name,
          legalname: selectedSub.legalname,
          country: selectedSub.country
        },
        // Clear defaults that are invalid for new subsidiary
        default_location: null,
        default_department: null,
        default_class: null,
        // 🔧 FIX: Clear cached dropdown data from bill data (dropdown reads from these!)
        all_locations: [],
        all_departments: [],
        all_classes: [],
        all_customers: [],
        all_netsuite_locations: [],
        all_netsuite_departments: [],
        all_netsuite_classes: [],
        all_netsuite_customers: [],
        all_netsuite_tax_codes: [],
        line_items: cleanedLineItems,
        expense: {
          ...baseData.expense,
          items: cleanedExpenseItems
        }
      });

      console.log('🏢 Bill data updated with new subsidiary, old location/dept/class cleared');
    }
  };

  // 🏢 Get current subsidiary (selected override, billData, or workflow context)
  const getCurrentSubsidiary = () => {
    if (selectedSubsidiary) {
      return selectedSubsidiary;
    }

    // 🔧 FIX: Get workflow context subsidiary as source of truth for names
    const workflowSubsidiary = workflowContext?.subsidiary_identification_result?.subsidiary_data;

    // Helper to find subsidiary name from allSubsidiaries (if loaded)
    const findSubsidiaryName = (subId) => {
      if (!subId || !allSubsidiaries?.length) return null;
      const found = allSubsidiaries.find(s => String(s.id) === String(subId));
      return found ? { name: found.name, country: found.country } : null;
    };

    // Check billData for subsidiary (for drafts that have subsidiary saved)
    const currentData = getCurrentBillData();
    if (currentData?.subsidiary?.id) {
      const fallback = findSubsidiaryName(currentData.subsidiary.id);
      return {
        id: currentData.subsidiary.id,
        subsidiary_id: currentData.subsidiary.id,
        name: currentData.subsidiary.name || workflowSubsidiary?.subsidiary_name || fallback?.name,
        subsidiary_name: currentData.subsidiary.name || workflowSubsidiary?.subsidiary_name || fallback?.name,
        legalname: currentData.subsidiary.legalname || workflowSubsidiary?.subsidiary_legal_name,
        subsidiary_legal_name: currentData.subsidiary.legalname || workflowSubsidiary?.subsidiary_legal_name,
        country: currentData.subsidiary.country || workflowSubsidiary?.country || fallback?.country
      };
    }

    // If only subsidiary_id exists, try to get name from multiple sources
    if (currentData?.subsidiary_id) {
      const subId = currentData.subsidiary_id;
      const fallback = findSubsidiaryName(subId);

      // Source 1: workflow context (if IDs match)
      if (workflowSubsidiary?.subsidiary_name &&
          String(workflowSubsidiary?.subsidiary_id) === String(subId)) {
        return {
          id: subId,
          subsidiary_id: subId,
          name: workflowSubsidiary.subsidiary_name,
          subsidiary_name: workflowSubsidiary.subsidiary_name,
          legalname: workflowSubsidiary.subsidiary_legal_name,
          subsidiary_legal_name: workflowSubsidiary.subsidiary_legal_name,
          country: workflowSubsidiary.country
        };
      }

      // Source 2: workflow context (if it has a name, even if IDs differ)
      if (workflowSubsidiary?.subsidiary_name) {
        return workflowSubsidiary;
      }

      // Source 3: allSubsidiaries lookup
      if (fallback?.name) {
        return {
          id: subId,
          subsidiary_id: subId,
          name: fallback.name,
          subsidiary_name: fallback.name,
          country: fallback.country
        };
      }

      // Last resort - return ID only
      return {
        id: subId,
        subsidiary_id: subId,
        name: null,
        subsidiary_name: null
      };
    }

    // No subsidiary in billData, use workflow context directly
    return workflowSubsidiary || null;
  };

  // Function to load all NetSuite tax codes
  const handleLoadAllTaxCodes = async () => {
    // Get subsidiary ID - check selectedSubsidiary FIRST (user override)
    const currentData = getCurrentBillData();
    const subsidiaryId = selectedSubsidiary?.id
      || currentData?.subsidiary_id
      || currentData?.subsidiary?.id
      || workflowContext?.subsidiary_identification_result?.subsidiary_data?.subsidiary_id
      || workflowContext?.subsidiary_id;

    console.log('🔍 Load All Tax Codes Debug:', {
      token: token ? 'present' : 'missing',
      subsidiaryId,
      currentTaxCodesCount: allTaxCodes.length
    });

    if (!token) {
      console.error('❌ Cannot load tax codes: missing token');
      alert('Cannot load tax codes: Missing authentication. Please try reopening the workflow.');
      return;
    }

    if (!subsidiaryId) {
      console.warn('⚠️ Loading tax codes without subsidiary filter');
    }

    setLoading(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';
      const url = subsidiaryId
        ? `${API_BASE_URL}/api/bill-processing/tax-codes?subsidiary_id=${subsidiaryId}`
        : `${API_BASE_URL}/api/bill-processing/tax-codes`;

      console.log(`🏷️ Fetching all tax codes from: ${url}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success && data.tax_codes) {
        console.log(`✅ Loaded ${data.tax_codes.length} tax codes`);
        setAllTaxCodes(data.tax_codes);
      } else {
        console.error('❌ Failed to load tax codes:', data.error);
      }
    } catch (error) {
      console.error('❌ Error loading tax codes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to load all locations
  const handleLoadAllLocations = async () => {
    if (locationsLoaded && allLocations.length > 0) {
      console.log('📍 Locations already loaded');
      return;
    }

    const currentData = getCurrentBillData();
    const subsidiaryId = selectedSubsidiary?.id
      || currentData?.subsidiary_id
      || currentData?.subsidiary?.id
      || workflowContext?.subsidiary_identification_result?.subsidiary_data?.subsidiary_id;

    setLoadingLocations(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';
      const url = subsidiaryId
        ? `${API_BASE_URL}/api/bill-processing/locations?subsidiary_id=${subsidiaryId}`
        : `${API_BASE_URL}/api/bill-processing/locations`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success && data.locations) {
        console.log(`✅ Loaded ${data.locations.length} locations`);
        setAllLocations(data.locations);
        setLocationsLoaded(true);
      }
    } catch (error) {
      console.error('❌ Error loading locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Function to load all departments
  const handleLoadAllDepartments = async () => {
    if (departmentsLoaded && allDepartments.length > 0) {
      console.log('🏛️ Departments already loaded');
      return;
    }

    const currentData = getCurrentBillData();
    const subsidiaryId = selectedSubsidiary?.id
      || currentData?.subsidiary_id
      || currentData?.subsidiary?.id
      || workflowContext?.subsidiary_identification_result?.subsidiary_data?.subsidiary_id;

    setLoadingDepartments(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';
      const url = subsidiaryId
        ? `${API_BASE_URL}/api/bill-processing/departments?subsidiary_id=${subsidiaryId}`
        : `${API_BASE_URL}/api/bill-processing/departments`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success && data.departments) {
        console.log(`✅ Loaded ${data.departments.length} departments`);
        setAllDepartments(data.departments);
        setDepartmentsLoaded(true);
      }
    } catch (error) {
      console.error('❌ Error loading departments:', error);
    } finally {
      setLoadingDepartments(false);
    }
  };

  // Function to load all classes
  const handleLoadAllClasses = async () => {
    if (classesLoaded && allClasses.length > 0) {
      console.log('🏷️ Classes already loaded');
      return;
    }

    const currentData = getCurrentBillData();
    const subsidiaryId = selectedSubsidiary?.id
      || currentData?.subsidiary_id
      || currentData?.subsidiary?.id
      || workflowContext?.subsidiary_identification_result?.subsidiary_data?.subsidiary_id;

    setLoadingClasses(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bytebtsandbox.sankalvax.ai';
      const url = subsidiaryId
        ? `${API_BASE_URL}/api/bill-processing/classes?subsidiary_id=${subsidiaryId}`
        : `${API_BASE_URL}/api/bill-processing/classes`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success && data.classes) {
        console.log(`✅ Loaded ${data.classes.length} classes`);
        setAllClasses(data.classes);
        setClassesLoaded(true);
      }
    } catch (error) {
      console.error('❌ Error loading classes:', error);
    } finally {
      setLoadingClasses(false);
    }
  };

  // Function to handle tax code change for a line item
  const handleLineItemTaxCodeChange = (lineNumber, taxCodeId) => {
    const currentData = getCurrentBillData();

    // ✅ Update local state (like expenses do)
    setLineItemTaxCodes(prev => ({
      ...prev,
      [lineNumber]: taxCodeId
    }));

    // Find selected tax code to get the rate (like expenses do)
    const selectedTaxCode = allTaxCodes.find(tc => tc.id === taxCodeId);

    // Update the line item with the new tax code (DON'T modify tax_rate - PDF tax stays constant)
    const updatedLineItems = currentData.line_items.map(item => {
      if (item?.line_number === lineNumber) {
        return {
          ...item,
          tax_code_id: taxCodeId
          // ✅ DO NOT modify tax_rate - it should stay as the original PDF value
        };
      }
      return item;
    });

    // Update the bill data
    const updatedBillData = {
      ...currentData,
      line_items: updatedLineItems
    };

    setModifiedBillData(updatedBillData);
    console.log(`✅ Updated tax code for line ${lineNumber} to ${taxCodeId}${selectedTaxCode ? ` (rate: ${selectedTaxCode.rate * 100}%)` : ''}`);
  };

  // Function to handle location change for a line item
  const handleLineItemLocationChange = (lineNumber, locationId) => {
    const currentData = getCurrentBillData();
    setLineItemLocations(prev => ({ ...prev, [lineNumber]: locationId }));

    const selectedLocation = allLocations.find(loc => String(loc.id) === String(locationId));
    const updatedLineItems = currentData.line_items.map(item => {
      if (item?.line_number === lineNumber) {
        return { ...item, location: locationId ? { id: locationId, name: selectedLocation?.name } : null };
      }
      return item;
    });

    setModifiedBillData({ ...currentData, line_items: updatedLineItems });
    console.log(`✅ Updated location for line ${lineNumber} to ${locationId}`);
  };

  // Function to handle department change for a line item
  const handleLineItemDepartmentChange = (lineNumber, departmentId) => {
    const currentData = getCurrentBillData();
    setLineItemDepartments(prev => ({ ...prev, [lineNumber]: departmentId }));

    const selectedDept = allDepartments.find(dept => String(dept.id) === String(departmentId));
    const updatedLineItems = currentData.line_items.map(item => {
      if (item?.line_number === lineNumber) {
        return { ...item, department: departmentId ? { id: departmentId, name: selectedDept?.name } : null };
      }
      return item;
    });

    setModifiedBillData({ ...currentData, line_items: updatedLineItems });
    console.log(`✅ Updated department for line ${lineNumber} to ${departmentId}`);
  };

  // Function to handle class change for a line item
  const handleLineItemClassChange = (lineNumber, classId) => {
    const currentData = getCurrentBillData();
    setLineItemClasses(prev => ({ ...prev, [lineNumber]: classId }));

    const selectedClass = allClasses.find(cls => String(cls.id) === String(classId));
    const updatedLineItems = currentData.line_items.map(item => {
      if (item?.line_number === lineNumber) {
        return { ...item, class: classId ? { id: classId, name: selectedClass?.name } : null };
      }
      return item;
    });

    setModifiedBillData({ ...currentData, line_items: updatedLineItems });
    console.log(`✅ Updated class for line ${lineNumber} to ${classId}`);
  };

  const handleConfirmBill = async () => {
    // Prevent duplicate calls while processing
    if (loading) {
      console.log('🚫 handleConfirmBill already in progress, ignoring duplicate call');
      return;
    }

    // ✅ Get fresh auth token in case modal was open for a while and token expired
    const freshToken = getAuthToken();
    if (!freshToken) {
      alert('Your session has expired. Please refresh the page and log in again.');
      return;
    }
    console.log('🔑 Using fresh authentication token for bill confirmation');

    // 🔄 CRITICAL FIX: Use getCurrentBillData() to get AI-modified data
    const currentData = getCurrentBillData();

    console.log('✅ PAYLOAD SOURCE:', {
      using: modifiedBillData ? 'MODIFIED (bill-creation-ready or AI-modified)' : 'ORIGINAL (fallback)',
      payload_status: currentData._payload_status || 'unknown',
      initialized_at: currentData._initialized_at || 'not tracked',
      invoice_number: currentData.invoice_number,
      total_amount: currentData.total_amount
    });

    if (!isPOLinked && currentData.line_items) {
      // Check for unmatched items (excluding removed items)
      const unmatched = currentData.line_items.filter(item =>
        item !== null && item !== undefined &&
        !removedLineItems.has(item?.line_number) && // Exclude removed items
        !item?.matched_netsuite_item &&
        (!selectedItems[item?.line_number] || selectedItems[item?.line_number] === '')
      );

      if (unmatched.length > 0) {
        alert(`Please select NetSuite items for ${unmatched.length} unmatched line items before proceeding.`);
        return;
      }
    }

    // 🔒 FREEZE display data to prevent changes during bill creation
    const dataToFreeze = getCurrentBillData();
    setFrozenDisplayData(dataToFreeze);
    console.log('🔒 Display data frozen - UI will not change during bill creation');

    setLoading(true);
    try {
      let finalBillData;
      let nextAction;

      if (isPOLinked) {
        // Include edited expenses in the final data
        const expenseData = expenses.length > 0 ? {
          items: expenses.map(exp => {
            // ✅ Extract account ID properly - check account object, then account_id field
            let accountId = (typeof exp.account === 'object' && exp.account !== null)
              ? exp.account.id
              : (exp.account_id || exp.account || '');

            // 🔧 CRITICAL VALIDATION: Ensure we have numeric internal ID, not account name
            // If accountId is not a number or looks like a name, try to find the internal ID
            if (accountId && isNaN(accountId) && allExpenseAccounts && allExpenseAccounts.length > 0) {
              // Try to find by account name
              const matchedAccount = allExpenseAccounts.find(acc =>
                acc.accountsearchdisplaynamecopy === accountId ||
                acc.name === accountId ||
                acc.description === accountId
              );

              if (matchedAccount) {
                console.warn(`⚠️ Expense "${exp.name}" had account name "${accountId}" instead of ID. Using internal ID: ${matchedAccount.id}`);
                accountId = matchedAccount.id;
              } else {
                console.error(`❌ Could not find internal ID for account "${accountId}" - bill creation may fail!`);
              }
            }

            const expenseItem = {
              id: exp.id,  // ✅ CRITICAL: Preserve expense ID for AI updates
              account: { id: accountId },
              amount: parseFloat(exp.amount) || 0,
              memo: exp.memo || '',
              name: exp.name || ''
            };

            // 🔧 CRITICAL FIX: Preserve item.id if expense was originally item-based
            // NetSuite PO transformations require item field for item-based expenses
            if (exp.item_id || (exp.item && exp.item.id)) {
              const itemId = exp.item_id || exp.item.id;
              expenseItem.item = { id: itemId };
              console.log(`✅ Preserved item.id for PO expense "${exp.name}": item=${itemId}`);
            }

            // 🔧 Add tax code to expense item (NetSuite supports taxcode on expense lines)
            if (exp.tax_code_id) {
              expenseItem.taxcode = { id: exp.tax_code_id };  // NetSuite expects: { id: "123" }
              console.log(`✅ Added tax to expense "${exp.name}": taxcode=${exp.tax_code_id}`);
            }

            // 🔧 Add location, department, class, customer to expense item
            if (exp.location?.id) {
              expenseItem.location = { id: exp.location.id };
            }
            if (exp.department?.id) {
              expenseItem.department = { id: exp.department.id };
            }
            if (exp.class?.id) {
              expenseItem.class = { id: exp.class.id };
            }
            if (exp.customer?.id) {
              expenseItem.customer = { id: exp.customer.id };
            }

            return expenseItem;
          }).filter(exp => exp.amount !== 0) // Remove zero-amount expenses (keep negative for credits/refunds)
        } : { items: [] };

        // Filter out removed line items from po_data and add tax codes - use ref to ensure persistence
        const removedSet = removedLineItemsRef.current.size > 0 ? removedLineItemsRef.current : removedLineItems;
        console.log(`📊 PO Line items: removed=${removedSet.size} (${Array.from(removedSet).join(', ')})`);

        const filteredPoData = currentData.po_data ? {
          ...currentData.po_data,
          line_items: (currentData.po_data.line_items || []).filter(item =>
            item !== null && item !== undefined && !removedSet.has(item?.line_number)
          ).map(item => {
            // 🔧 Match tax code for this PO line item
            let taxCodeId = null;
            const itemTaxRate = item?.tax_rate;

            if (itemTaxRate !== null && itemTaxRate !== undefined && currentData.all_netsuite_tax_codes) {
              // Find matching tax code by rate
              const matchingTaxCode = currentData.all_netsuite_tax_codes.find(tc => {
                const tcRate = tc.line ? parseFloat(tc.line.tax_rate) * 100 : null;
                return tcRate !== null && Math.abs(tcRate - parseFloat(itemTaxRate)) < 0.01;
              });

              if (matchingTaxCode && matchingTaxCode.line) {
                taxCodeId = matchingTaxCode.line.netsuite_tax_code_id;
                console.log(`✅ Matched tax code for PO line ${item?.line_number}: rate=${itemTaxRate}% -> tax_code_id=${taxCodeId}`);
              } else {
                console.warn(`⚠️ No matching tax code found for PO line ${item?.line_number} with rate ${itemTaxRate}%`);
              }
            }

            // ✏️ QTY: the quantity to bill comes from the review grid — the reviewer's
            // edit if they changed it, otherwise the PDF/invoiced qty, then the previous
            // fallback chain (open qty → ordered qty). Written into
            // open_for_billing_quantity because that is the field the create-bill prompt
            // reads, so no prompt/backend/RESTlet change is needed.
            // ✏️ RATE: the unit price to bill — the reviewer's edit if they changed it,
            // otherwise the PDF/invoiced unit price, then the PO rate. total_amount is
            // kept consistent (qty × rate) so downstream totals agree with the grid.
            // NOTE: the PO-linked bill schema tells the AI that rate is inherited from
            // the PO and must not be sent, so today this value is carried but NOT posted
            // to NetSuite — see Docs/backups/2026-08-04/REVERT.md.
            const _effRate = effectiveRateFor(item);
            const _effQty = effectiveQtyFor(item);
            return {
              ...item,
              tax_code_id: taxCodeId,
              open_for_billing_quantity: _effQty,
              rate: _effRate,
              total_amount: Number.isFinite(_effQty) && Number.isFinite(_effRate)
                ? Number((_effQty * _effRate).toFixed(2))
                : item?.total_amount
            };
          })
        } : currentData.po_data;

        finalBillData = {
          ...currentData,
          ...(billTranDateTouched && billTranDate ? { invoice_date: billTranDate, bill_date: billTranDate, tranDate: billTranDate } : {}),
          ...(billDueDateTouched && billDueDate ? { due_date: billDueDate, dueDate: billDueDate } : {}),
          memo: billMemo,  // 📝 Header-level Memo (PO number by default when PO-connected; editable; optional)
          po_data: filteredPoData,
          confirmed_vendor_id: selectedVendor,  // ✅ Internal numeric ID for bill creation
          netsuite_vendor_id: selectedVendor,   // ✅ Also store with explicit name for clarity
          expense: expenseData,
          // 💱 Include matched currency
          currency: matchedCurrency?.code || currentData?.currency,
          currency_id: matchedCurrency?.id || currentData?.currency_id,
          currency_symbol: matchedCurrency?.symbol || currentData?.currency_symbol
        };
        nextAction = "create_po_linked_bill_step";
        console.log('💰 Including edited expenses in bill data:', expenseData);
      } else {
        // 🔧 FIXED: Always pass proper NetSuite item ID + include expenses for standalone bills
        const expenseData = expenses.length > 0 ? {
          items: expenses.map(exp => {
            // ✅ Extract account ID properly - check account object, then account_id field
            let accountId = (typeof exp.account === 'object' && exp.account !== null)
              ? exp.account.id
              : (exp.account_id || exp.account || '');

            // 🔧 CRITICAL VALIDATION: Ensure we have numeric internal ID, not account name
            // If accountId is not a number or looks like a name, try to find the internal ID
            if (accountId && isNaN(accountId) && allExpenseAccounts && allExpenseAccounts.length > 0) {
              // Try to find by account name
              const matchedAccount = allExpenseAccounts.find(acc =>
                acc.accountsearchdisplaynamecopy === accountId ||
                acc.name === accountId ||
                acc.description === accountId
              );

              if (matchedAccount) {
                console.warn(`⚠️ Expense "${exp.name}" had account name "${accountId}" instead of ID. Using internal ID: ${matchedAccount.id}`);
                accountId = matchedAccount.id;
              } else {
                console.error(`❌ Could not find internal ID for account "${accountId}" - bill creation may fail!`);
              }
            }

            const expenseItem = {
              id: exp.id,  // ✅ CRITICAL: Preserve expense ID for AI updates
              account: { id: accountId },
              amount: parseFloat(exp.amount) || 0,
              memo: exp.memo || '',
              name: exp.name || ''
            };

            // 🔧 Add tax code to expense item (NetSuite supports taxcode on expense lines)
            if (exp.tax_code_id) {
              expenseItem.taxcode = { id: exp.tax_code_id };  // NetSuite expects: { id: "123" }
              console.log(`✅ Added tax to expense "${exp.name}": taxcode=${exp.tax_code_id}`);
            }

            // 🔧 Add location, department, class, customer to expense item
            if (exp.location?.id) {
              expenseItem.location = { id: exp.location.id };
            }
            if (exp.department?.id) {
              expenseItem.department = { id: exp.department.id };
            }
            if (exp.class?.id) {
              expenseItem.class = { id: exp.class.id };
            }
            if (exp.customer?.id) {
              expenseItem.customer = { id: exp.customer.id };
            }

            return expenseItem;
          }).filter(exp => exp.amount !== 0) // Remove zero-amount expenses (keep negative for credits/refunds)
        } : { items: [] };

        // 🔧 Filter out removed line items - use ref to ensure persistence
        const removedSet = removedLineItemsRef.current.size > 0 ? removedLineItemsRef.current : removedLineItems;
        const filteredLineItems = currentData.line_items.filter(item => item !== null && item !== undefined && !removedSet.has(item?.line_number));

        console.log(`📊 Line items: total=${currentData.line_items?.length || 0}, removed=${removedSet.size} (${Array.from(removedSet).join(', ')}), final=${filteredLineItems.length}`);
        if (filteredLineItems.length === 0 && currentData.line_items?.length > 0) {
          console.log(`✅ All ${currentData.line_items.length} line items have been removed - sending empty items array`);
        }

        finalBillData = {
          ...currentData,
          ...(billTranDateTouched && billTranDate ? { invoice_date: billTranDate, bill_date: billTranDate, tranDate: billTranDate } : {}),
          ...(billDueDateTouched && billDueDate ? { due_date: billDueDate, dueDate: billDueDate } : {}),
          memo: billMemo,  // 📝 Header-level Memo (empty by default for non-PO bills; editable; optional)
          vendor: {
            ...currentData.vendor,
            netsuite_vendor_id: selectedVendor,   // ✅ NetSuite internal numeric ID (e.g., "7212")
            selected_entity_id: selectedVendor    // ✅ Keep for backward compatibility
          },
          line_items: filteredLineItems.map(item => {
            let selectedItemId;
            let selectedItemDetails = null;
            const userSelection = selectedItems[item?.line_number];

            // Priority: user selection > matched item > null
            if (userSelection && userSelection !== '') {
              selectedItemId = userSelection;

              // 🔍 Find full item details from multiple sources (priority order)

              // 1. Check lineItemsAllItems (from "Load All" button) FIRST
              if (!selectedItemDetails && lineItemsAllItems[item?.line_number] && Array.isArray(lineItemsAllItems[item?.line_number])) {
                const foundItem = lineItemsAllItems[item?.line_number].find(nsItem =>
                  String(nsItem?.netsuite_item_id) === String(selectedItemId)
                );
                if (foundItem) {
                  console.log(`✅ Found selected item in lineItemsAllItems for line ${item?.line_number}:`, foundItem);
                  selectedItemDetails = {
                    netsuite_item_id: foundItem.netsuite_item_id,
                    item_name_code: foundItem.item_name_code,
                    item_display_name: foundItem.item_display_name,
                    // Handle both field names (different tools return different names)
                    item_description: foundItem.item_description || foundItem.item_description_from_netsuite
                  };
                }
              }

              // 2. Check available_items (from AI search results)
              if (!selectedItemDetails && item?.available_items && Array.isArray(item.available_items)) {
                const foundItem = item.available_items.find(nsItem =>
                  String(nsItem?.netsuite_item_id) === String(selectedItemId)
                );
                if (foundItem) {
                  console.log(`✅ Found selected item in available_items for line ${item?.line_number}:`, foundItem);
                  selectedItemDetails = {
                    netsuite_item_id: foundItem.netsuite_item_id,
                    item_name_code: foundItem.item_name_code,
                    item_display_name: foundItem.item_display_name,
                    // Handle both field names (different tools return different names)
                    item_description: foundItem.item_description || foundItem.item_description_from_netsuite
                  };
                }
              }

              // 3. Fallback: search in all_netsuite_items (legacy global list)
              if (!selectedItemDetails && currentData.all_netsuite_items && Array.isArray(currentData.all_netsuite_items)) {
                const foundItem = currentData.all_netsuite_items.find(nsItem =>
                  String(nsItem?.netsuite_item_id) === String(selectedItemId)
                );
                if (foundItem) {
                  console.log(`✅ Found selected item in all_netsuite_items for line ${item?.line_number}:`, foundItem);
                  selectedItemDetails = {
                    netsuite_item_id: foundItem.netsuite_item_id,
                    item_name_code: foundItem.item_name_code,
                    item_display_name: foundItem.item_display_name,
                    // Handle both field names (different tools return different names)
                    item_description: foundItem.item_description || foundItem.item_description_from_netsuite
                  };
                }
              }

              // 4. Warning if still not found
              if (!selectedItemDetails) {
                console.warn(`⚠️ Could not find item details for selected ID ${selectedItemId} on line ${item?.line_number}`);
              }
            } else if (item?.matched_netsuite_item) {
              selectedItemId = item.matched_netsuite_item.netsuite_item_id;
              // Also pass along the matched item details
              selectedItemDetails = item.matched_netsuite_item;
            } else {
              selectedItemId = null;
            }

            // 🔧 Match tax code for this line item
            // ✅ IMPORTANT: If using modifiedBillData (AI modified), respect AI's tax_code_id (could be null if AI removed it)
            // Only auto-match tax codes if not already set in the item
            let taxCodeId = item?.tax_code_id || null;  // Preserve existing tax_code_id if set

            // Only auto-match if no tax_code_id exists AND we're not using AI-modified data
            if (!taxCodeId && !modifiedBillData) {
              const itemTaxRate = item?.tax_rate;

              if (itemTaxRate !== null && itemTaxRate !== undefined && currentData.all_netsuite_tax_codes) {
                // Find matching tax code by rate
                const matchingTaxCode = currentData.all_netsuite_tax_codes.find(tc => {
                  const tcRate = tc.line ? parseFloat(tc.line.tax_rate) * 100 : null;
                  return tcRate !== null && Math.abs(tcRate - parseFloat(itemTaxRate)) < 0.01;
                });

                if (matchingTaxCode && matchingTaxCode.line) {
                  taxCodeId = matchingTaxCode.line.netsuite_tax_code_id;
                  console.log(`✅ Matched tax code for line ${item?.line_number}: rate=${itemTaxRate}% -> tax_code_id=${taxCodeId}`);
                } else {
                  console.warn(`⚠️ No matching tax code found for line ${item?.line_number} with rate ${itemTaxRate}%`);
                }
              }
            } else if (taxCodeId) {
              console.log(`✅ Using existing tax_code_id for line ${item?.line_number}: ${taxCodeId}`);
            } else {
              console.log(`ℹ️ No tax code for line ${item?.line_number} (AI may have removed it)`);
            }

            return {
              ...item,
              selected_netsuite_item_id: selectedItemId,
              // Include full item details for memorization
              matched_netsuite_item: selectedItemDetails,
              // Include matched tax code ID for bill creation (respects AI modifications)
              tax_code_id: taxCodeId,
              // ✅ Include discount fields (discount_rate and discount_amount)
              discount_rate: item?.discount_rate !== undefined ? item.discount_rate : null,
              discount_amount: item?.discount_amount !== undefined ? item.discount_amount : null
            };
          }),
          expense: expenseData,
          // 💱 Include matched currency
          currency: matchedCurrency?.code || currentData?.currency,
          currency_id: matchedCurrency?.id || currentData?.currency_id,
          currency_symbol: matchedCurrency?.symbol || currentData?.currency_symbol,
          // ✅ Include global discount fields (discount_rate and discount_amount)
          discount_rate: currentData?.discount_rate !== undefined ? currentData.discount_rate : null,
          discount_amount: currentData?.discount_amount !== undefined ? currentData.discount_amount : null
        };

        nextAction = "proceed_to_create_standalone_bill";
        console.log('💰 Including edited expenses in standalone bill data:', expenseData);
      }

      // 📄 USE PDF TOTAL: Keep total_amount from PDF extraction (constant value)
      // ✅ The total_amount from PDF should remain unchanged
      const pdfTotal = finalBillData.total_amount || finalBillData.bill_total_amount;

      if (pdfTotal !== null && pdfTotal !== undefined) {
        finalBillData.total_amount = parseFloat(pdfTotal);
        console.log(`✅ Using PDF total_amount: ${finalBillData.total_amount.toFixed(2)}`);
      } else {
        console.warn('⚠️ No total_amount found in PDF data, keeping original value');
      }

      // ✅ Keep tax_amount from PDF if available
      if (finalBillData.tax_amount === null || finalBillData.tax_amount === undefined) {
        console.warn('⚠️ No tax_amount in bill data');
      } else {
        console.log(`✅ Using PDF tax_amount: ${parseFloat(finalBillData.tax_amount).toFixed(2)}`);
      }

      // 🔎 Debug print of final selected IDs
      console.log("🧾 Final selected line items with NetSuite IDs:");
      finalBillData.line_items?.filter(li => li !== null && li !== undefined).forEach(li => {
        console.log(`   Line ${li?.line_number}: selected_netsuite_item_id =`, li?.selected_netsuite_item_id);
      });

      // 🗓️ DUE DATE: send it ONLY when the user actually set it in the modal. The `...currentData`
      // spread above can otherwise re-inject an extracted/auto due date even though the user never
      // touched the field. Strip it here when untouched so the payload has no dueDate and the
      // backend lets NetSuite derive it from the vendor's payment terms. (Transaction date is
      // always kept — a bill needs one.)
      if (!billDueDateTouched) {
        delete finalBillData.due_date;
        delete finalBillData.dueDate;
      }

      // OPTIMIZATION: For standalone bills, create a copy with reduced all_netsuite_items
      let optimizedBillData = finalBillData;
      if (!isPOLinked && finalBillData.all_netsuite_items && finalBillData.all_netsuite_items.length > 20) {
        optimizedBillData = {
          ...finalBillData,
          all_netsuite_items: finalBillData.all_netsuite_items.slice(0, 20)
        };
      }

      const confirmationData = {
        next_action_required: nextAction,
        bill_data: optimizedBillData
      };
      console.log("📤 Sending confirmationData to backend:", confirmationData);

      // ⚠️ DON'T update modifiedBillData during bill creation to prevent display issues
      // Updating here causes vendor/total amounts to disappear during bill creation
      // Only the payload sent to API needs tracking metadata
      const payloadWithTracking = {
        ...finalBillData,
        _payload_status: 'ready_for_bill_creation',
        _prepared_at: new Date().toISOString(),
        _previous_status: modifiedBillData?._payload_status || 'unknown'
      };
      // ❌ REMOVED: setModifiedBillData(payloadWithTracking); - Don't update display during creation
      console.log("💾 Prepared final payload (preserving current display to avoid UI changes)");
      console.log("💾 Payload status:", payloadWithTracking._payload_status);

      // 🔄 NEW: Call workflow API directly instead of using onConfirm callback
      // This keeps modal open to show results

      // ✅ FIX: Get runId and workflowId from currentData (already declared at line 671)
      // ⚠️ Don't redeclare currentData - reuse the one from line 671 to avoid TDZ error
      const effectiveRunId = propRunId || currentData?.run_id;
      const effectiveWorkflowId = propWorkflowId || currentData?.workflow_id;

      if (!effectiveRunId) {
        throw new Error('No workflow run ID available. Please try reopening the workflow.');
      }

      // Strip out large arrays to prevent 413 Content Too Large error
      const {
        all_netsuite_items,
        original_pdf_line_items,
        available_vendors,
        ...billDataWithoutLargeArrays
      } = confirmationData;

      // Also strip from nested bill_data
      if (billDataWithoutLargeArrays.bill_data) {
        const {
          all_netsuite_items: nestedItems,
          original_pdf_line_items: nestedPdfItems,
          available_vendors: nestedVendors,
          // ✅ FIX: Strip payload tracking metadata (internal use only, not for backend)
          _payload_status,
          _initialized_at,
          _modified_at,
          _prepared_at,
          _previous_status,
          ...cleanBillData
        } = billDataWithoutLargeArrays.bill_data;
        billDataWithoutLargeArrays.bill_data = cleanBillData;
        console.log("🧹 Stripped payload tracking metadata before sending to backend");
      }

      // Strip from po_data if exists
      if (billDataWithoutLargeArrays.bill_data?.po_data) {
        const { inventory_assignments, ...cleanPoData } = billDataWithoutLargeArrays.bill_data.po_data;
        billDataWithoutLargeArrays.bill_data.po_data = cleanPoData;
      }

      const userMessage = `Bill confirmed for creation with the following data: ${JSON.stringify(billDataWithoutLargeArrays)}`;

      console.log('🌐 Calling workflow API to create bill:', `/api/v2/workflows/continue/${effectiveRunId}`);

      const response = await fetch(`/api/v2/workflows/continue/${effectiveRunId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_message: userMessage,
          token: freshToken
        })
      });

      console.log('📡 Bill creation response - status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Bill creation API error:', errorText);
        throw new Error(`Bill creation failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Bill creation result:', result);
      console.log('🔍 RESPONSE DEBUG - Full result structure:', {
        success: result.success,
        run_id: result.run_id,
        message: result.message,
        data_keys: result.data ? Object.keys(result.data) : null,
        messages_count: result.data?.messages?.length || 0,
        has_messages_array: Array.isArray(result.data?.messages)
      });

      // ✅ CRITICAL FIX: Trigger message polling for draft workflows
      // This ensures background chat updates while modal is open (same behavior as live workflows)
      if (chatService && chatService.pollForNewMessages) {
        console.log('📨 Triggering message polling to update background chat');
        // Use chatService's tracked message count (messages already displayed in UI)
        const currentMessageCount = chatService.v2MessageCount || 0;
        console.log(`📊 Starting poll from message count: ${currentMessageCount} (total in response: ${result.data?.messages?.length || 0})`);
        // Don't await - let it run in background
        chatService.pollForNewMessages(effectiveRunId, currentMessageCount, 15).catch(err => {
          console.error('⚠️ Message polling error (non-critical):', err);
        });
      } else {
        console.warn('⚠️ chatService not available for message polling - background messages may not update');
      }

      // ✅ FIX: Check actual workflow status from backend response
      // Backend returns: {success: true/false, message: "...", data: {status: "completed"/"failed"/...}}
      const workflowStatus = result.data?.status || result.status;
      const backendSuccess = result.success;

      console.log(`📊 Backend response - success: ${backendSuccess}, workflow status: ${workflowStatus}, message: ${result.message}`);

      // Check if workflow failed - either success=false OR status='failed'
      if (!backendSuccess || workflowStatus === 'failed') {
        // Workflow failed - show error state
        console.error('❌ Bill creation failed - backend success:', backendSuccess, 'workflow status:', workflowStatus);

        setBillCreationStatus('error');
        setBillCreationMessage(result.message || 'Bill creation failed. Please check the error message and try again.');

        // Add error message to chat
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: `❌ ${result.message || 'Bill creation failed. Please check the chat history for error details and try again.'}`
        }]);

        return; // Exit early - don't proceed to success handling
      }

      // Extract NetSuite bill URL from response
      // The URL is typically in the workflow messages from the bill creation tool
      let netsuiteUrl = null;

      console.log('🔍 URL EXTRACTION: Starting URL search in response');
      console.log('🔍 URL EXTRACTION: result.data structure:', Object.keys(result.data || {}));
      console.log('🔍 URL EXTRACTION: messages count:', result.data?.messages?.length);

      // ✅ FIX: Extract URL from workflow messages (tool results from create_netsuite_vendor_bill or create_bill_from_purchase_order)
      if (result.data?.messages && Array.isArray(result.data.messages)) {
        console.log('🔍 URL EXTRACTION: Searching through', result.data.messages.length, 'messages');

        // Search messages in reverse (most recent first) for bill creation tool results
        for (let i = result.data.messages.length - 1; i >= 0; i--) {
          const msg = result.data.messages[i];
          console.log(`🔍 URL EXTRACTION: Message ${i}:`, {
            message_type: msg.message_type,
            tool_name: msg.tool_name,
            has_content: !!msg.message_content
          });

          // Check tool_result messages for bill creation tools
          if (msg.message_type === 'tool_result' &&
              (msg.tool_name === 'create_netsuite_vendor_bill' || msg.tool_name === 'create_bill_from_purchase_order')) {
            console.log('🔍 URL EXTRACTION: Found bill creation tool result:', msg.tool_name);
            try {
              // Parse the tool result content
              const content = typeof msg.message_content === 'string' ? msg.message_content : JSON.stringify(msg.message_content);
              console.log('🔍 URL EXTRACTION: Tool result content preview:', content.substring(0, 200));

              // Extract JSON from "Completed: tool_name\nResponse:\n{...}" format
              // Try multiple extraction methods to handle different formats
              let toolResult = null;

              // Method 1: Try parsing after "Response:" with flexible whitespace/newline handling
              const jsonMatch = content.match(/Response:[\s\n]*(\{[\s\S]*?\})\s*$/m);
              if (jsonMatch) {
                console.log('🔍 URL EXTRACTION: Found JSON in response (Method 1)');
                try {
                  toolResult = JSON.parse(jsonMatch[1]);
                } catch (e) {
                  console.warn('⚠️ Failed to parse JSON from regex match:', e);
                }
              }

              // Method 2: If Method 1 failed, try finding JSON by looking for first { to last }
              if (!toolResult && content.includes('{') && content.includes('}')) {
                const startIdx = content.indexOf('{');
                const endIdx = content.lastIndexOf('}');
                if (startIdx >= 0 && endIdx > startIdx) {
                  try {
                    const jsonStr = content.substring(startIdx, endIdx + 1);
                    console.log('🔍 URL EXTRACTION: Found JSON in response (Method 2)');
                    toolResult = JSON.parse(jsonStr);
                  } catch (e) {
                    console.warn('⚠️ Failed to parse JSON from substring:', e);
                  }
                }
              }

              if (toolResult) {
                console.log('🔍 URL EXTRACTION: Parsed tool result keys:', Object.keys(toolResult));
                if (toolResult.netsuite_url) {
                  netsuiteUrl = toolResult.netsuite_url;
                  console.log('✅ Found NetSuite URL in tool result:', netsuiteUrl);
                  break;
                } else {
                  console.warn('⚠️ Tool result parsed but no netsuite_url field found:', Object.keys(toolResult));
                }
              } else {
                console.warn('⚠️ Could not extract JSON from tool result content');
                console.warn('⚠️ Content length:', content.length, 'First 500 chars:', content.substring(0, 500));
              }
            } catch (e) {
              console.error('❌ Failed to parse tool result for URL:', e);
              console.error('❌ Content that failed to parse:', msg.message_content);
            }
          }
        }
      } else {
        console.warn('⚠️ URL EXTRACTION: No messages array in result.data');
      }

      // Fallback: Check top-level result fields (legacy support)
      if (!netsuiteUrl) {
        console.log('🔍 URL EXTRACTION: No URL found in messages, trying fallback locations');
        console.log('🔍 URL EXTRACTION: result keys:', Object.keys(result));

        if (result.bill_url) {
          netsuiteUrl = result.bill_url;
          console.log('✅ Found URL in result.bill_url:', netsuiteUrl);
        } else if (result.netsuite_url) {
          netsuiteUrl = result.netsuite_url;
          console.log('✅ Found URL in result.netsuite_url:', netsuiteUrl);
        } else if (result.netsuite_bill_url) {
          netsuiteUrl = result.netsuite_bill_url;
          console.log('✅ Found URL in result.netsuite_bill_url:', netsuiteUrl);
        } else if (result.data?.bill_url) {
          netsuiteUrl = result.data.bill_url;
          console.log('✅ Found URL in result.data.bill_url:', netsuiteUrl);
        } else if (result.message && result.message.includes('https://')) {
          // Extract URL from message text
          const urlMatch = result.message.match(/(https:\/\/[^\s]+)/);
          if (urlMatch) {
            netsuiteUrl = urlMatch[1];
            console.log('✅ Found URL in result.message:', netsuiteUrl);
          }
        } else {
          console.error('❌ No NetSuite URL found in any location');
          console.error('❌ Full result:', JSON.stringify(result, null, 2).substring(0, 1000));
        }
      }

      // Set success state
      setBillCreationStatus('success');
      setBillCreationMessage('Bill created successfully in NetSuite!');
      setNetSuiteBillUrl(netsuiteUrl || '');

      console.log('🎯 FINAL URL SET:', netsuiteUrl || '(empty)');
      console.log('🎯 netSuiteBillUrl state will be:', netsuiteUrl || '');

      // Add success message to chat
      const successMsg = netsuiteUrl
        ? `✅ Bill created successfully! View in NetSuite: ${netsuiteUrl}`
        : '✅ Bill created successfully in NetSuite!';

      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: successMsg
      }]);

      console.log('✅ Bill creation completed successfully');
      console.log('✅ NetSuite URL:', netsuiteUrl || '(not found)');

    } catch (error) {
      console.error('🔥 Error confirming bill:', error);

      // Set error state instead of showing alert
      setBillCreationStatus('error');
      setBillCreationMessage(error.message || 'Failed to create bill in NetSuite');

      // Add error to chat so user can see it and fix issues
      setChatMessages(prev => [...prev, {
        role: 'system',
        content: `❌ Error creating bill: ${error.message || 'Unknown error'}. Please review the bill data and try again.`
      }]);

      console.log('❌ Bill creation failed, user can modify and retry');
    } finally {
      setLoading(false);
      // 🔓 UNFREEZE display data after bill creation completes
      setFrozenDisplayData(null);
      console.log('🔓 Display data unfrozen - UI can update normally');
    }
  };

  // ❌ X Button Handler - Just close modal, no draft saving
  const handleClose = () => {
    console.log('❌ X button clicked - closing modal without draft save');
    console.log('📍 User stays on current chat page');

    // Close AI chat if open
    setChatOpen(false);
    setLoading(false);

    // ⚠️ CRITICAL FIX: Pass modified bill data to parent even when closing without draft save
    // This allows parent to preserve modifications if needed
    const dataToPass = modifiedBillData || billData;
    console.log('📤 Passing modified bill data to onClose:', dataToPass ? 'has data' : 'no data');

    // If parent provided onClose callback, use it (just hides modal, no draft save)
    // Otherwise fall back to onCancel (legacy behavior)
    if (onClose) {
      console.log('✅ Using onClose callback - passing modified data');
      onClose(dataToPass); // Pass modified data
    } else {
      console.log('⚠️ No onClose prop provided, falling back to onCancel');
      if (onCancel) {
        onCancel(dataToPass); // Pass modified data
      }
    }
  };

  // 🔴 Cancel Button Handler - Save as draft and close
  const handleCancel = async () => {
    console.log('🔴 Cancel button clicked - saving as draft');

    // ⚠️ CRITICAL FIX: Pass modified bill data to parent for draft saving
    const dataToPass = modifiedBillData || billData;
    console.log('📤 Passing modified bill data to onCancel for draft save:', dataToPass ? 'has data' : 'no data');
    if (modifiedBillData) {
      console.log('💾 Modified data includes:', Object.keys(modifiedBillData));
    }

    // Bill not created yet - proceed with normal cancellation (save as draft)
    setLoading(true);
    try {
      if (onCancel) {
        await onCancel(dataToPass); // Pass modified data
      }
    } catch (error) {
      console.error('🚫 Error during cancellation:', error);
    } finally {
      setLoading(false);
    }
  };

  // 💰 Expense Handlers
  const handleExpenseChange = (expenseId, field, value) => {
    setExpenses(prevExpenses =>
      prevExpenses.map(exp =>
        exp.id === expenseId
          ? { ...exp, [field]: value }
          : exp
      )
    );
  };

  const handleAddExpense = () => {
    // 🎯 Get defaults - PO items for PO-linked, AI defaults for standalone
    const aiDefaults = workflowContext?.bill_defaults_result?.bill_data;
    const poData = billData?.po_data;
    const firstPoItem = poData?.line_items?.[0];

    // Use PO defaults if available (convert IDs to object format), otherwise AI defaults
    const poLocation = firstPoItem?.location_id ? { id: String(firstPoItem.location_id) } : firstPoItem?.location;
    const poClass = firstPoItem?.class_id ? { id: String(firstPoItem.class_id) } : firstPoItem?.class;
    const poDepartment = firstPoItem?.department_id ? { id: String(firstPoItem.department_id) } : firstPoItem?.department;

    const defaultLocation = (poData && poLocation?.id) ? poLocation : aiDefaults?.default_location;
    const defaultDepartment = (poData && poDepartment?.id) ? poDepartment : aiDefaults?.default_department;
    const defaultClass = (poData && poClass?.id) ? poClass : aiDefaults?.default_class;

    const newExpense = {
      id: nextExpenseId,
      account: { id: '' },
      amount: '',
      memo: '',
      name: '',
      // 🎯 Apply PO or AI defaults for location/department/class
      location: defaultLocation || null,
      department: defaultDepartment || null,
      class: defaultClass || null,
      customer: null
    };

    console.log('🎯 Applied defaults to new expense:', {
      source: poData ? 'PO' : 'AI',
      location: newExpense.location?.id,
      department: newExpense.department?.id,
      class: newExpense.class?.id
    });

    // 🔧 AUTO-APPLY PDF TAX: If PDF has tax and we have tax codes, auto-match
    if (currentBillData?.tax_rate && allTaxCodes && allTaxCodes.length > 0) {
      const pdfTaxRate = parseFloat(currentBillData.tax_rate);
      const matchingTaxCode = allTaxCodes.find(tc => {
        if (tc.rate) {
          const tcRate = parseFloat(tc.rate) * 100; // Convert 0.09 to 9
          return Math.abs(tcRate - pdfTaxRate) < 0.1; // Match within 0.1%
        }
        return false;
      });

      if (matchingTaxCode) {
        newExpense.tax_code_id = matchingTaxCode.id;
        newExpense.tax_rate = pdfTaxRate;
        console.log(`✅ Auto-applied PDF tax to new expense: ${pdfTaxRate}% (tax_code_id=${newExpense.tax_code_id})`);
      }
    }

    setExpenses([...expenses, newExpense]);
    setNextExpenseId(nextExpenseId + 1);
  };

  const handleRemoveExpense = (expenseId) => {
    // 🔧 FIX: Mark that user has manually modified expenses - prevents re-initialization
    setExpensesInitialized(true);

    setExpenses(prevExpenses => {
      const updatedExpenses = prevExpenses.filter(exp => exp.id !== expenseId);
      console.log(`💰 Removed expense ${expenseId} - now ${updatedExpenses.length} expenses remaining`);
      return updatedExpenses;
    });
  };

  // Handle sending chat message to modify bill
  const handleSendChatMessage = async () => {
    console.log('🚀 handleSendChatMessage called, chatInput:', chatInput);

    if (!chatInput.trim()) {
      console.log('⚠️ Chat input is empty, returning');
      return;
    }

    const userMessage = chatInput.trim();
    console.log('💬 Processing user message:', userMessage);
    setChatInput('');

    // Add user message to chat
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, newUserMessage]);

    setChatLoading(true);
    console.log('⏳ Chat loading started');

    try {
      const currentData = modifiedBillData || billData;
      console.log('📦 Current bill data source:', modifiedBillData ? 'MODIFIED' : 'ORIGINAL');

      // IMPORTANT: Create a shallow copy to avoid mutating original billData/modifiedBillData
      // We only optimize the payload sent to the chat API - original data stays intact
      const optimizedData = { ...currentData };

      // ⭐ CRITICAL: Sync current expense state to the bill data being sent to backend
      // This ensures removed expenses don't come back when AI adds new ones
      if (expenses && expenses.length > 0) {
        optimizedData.expense = {
          items: expenses.map(exp => ({
            id: exp.id,
            account: exp.account_id || (typeof exp.account === 'object' ? exp.account.id : exp.account) || '',
            account_id: exp.account_id || (typeof exp.account === 'object' ? exp.account.id : exp.account) || '',
            name: exp.name || exp.account_name || '',
            account_name: exp.account_name || exp.name || '',
            amount: exp.amount,
            memo: exp.memo || '',
            tax_code_id: exp.tax_code_id || '',
            tax_rate: exp.tax_rate || null,
            source: exp.source || 'existing',
            confidence: exp.confidence || null,
            original_description: exp.original_description || null,
            location: exp.location || null,
            class: exp.class || null
          }))
        };
        console.log(`💰 Synced ${expenses.length} current expenses to bill_data before sending to AI`);
      } else {
        // No expenses - clear the expense section
        optimizedData.expense = { items: [] };
        console.log('💰 No expenses in current state - cleared expense.items before sending to AI');
      }

      // Remove large lookup arrays to reduce request size (backend doesn't need these for chat)
      // This prevents 413 "Payload Too Large" errors without affecting normal bill creation flow
      delete optimizedData.all_netsuite_items;
      delete optimizedData.all_netsuite_classes;
      delete optimizedData.all_netsuite_locations;
      delete optimizedData.all_netsuite_departments;
      delete optimizedData.all_netsuite_tax_codes;
      delete optimizedData.available_vendors;

      // Get auth token from cookie
      const authToken = getAuthToken() || token;
      if (!authToken) {
        console.error('❌ No auth token available');
        throw new Error('No authentication token available. Please login again.');
      }

      console.log('📡 Sending POST request to /api/bill-processing/modify-bill-chat');
      console.log('📝 Request payload:', {
        user_message: userMessage,
        has_bill_data: !!optimizedData,
        expense_count: optimizedData?.expense?.items?.length || 0
      });

      const response = await fetch('/api/bill-processing/modify-bill-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Accept-Encoding': 'gzip, deflate, br',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          user_message: userMessage,
          current_bill_data: optimizedData,  // Send optimized copy, not original
          conversation_history: chatMessages,
          run_id: propRunId,
          workflow_id: propWorkflowId
        })
      });

      console.log('✅ Response received, status:', response.status, 'ok:', response.ok);

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      console.log('📄 Response content-type:', contentType);
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('❌ Non-JSON response received:', textResponse.substring(0, 200));
        throw new Error(`Server returned non-JSON response (status: ${response.status})`);
      }

      const data = await response.json();

      if (data.success) {
        // Add AI response to chat
        const aiMessage = {
          role: 'assistant',
          content: data.ai_response,
          source: 'ai_chat', // Mark as AI assistant chat (not workflow message)
          timestamp: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, aiMessage]);

        // ⭐ IMPORTANT: Sync expenses FIRST, before updating modifiedBillData
        // This prevents useEffect race condition where it reinitializes expenses
        if (data.modified_bill_data && data.modified_bill_data.expense && data.modified_bill_data.expense.items) {
          console.log('💰 Syncing expenses from AI-modified bill data:', data.modified_bill_data.expense.items);
          console.log('💰 Number of expenses in modified data:', data.modified_bill_data.expense.items.length);

          // Convert backend expense structure to frontend structure
          // 🔧 CRITICAL FIX: Merge with existing expenses to preserve fields from previous modifications
          const updatedExpenses = data.modified_bill_data.expense.items.map((exp, index) => {
            console.log(`💰 Processing expense ${index + 1}:`, exp);
            console.log(`💰 Expense ${index + 1} tax_code_id:`, exp.tax_code_id);
            console.log(`💰 Expense ${index + 1} tax_rate:`, exp.tax_rate);

            // Find existing expense by ID to preserve fields from previous modifications
            const existingExpense = expenses.find(e => e.id === exp.id);
            if (existingExpense) {
              console.log(`🔄 Found existing expense ${exp.id}, will merge with new data`);
            }

            // Handle account field - backend returns string, frontend expects object
            let accountObj;
            let accountId = '';
            if (typeof exp.account === 'object' && exp.account !== null) {
              // Already an object
              accountId = exp.account.id || '';
              accountObj = { id: accountId };
            } else if (typeof exp.account === 'string') {
              // String ID from backend
              accountId = exp.account;
              accountObj = { id: accountId };
            } else if (exp.account_id) {
              // Fallback to account_id field
              accountId = exp.account_id;
              accountObj = { id: accountId };
            } else {
              accountObj = { id: '' };
            }

            // ✅ CRITICAL: Look up account name AND validate internal ID from allExpenseAccounts
            let accountName = exp.name || exp.account_name || '';
            let validatedAccountId = accountId;

            if (accountId && allExpenseAccounts && allExpenseAccounts.length > 0) {
              // First try to match by internal ID (numeric)
              let matchedAccount = allExpenseAccounts.find(acc => String(acc.id) === String(accountId));

              // If not found by ID, check if AI provided account number instead of internal ID
              if (!matchedAccount && !isNaN(accountId)) {
                matchedAccount = allExpenseAccounts.find(acc => String(acc.acctnumber) === String(accountId));
                if (matchedAccount) {
                  console.log(`⚠️ AI provided account number ${accountId}, not internal ID. Using internal ID: ${matchedAccount.id}`);
                  validatedAccountId = matchedAccount.id;
                  accountName = matchedAccount.accountsearchdisplaynamecopy || matchedAccount.name || '';
                }
              }

              // 🔧 NEW: If still not found and accountId is a string name (not numeric), search by name
              if (!matchedAccount && isNaN(accountId)) {
                matchedAccount = allExpenseAccounts.find(acc =>
                  acc.accountsearchdisplaynamecopy === accountId ||
                  acc.name === accountId ||
                  acc.description === accountId ||
                  (acc.accountsearchdisplaynamecopy && acc.accountsearchdisplaynamecopy.toLowerCase().includes(accountId.toLowerCase()))
                );
                if (matchedAccount) {
                  console.log(`✅ AI provided account name "${accountId}". Matched to: ${matchedAccount.accountsearchdisplaynamecopy} (ID: ${matchedAccount.id})`);
                  validatedAccountId = matchedAccount.id;
                  accountName = matchedAccount.accountsearchdisplaynamecopy || matchedAccount.name || '';
                } else {
                  console.error(`❌ AI provided account name "${accountId}" but could not find matching account!`);
                }
              }

              // Set account name if found and not already set
              if (matchedAccount && !accountName) {
                accountName = matchedAccount.accountsearchdisplaynamecopy || matchedAccount.name || '';
                console.log(`✅ Looked up account for ID ${accountId}: ${accountName} (internal ID: ${validatedAccountId})`);
              }
            }

            // ✅ VALIDATION: Location - validate and look up ID from allLocations
            let validatedLocation = null;
            const locationInput = exp.location?.id || exp.location_id || exp.location;
            console.log(`🔍 Location input from AI:`, locationInput, `| allLocations loaded:`, allLocations?.length || 0);
            if (locationInput && allLocations && allLocations.length > 0) {
              // First try to match by ID
              let matchedLocation = allLocations.find(loc => String(loc.id) === String(locationInput));

              // If not found by ID, try matching by name
              if (!matchedLocation && isNaN(locationInput)) {
                matchedLocation = allLocations.find(loc =>
                  loc.name === locationInput ||
                  (loc.name && loc.name.toLowerCase().includes(String(locationInput).toLowerCase()))
                );
              }

              if (matchedLocation) {
                validatedLocation = { id: matchedLocation.id, name: matchedLocation.name };
                console.log(`✅ Location validated: "${locationInput}" -> ID: ${matchedLocation.id}, Name: ${matchedLocation.name}`);
              } else {
                // Not found in list - if AI provided an ID, try to use it anyway
                console.warn(`⚠️ Location "${locationInput}" not found in allLocations (${allLocations.length} items)`);
                if (exp.location?.id) {
                  validatedLocation = exp.location;
                  console.log(`⚠️ Using original AI location with ID: ${exp.location.id}`);
                }
              }
            } else if (locationInput) {
              // Arrays not loaded but AI provided something - preserve the original if it has ID
              if (exp.location?.id) {
                validatedLocation = exp.location;
                console.log(`⚠️ allLocations empty, using original AI location: ${JSON.stringify(exp.location)}`);
              } else if (!isNaN(locationInput)) {
                // AI provided numeric ID only
                validatedLocation = { id: locationInput };
                console.log(`⚠️ allLocations empty, using AI location ID: ${locationInput}`);
              }
            }

            // ✅ VALIDATION: Department - validate and look up ID from allDepartments
            let validatedDepartment = null;
            const departmentInput = exp.department?.id || exp.department_id || exp.department;
            console.log(`🔍 Department input from AI:`, departmentInput, `| allDepartments loaded:`, allDepartments?.length || 0);
            if (departmentInput && allDepartments && allDepartments.length > 0) {
              // First try to match by ID
              let matchedDepartment = allDepartments.find(dept => String(dept.id) === String(departmentInput));

              // If not found by ID, try matching by name
              if (!matchedDepartment && isNaN(departmentInput)) {
                matchedDepartment = allDepartments.find(dept =>
                  dept.name === departmentInput ||
                  (dept.name && dept.name.toLowerCase().includes(String(departmentInput).toLowerCase()))
                );
              }

              if (matchedDepartment) {
                validatedDepartment = { id: matchedDepartment.id, name: matchedDepartment.name };
                console.log(`✅ Department validated: "${departmentInput}" -> ID: ${matchedDepartment.id}, Name: ${matchedDepartment.name}`);
              } else {
                console.warn(`⚠️ Department "${departmentInput}" not found in allDepartments (${allDepartments.length} items)`);
                if (exp.department?.id) {
                  validatedDepartment = exp.department;
                  console.log(`⚠️ Using original AI department with ID: ${exp.department.id}`);
                }
              }
            } else if (departmentInput) {
              if (exp.department?.id) {
                validatedDepartment = exp.department;
                console.log(`⚠️ allDepartments empty, using original AI department: ${JSON.stringify(exp.department)}`);
              } else if (!isNaN(departmentInput)) {
                validatedDepartment = { id: departmentInput };
                console.log(`⚠️ allDepartments empty, using AI department ID: ${departmentInput}`);
              }
            }

            // ✅ VALIDATION: Class - validate and look up ID from allClasses
            let validatedClass = null;
            const classInput = exp.class?.id || exp.class_id || exp.class;
            console.log(`🔍 Class input from AI:`, classInput, `| allClasses loaded:`, allClasses?.length || 0);
            if (classInput && allClasses && allClasses.length > 0) {
              // First try to match by ID
              let matchedClass = allClasses.find(cls => String(cls.id) === String(classInput));

              // If not found by ID, try matching by name
              if (!matchedClass && isNaN(classInput)) {
                matchedClass = allClasses.find(cls =>
                  cls.name === classInput ||
                  (cls.name && cls.name.toLowerCase().includes(String(classInput).toLowerCase()))
                );
              }

              if (matchedClass) {
                validatedClass = { id: matchedClass.id, name: matchedClass.name };
                console.log(`✅ Class validated: "${classInput}" -> ID: ${matchedClass.id}, Name: ${matchedClass.name}`);
              } else {
                console.warn(`⚠️ Class "${classInput}" not found in allClasses (${allClasses.length} items)`);
                if (exp.class?.id) {
                  validatedClass = exp.class;
                  console.log(`⚠️ Using original AI class with ID: ${exp.class.id}`);
                }
              }
            } else if (classInput) {
              if (exp.class?.id) {
                validatedClass = exp.class;
                console.log(`⚠️ allClasses empty, using original AI class: ${JSON.stringify(exp.class)}`);
              } else if (!isNaN(classInput)) {
                validatedClass = { id: classInput };
                console.log(`⚠️ allClasses empty, using AI class ID: ${classInput}`);
              }
            }

            // ✅ VALIDATION: Customer - validate and look up ID from allCustomers
            let validatedCustomer = null;
            const customerInput = exp.customer?.id || exp.customer_id || exp.customer;
            console.log(`🔍 Customer input from AI:`, customerInput, `| allCustomers loaded:`, allCustomers?.length || 0);
            if (customerInput && allCustomers && allCustomers.length > 0) {
              // First try to match by ID
              let matchedCustomer = allCustomers.find(cust => String(cust.id) === String(customerInput));

              // If not found by ID, try matching by name (companyname or entityid)
              if (!matchedCustomer && isNaN(customerInput)) {
                matchedCustomer = allCustomers.find(cust =>
                  cust.companyname === customerInput ||
                  cust.entityid === customerInput ||
                  (cust.companyname && cust.companyname.toLowerCase().includes(String(customerInput).toLowerCase())) ||
                  (cust.entityid && cust.entityid.toLowerCase().includes(String(customerInput).toLowerCase()))
                );
              }

              if (matchedCustomer) {
                validatedCustomer = { id: matchedCustomer.id, name: matchedCustomer.companyname || matchedCustomer.entityid };
                console.log(`✅ Customer validated: "${customerInput}" -> ID: ${matchedCustomer.id}, Name: ${matchedCustomer.companyname || matchedCustomer.entityid}`);
              } else {
                console.warn(`⚠️ Customer "${customerInput}" not found in allCustomers (${allCustomers.length} items)`);
                if (exp.customer?.id) {
                  validatedCustomer = exp.customer;
                  console.log(`⚠️ Using original AI customer with ID: ${exp.customer.id}`);
                }
              }
            } else if (customerInput) {
              if (exp.customer?.id) {
                validatedCustomer = exp.customer;
                console.log(`⚠️ allCustomers empty, using original AI customer: ${JSON.stringify(exp.customer)}`);
              } else if (!isNaN(customerInput)) {
                validatedCustomer = { id: customerInput };
                console.log(`⚠️ allCustomers empty, using AI customer ID: ${customerInput}`);
              }
            }

            console.log(`🔄 AI Expense field validation complete:`, {
              location: validatedLocation,
              department: validatedDepartment,
              class: validatedClass,
              customer: validatedCustomer
            });

            const convertedExpense = {
              id: exp.id || nextExpenseId + index,
              account: { id: validatedAccountId },  // Use validated internal ID
              account_id: validatedAccountId,  // Use validated internal ID
              name: accountName,
              account_name: accountName,
              amount: exp.amount || '',
              memo: exp.memo || '',
              // 🔧 PRESERVE fields from existing expense if not provided by AI
              tax_code_id: exp.tax_code_id !== undefined ? exp.tax_code_id : (existingExpense?.tax_code_id || ''),
              tax_rate: exp.tax_rate !== undefined && exp.tax_rate !== null ? exp.tax_rate : (existingExpense?.tax_rate || null),
              item_id: exp.item_id || existingExpense?.item_id,
              item: exp.item || existingExpense?.item,
              source: exp.source || existingExpense?.source || 'ai_added',
              added_by_ai: exp.source === 'ai_added' || existingExpense?.added_by_ai,
              confidence: exp.confidence !== undefined ? exp.confidence : (existingExpense?.confidence || null),
              original_description: exp.original_description || existingExpense?.original_description || null,
              // 🎯 Apply VALIDATED AI values, fall back to existing expense, then AI defaults
              // Use validated values from lookup against allLocations/allDepartments/allClasses/allCustomers
              location: validatedLocation || (existingExpense?.location?.id ? existingExpense.location : null) || workflowContext?.bill_defaults_result?.bill_data?.default_location || null,
              department: validatedDepartment || (existingExpense?.department?.id ? existingExpense.department : null) || workflowContext?.bill_defaults_result?.bill_data?.default_department || null,
              class: validatedClass || (existingExpense?.class?.id ? existingExpense.class : null) || workflowContext?.bill_defaults_result?.bill_data?.default_class || null,
              customer: validatedCustomer || existingExpense?.customer || null
            };

            console.log(`💰 Converted expense ${index + 1}:`, convertedExpense);
            console.log(`💰 Expense ${index + 1} source field:`, convertedExpense.source);
            console.log(`💰 Expense ${index + 1} added_by_ai field:`, convertedExpense.added_by_ai);
            // 🎯 Log AI defaults application for expenses
            const aiDefaults = workflowContext?.bill_defaults_result?.bill_data;
            if (aiDefaults) {
              console.log(`🎯 Expense ${index + 1} AI Defaults check - location: ${convertedExpense.location?.id || 'none'}, dept: ${convertedExpense.department?.id || 'none'}, class: ${convertedExpense.class?.id || 'none'}`);
              if (convertedExpense.location?.id === aiDefaults.default_location?.id) {
                console.log(`📍 Expense ${index + 1} location from AI defaults`);
              }
              if (convertedExpense.department?.id === aiDefaults.default_department?.id) {
                console.log(`🏢 Expense ${index + 1} department from AI defaults`);
              }
              if (convertedExpense.class?.id === aiDefaults.default_class?.id) {
                console.log(`📦 Expense ${index + 1} class from AI defaults`);
              }
            }
            if (existingExpense) {
              console.log(`✅ Merged with existing expense - preserved: item_id=${convertedExpense.item_id}, tax_code_id=${convertedExpense.tax_code_id}`);
            }
            return convertedExpense;
          });

          console.log('💰 Converted expenses for frontend:', updatedExpenses);
          console.log('💰 About to call setExpenses with these expenses');
          setExpenses(updatedExpenses);
          console.log('💰 setExpenses called - expenses state should now be updated');

          // Update next expense ID to avoid conflicts
          // ✅ FIX: Handle empty array case (Math.max returns -Infinity on empty spread)
          const maxId = updatedExpenses.length > 0
            ? Math.max(...updatedExpenses.map(e => e.id || 0), nextExpenseId)
            : nextExpenseId;
          setNextExpenseId(maxId + 1);

          console.log(`✅ Synced ${updatedExpenses.length} expenses, next ID: ${maxId + 1}`);
          console.log(`✅ Current expenses state after sync:`, updatedExpenses);
        } else {
          console.warn('⚠️ No expenses found in AI-modified bill data');
          console.log('⚠️ Modified bill data structure:', data.modified_bill_data);
        }

        // ⭐ Update modifiedBillData AFTER syncing expenses to prevent useEffect race condition
        // ✅ Preserve payload tracking metadata AND available_items from original data
        const currentData = getCurrentBillData();

        // Helper function to clean null characters from strings
        const cleanNullChars = (str) => {
          if (typeof str === 'string') {
            return str.replace(/\u0000/g, '').trim();
          }
          return str;
        };

        // Merge line items: preserve available_items + clean null characters
        const mergedLineItems = data.modified_bill_data.line_items?.map(aiItem => {
          // Find matching item from current data by line_number
          const currentItem = currentData.line_items?.find(item => item?.line_number === aiItem?.line_number);

          // Clean null characters from string fields
          const cleanedItem = {
            ...aiItem,
            description: cleanNullChars(aiItem.description),
            item_name: cleanNullChars(aiItem.item_name),
            tax_label: cleanNullChars(aiItem.tax_label),
            available_items: currentItem?.available_items || aiItem?.available_items || []
          };

          // Also clean available_items descriptions
          if (cleanedItem.available_items) {
            cleanedItem.available_items = cleanedItem.available_items.map(avItem => ({
              ...avItem,
              item_description: cleanNullChars(avItem.item_description),
              item_name_code: cleanNullChars(avItem.item_name_code)
            }));
          }

          return cleanedItem;
        });

        const updatedPayload = {
          ...data.modified_bill_data,
          line_items: mergedLineItems,  // Use merged line items with preserved available_items
          _payload_status: 'ai_modified',
          _modified_at: new Date().toISOString(),
          _previous_status: modifiedBillData?._payload_status || 'unknown'
        };
        setModifiedBillData(updatedPayload);
        console.log('✅ AI-modified payload updated, preserving tracking metadata and available_items');

        // 🎯 Sync AI-modified line item location/department/class to state variables
        // This ensures dropdowns show the correct values after AI modification
        const aiDefaults = workflowContext?.bill_defaults_result?.bill_data;
        if (mergedLineItems && mergedLineItems.length > 0) {
          const newLocations = {};
          const newDepartments = {};
          const newClasses = {};

          mergedLineItems.forEach(item => {
            const lineNum = item?.line_number;
            if (lineNum) {
              // Use AI-provided value, or fallback to AI default if not set
              const locId = item.location?.id || aiDefaults?.default_location?.id;
              const deptId = item.department?.id || aiDefaults?.default_department?.id;
              const classId = item.class?.id || aiDefaults?.default_class?.id;

              if (locId) newLocations[lineNum] = locId;
              if (deptId) newDepartments[lineNum] = deptId;
              if (classId) newClasses[lineNum] = classId;
            }
          });

          if (Object.keys(newLocations).length > 0) {
            setLineItemLocations(prev => ({ ...prev, ...newLocations }));
            console.log('📍 Synced line item locations from AI:', newLocations);
          }
          if (Object.keys(newDepartments).length > 0) {
            setLineItemDepartments(prev => ({ ...prev, ...newDepartments }));
            console.log('🏛️ Synced line item departments from AI:', newDepartments);
          }
          if (Object.keys(newClasses).length > 0) {
            setLineItemClasses(prev => ({ ...prev, ...newClasses }));
            console.log('🏷️ Synced line item classes from AI:', newClasses);
          }
        }

        // 🔄 Force re-render by incrementing key
        setRenderKey(prev => prev + 1);
        console.log('✅ Incremented renderKey to force UI update');

        // 🔍 DEBUG: Log what backend actually returned
        console.log('🔍 DEBUG - AI Response Data:', {
          invoice_number_in_response: data.modified_bill_data?.invoice_number,
          invoice_date_in_response: data.modified_bill_data?.invoice_date,
          line_items_count: data.modified_bill_data?.line_items?.length,
          has_expense: !!data.modified_bill_data?.expense,
          full_modified_data: data.modified_bill_data
        });
        console.log('🔍 DEBUG - What AI said:', data.ai_response);

        // Show changes made
        if (data.changes_made && Object.keys(data.changes_made).length > 0) {
          const changesMessage = {
            role: 'system',
            content: `✅ Changes applied: ${Object.keys(data.changes_made).join(', ')}`
          };
          setChatMessages(prev => [...prev, changesMessage]);
        }

        // If AI requests modal reload, trigger onModify callback to refresh the modal
        if (data.reload_modal && onModify) {
          console.log('🔄 AI requested modal reload - calling onModify with updated bill data');
          // Close chat and trigger modal reload with new data
          setChatOpen(false);
          onModify(data.modified_bill_data);
        }
      } else {
        const errorMessage = {
          role: 'system',
          content: `Error: ${data.error || 'Failed to modify bill data'}`
        };
        setChatMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('❌ Error sending chat message:', error);
      const errorMessage = {
        role: 'system',
        content: `Error: ${error.message || 'Failed to communicate with server'}`
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  // Handle creating bill with AI-modified data
  // ⚠️ DEPRECATED: This function is no longer used
  // The main handleConfirmBill function now handles both original and modified data
  // This is kept for reference only and should be removed in future cleanup
  const handleConfirmModifiedBill = async () => {
    console.warn('⚠️ DEPRECATED: handleConfirmModifiedBill called - this should use handleConfirmBill instead');

    // Just call the main handler which already uses getCurrentBillData() that includes modifications
    await handleConfirmBill();
  };

  console.log('📋 PreBillCreationModal received billData:', billData);
  console.log('🔧 PreBillCreationModal received token:', !!token);
  console.log('🔍 DEBUG - isPOLinked:', isPOLinked);
  console.log('🔍 DEBUG - po_data structure:', getPoData());
  console.log('🔍 DEBUG - po_data.vendor_name:', getPoData()?.vendor_name);
  console.log('🔍 DEBUG - po_data.po_number:', getPoData()?.po_number);
  console.log('🔍 DEBUG - po_data.line_items length:', getPoData()?.line_items?.length);

  if (!billData) {
    return null;
  }

  return (
    <div className={`fixed z-50 transition-all duration-300 ${
      isMinimized
        ? 'inset-auto bottom-0 right-0 p-0'  // No overlay when minimized
        : 'inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4'  // Full overlay when maximized
    }`}>
      <div
        key={renderKey}
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl transition-all duration-300 ${
        isMinimized
          ? 'fixed bottom-4 right-4 w-96 max-w-[90vw] shadow-2xl'
          : 'max-w-7xl w-full max-h-[90vh] overflow-y-auto'
      }`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              📋 Review Bill Before Creation
            </h2>
            {!isMinimized && (
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Please review and confirm all details before creating the NetSuite bill
              </p>
            )}
            {!isMinimized && getCurrentBillData()?.partial_ir && getCurrentBillData()?.partial_ir_remark && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200">
                <span className="text-lg leading-none">⚠️</span>
                <div className="text-sm">
                  <span className="font-semibold">Partial receipt (IR): </span>
                  {getCurrentBillData().partial_ir_remark.replace(/^Partial receipt \(IR\):\s*/, '')}
                  <div className="text-xs mt-1 opacity-80">Only the received quantity will be billed — review before proceeding.</div>
                </div>
              </div>
            )}
          </div>

          {/* Header Buttons */}
          <div className="flex items-center space-x-2">
            {/* View PDF Button */}
            {hasUploadedPdf && onViewPdf && (
              <button
                onClick={onViewPdf}
                className="flex items-center space-x-2 px-3 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded-lg transition-colors"
                title="View Uploaded PDF"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">View PDF</span>
              </button>
            )}
            {/* Minimize/Maximize Button */}
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={isMinimized ? 'Maximize' : 'Minimize'}
            >
              {isMinimized ? (
                // Maximize icon
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              ) : (
                // Minimize icon
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              {/* X/Close icon */}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Main Content - Hidden when minimized */}
        {!isMinimized && (
          <>
        <div className="p-6 space-y-6">
          {/* Vendor Information */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              🏢 Vendor Information {isPOLinked && <span className="text-sm bg-green-100 dark:bg-green-800 px-2 py-1 rounded">PO-Linked</span>}
            </h3>

            {isPOLinked ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">Company:</span>
                  <p className="text-gray-900 dark:text-white">{getPoData()?.vendor_name}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Entity ID:</span>
                  <p className="text-gray-900 dark:text-white">{selectedVendor}</p>
                </div>
              </div>
            ) : (currentBillData?.vendor?.matched === false || (currentBillData?.available_vendors && currentBillData.available_vendors.length > 0)) ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {(currentBillData?.available_vendors?.length || 0) > 1 ? 'Select Vendor:' : currentBillData?.vendor?.matched === false ? 'Vendor Not Found - Select Manually:' : 'Confirm Vendor:'}
                  {currentBillData?.vendor?.matched === false && (currentBillData?.available_vendors?.length || 0) > 1 && (
                    <span className="ml-2 text-yellow-600 dark:text-yellow-400 text-sm">⚠️ Multiple matches found - please verify</span>
                  )}
                </label>

                {/* Search Input and Load All Vendors Button */}
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="🔍 Search vendors..."
                    value={vendorSearchTerm}
                    onChange={(e) => setVendorSearchTerm(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={!allVendors.length}
                  />
                  <button
                    onClick={handleLoadAllVendors}
                    disabled={loadingAllVendors || allVendors.length > 0}
                    className={`px-4 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                      allVendors.length > 0
                        ? 'bg-green-600 text-white cursor-not-allowed'
                        : loadingAllVendors
                        ? 'bg-gray-400 text-white cursor-wait'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                    title={allVendors.length > 0 ? 'All vendors loaded' : 'Load all NetSuite vendors'}
                  >
                    {loadingAllVendors ? (
                      <>⏳ Loading...</>
                    ) : allVendors.length > 0 ? (
                      <>✅ All Loaded</>
                    ) : (
                      <>👥 Load All</>
                    )}
                  </button>
                </div>

                <select
                  value={selectedVendor}
                  onChange={(e) => {
                    // 💱 Currency matching is handled by useEffect that watches selectedVendor
                    setSelectedVendor(e.target.value);
                  }}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {(() => {
                    // Use all loaded vendors if available, otherwise use available_vendors
                    const vendorsSource = allVendors.length > 0 ? allVendors : (currentBillData?.available_vendors || []);
                    const searchTerm = vendorSearchTerm.toLowerCase();

                    const filteredVendors = vendorsSource
                      .filter(vendor => vendor !== null && vendor !== undefined)
                      .filter(vendor => {
                        if (!searchTerm) return true;
                        const companyName = (vendor?.companyname || vendor?.companyName || '').toLowerCase();
                        const entityId = (vendor?.entityid || vendor?.entityId || vendor?.entity_id || '').toLowerCase();
                        return companyName.includes(searchTerm) || entityId.includes(searchTerm);
                      });

                    console.log('🔍 Vendor Rendering Debug:', {
                      allVendorsCount: allVendors.length,
                      vendorsSourceCount: vendorsSource.length,
                      filteredVendorsCount: filteredVendors.length,
                      searchTerm,
                      firstVendor: filteredVendors[0]
                    });

                    const displayText = allVendors.length > 0
                      ? `-- Select from ${vendorsSource.length} vendors (All Vendors Loaded) --`
                      : `-- Select from ${vendorsSource.length} vendors (Limited) --`;

                    return (
                      <>
                        <option value="">{displayText}</option>
                        {filteredVendors.map(vendor => (
                          <option
                            key={vendor?.id || vendor?.vendor_id}
                            value={vendor?.id || vendor?.vendor_id}
                          >
                            {vendor?.companyname || vendor?.companyName} ({vendor?.entityid || vendor?.entityId || vendor?.entity_id}) {vendor?.currencysymbol || vendor?.currencySymbol ? `- ${vendor.currencysymbol || vendor.currencySymbol}` : ''}
                          </option>
                        ))}
                      </>
                    );
                  })()}
                </select>
                {allVendors.length > 0 && vendorSearchTerm && (
                  <p className="mt-1 text-xs text-gray-500">
                    💡 Tip: Clear search to see all {allVendors.length} vendors
                  </p>
                )}
                {currentBillData?.vendor?.vendor_name_from_pdf && (
                  <p className="mt-2 text-sm text-gray-500">
                    Original vendor from PDF: <span className="font-medium">{currentBillData.vendor.vendor_name_from_pdf}</span>
                  </p>
                )}
                {currentBillData?.vendor?.matched === false && (currentBillData?.available_vendors?.length || 0) === 0 && allVendors.length === 0 && (
                  <p className="mt-2 text-sm text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
                    ⚠️ No vendor matches found in NetSuite. Click <strong>"👥 Load All"</strong> button above to load all vendors and select manually.
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">Company:</span>
                  <p className="text-gray-900 dark:text-white">
                    {currentBillData?.vendor?.companyname ||
                     currentBillData?.vendor?.netsuite_vendor_name ||
                     currentBillData?.vendor?.vendor_name ||
                     'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Entity ID:</span>
                  <p className="text-gray-900 dark:text-white">
                    {currentBillData?.vendor?.entity_id ||
                     currentBillData?.vendor?.netsuite_entity_id ||
                     (currentBillData?.vendor?.netsuite_vendor_id ? `VEN${currentBillData.vendor.netsuite_vendor_id}` : null) ||
                     (currentBillData?.vendor?.id ? `VEN${currentBillData.vendor.id}` : null) ||
                     'N/A'}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <span className="text-sm font-medium text-gray-500">{isPOLinked ? 'PO Number:' : 'Invoice Number:'}</span>
                <p className="text-gray-900 dark:text-white">
                  {(() => {
                    const value = isPOLinked ? getPoData()?.po_number : currentBillData?.invoice_number;
                    console.log('🔍 RENDERING invoice_number:', value, 'from currentBillData:', currentBillData?.invoice_number);
                    return value;
                  })()}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">{isPOLinked ? 'PO Date:' : 'Invoice Date:'}</span>
                <p className="text-gray-900 dark:text-white">
                  {(() => {
                    const value = isPOLinked ? (getPoData()?.po_date || 'N/A') : (currentBillData?.invoice_date || currentBillData?.bill_date || 'N/A');
                    console.log('🔍 RENDERING invoice_date:', value);
                    return value;
                  })()}
                </p>
              </div>
            </div>
            {isPOLinked && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">PO Internal ID:</span>
                  <p className="text-gray-900 dark:text-white">{getPoData()?.po_internal_id}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Vendor Name:</span>
                  <p className="text-gray-900 dark:text-white">{getPoData()?.vendor_name}</p>
                </div>
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              📦 Line Items ({(isPOLinked ? getPoData()?.line_items : currentBillData?.line_items)?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)).length || 0})
            </h3>

            <div className="space-y-4">
              {(isPOLinked ? getPoData()?.line_items : currentBillData?.line_items)?.filter(item => item !== null && item !== undefined).map((item, index) => {
                // Get tax code for this line item
                // For PO items: tax_code_name, tax_code_id
                // For PDF items: tax_rate, tax_label
                let taxCodeDisplay = 'NA';

                // 🔧 PRIORITY 1: Check for PO tax code (for PO-linked bills)
                if (isPOLinked && item?.tax_code_name) {
                  taxCodeDisplay = item.tax_code_name;
                  console.log(`✅ PO Line ${item?.line_number}: Using PO tax code: ${taxCodeDisplay}`);
                }
                // PRIORITY 2: Check for PDF extracted tax (for standalone bills)
                else if (item?.tax_rate !== null && item?.tax_rate !== undefined) {
                  const itemTaxRate = item.tax_rate;
                  const itemTaxLabel = item.tax_label;

                  // If we have tax_label from AI extraction, use it
                  if (itemTaxLabel) {
                    taxCodeDisplay = `${itemTaxLabel} (${itemTaxRate}%)`;
                  }
                  // Otherwise, try to match with global tax code
                  else if (workflowContext?.tax_code_identification_result?.tax_code_data) {
                    const globalTaxCode = workflowContext.tax_code_identification_result.tax_code_data;

                    // Check if this item's tax rate matches the global tax code
                    if (parseFloat(itemTaxRate) === parseFloat(globalTaxCode.tax_rate * 100)) {
                      taxCodeDisplay = `${globalTaxCode.tax_code_name} (${itemTaxRate}%)`;
                    } else if (itemTaxRate === 0) {
                      taxCodeDisplay = 'Zero Rated (0%)';
                    } else {
                      taxCodeDisplay = `${itemTaxRate}%`;
                    }
                  }
                  // Last resort: just show percentage
                  else {
                    taxCodeDisplay = `${itemTaxRate}%`;
                  }
                }

                const isRemoved = removedLineItems.has(item?.line_number);

                return (
                <div key={index} className={`border rounded-lg p-4 relative ${
                  isRemoved
                    ? 'border-red-300 bg-red-50 dark:bg-red-900/20 opacity-60'
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'
                }`}>
                  {/* Remove/Restore Button */}
                  <button
                    onClick={() => isRemoved ? handleRestoreLineItem(item?.line_number) : handleRemoveLineItem(item?.line_number)}
                    className={`absolute top-2 right-2 px-3 py-1 rounded text-sm font-medium transition-colors ${
                      isRemoved
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : 'bg-red-500 hover:bg-red-600 text-white'
                    }`}
                    title={isRemoved ? 'Restore this item' : 'Remove this item from bill'}
                  >
                    {isRemoved ? '↺ Restore' : '✕ Remove'}
                  </button>

                  {isRemoved && (
                    <div className="mb-2 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 px-3 py-2 rounded text-sm font-medium">
                      🚫 This item will be excluded from the bill
                    </div>
                  )}

                  <div className="grid grid-cols-8 gap-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Item Name/SKU:</span>
                      <p className="text-gray-900 dark:text-white text-sm font-medium">
                        {/* ✅ SAFE: Check matched NetSuite item first (AI-updated), handle array/object, then fall back to PDF */}
                        {(() => {
                          // Safely handle if matched_netsuite_item is an array or object
                          const matchedItem = (item?.matched_netsuite_item && typeof item.matched_netsuite_item === 'object')
                            ? (Array.isArray(item.matched_netsuite_item) ? item.matched_netsuite_item[0] : item.matched_netsuite_item)
                            : null;

                          return matchedItem?.item_name_code ||
                                 matchedItem?.netsuite_item_name_code ||
                                 matchedItem?.item_display_name ||
                                 item?.item_search?.extracted_sku_or_code ||
                                 item?.description ||
                                 item?.item_name ||
                                 'N/A';
                        })()}
                      </p>
                      {/* SAFE: Show description from matched item or PDF extraction */}
                      {(() => {
                        // Safely handle if matched_netsuite_item is an array or object
                        const matchedItem = (item?.matched_netsuite_item && typeof item.matched_netsuite_item === 'object')
                          ? (Array.isArray(item.matched_netsuite_item) ? item.matched_netsuite_item[0] : item.matched_netsuite_item)
                          : null;

                        const desc = matchedItem?.item_description || item?.description;

                        return desc ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" title={desc}>
                            {desc.substring(0, 60)}{desc.length > 60 ? '...' : ''}
                          </p>
                        ) : null;
                      })()}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Quantity:</span>
                      {isPOLinked ? (() => {
                        const k = qtyKeyFor(item);
                        const openQty = parseFloat(item?.open_for_billing_quantity);
                        // ⚠️ CEILING FOR THE OVER-QTY WARNING. `open_for_billing_quantity`
                        // is the accurate limit, but NetSuite returns it null when the PO
                        // has no item receipts (common on the sandbox account after the
                        // 2026-07-31 switch — e.g. PO01005660). Without a fallback the
                        // warning silently never fires, so entering 12 against 1 ordered
                        // passed unflagged. Fall back to quantity_ordered, which is present
                        // in that case, and say which basis was used. When the open qty IS
                        // available nothing changes — prod behaviour is untouched.
                        const orderedQty = parseFloat(item?.quantity_ordered);
                        const hasOpenQty = Number.isFinite(openQty);
                        const capQty = hasOpenQty
                          ? openQty
                          : (Number.isFinite(orderedQty) ? orderedQty : NaN);
                        const shown = (k !== null && editedQuantities[k] !== undefined)
                          ? editedQuantities[k]
                          : String(effectiveQtyFor(item));
                        const entered = parseFloat(shown);
                        const overOpen = Number.isFinite(entered) && Number.isFinite(capQty) && entered > capQty;
                        const invalid = String(shown).trim() !== '' && (!Number.isFinite(entered) || entered <= 0);
                        return (
                          <>
                            <input
                              type="number" min="0" step="any" value={shown} disabled={isRemoved || k === null}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEditedQuantities(prev => {
                                  const next = { ...prev, [k]: v };
                                  editedQuantitiesRef.current = next;
                                  return next;
                                });
                              }}
                              title="Quantity to bill — prefilled from the PDF invoice"
                              className={`mt-1 w-full px-2 py-1 text-sm rounded border dark:bg-gray-800 dark:text-white focus:ring-2 ${
                                invalid || overOpen
                                  ? 'border-amber-400 focus:ring-amber-500'
                                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'}`}
                            />
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                              PDF: {pdfQtyFor(item) ?? '—'} · Open: {Number.isFinite(openQty) ? openQty : '—'}
                              {item?.quantity_ordered != null ? ` of ${item.quantity_ordered} ordered` : ''}
                            </p>
                            {overOpen && (
                              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                                ⚠️ Exceeds {hasOpenQty ? 'open' : 'ordered'} qty ({capQty}) — NetSuite may reject this line.
                              </p>
                            )}
                            {invalid && (
                              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                                ⚠️ Enter a quantity greater than 0.
                              </p>
                            )}
                          </>
                        );
                      })() : (
                        <p className="text-gray-900 dark:text-white">
                          {item?.quantity || item?.open_for_billing_quantity}
                        </p>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Unit Price:</span>
                      {isPOLinked ? (() => {
                        // ✏️ RATE EDIT (PO-linked). Prefilled from the PDF invoice — the
                        // vendor's actual price — falling back to the PO rate when the PDF
                        // has none. Editable, same keying as the quantity input.
                        const k = qtyKeyFor(item);
                        const shown = (k !== null && editedRates[k] !== undefined)
                          ? editedRates[k]
                          : String(effectiveRateFor(item));
                        const entered = parseFloat(shown);
                        const invalid = String(shown).trim() !== '' && (!Number.isFinite(entered) || entered < 0);
                        const poRate = parseFloat(item?.rate);
                        const pdfRate = pdfRateFor(item);
                        const differs = pdfRate !== null && Number.isFinite(poRate)
                          && Math.abs(pdfRate - poRate) > 0.005;
                        return (
                          <>
                            <input
                              type="number" min="0" step="any" value={shown} disabled={isRemoved || k === null}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEditedRates(prev => {
                                  const next = { ...prev, [k]: v };
                                  editedRatesRef.current = next;
                                  return next;
                                });
                              }}
                              title="Unit price to bill — prefilled from the PDF invoice"
                              className={`mt-1 w-full px-2 py-1 text-sm rounded border dark:bg-gray-800 dark:text-white focus:ring-2 ${
                                invalid
                                  ? 'border-amber-400 focus:ring-amber-500'
                                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'}`}
                            />
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                              PDF: {pdfRate !== null ? `${getCurrencySymbol()}${pdfRate}` : '—'}
                              {Number.isFinite(poRate) ? ` · PO: ${getCurrencySymbol()}${poRate}` : ''}
                            </p>
                            {differs && (
                              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                                ⚠️ PDF price differs from the PO price.
                              </p>
                            )}
                            {invalid && (
                              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                                ⚠️ Enter a unit price of 0 or more.
                              </p>
                            )}
                          </>
                        );
                      })() : (
                        <p className="text-gray-900 dark:text-white">
                          {getCurrencySymbol()}{item?.unit_price || item?.rate || '0.00'}
                        </p>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Total Amount:</span>
                      <p className="text-gray-900 dark:text-white">
                        {(() => {
                          // PO-linked: always qty × rate, using the effective (edited or
                          // PDF-derived) values, so the figure shown always matches the
                          // quantity and unit price in the two fields above.
                          if (isPOLinked) {
                            const amt = effectiveAmountFor(item);
                            if (amt !== null) {
                              return `${getCurrencySymbol()}${amt.toFixed(2)}`;
                            }
                          }
                          return `${getCurrencySymbol()}${isPOLinked ? (item?.total_amount || item?.extended_price || item?.amount || '0.00') : (item?.extended_price || item?.amount || item?.total_amount || '0.00')}`;
                        })()}
                      </p>
                    </div>
                    {/* 💸 Discount Rate Column - Only show if item has discount */}
                    <div>
                      <span className="text-sm font-medium text-gray-500">Discount %:</span>
                      <p className="text-gray-900 dark:text-white">
                        {item?.discount_rate !== null && item?.discount_rate !== undefined
                          ? `${item.discount_rate}%`
                          : <span className="text-gray-400">-</span>
                        }
                      </p>
                    </div>
                    {/* 💸 Discount Amount Column - Only show if item has discount */}
                    <div>
                      <span className="text-sm font-medium text-gray-500">Discount Amt:</span>
                      <p className="text-gray-900 dark:text-white">
                        {item?.discount_amount !== null && item?.discount_amount !== undefined
                          ? `${getCurrencySymbol()}${item.discount_amount}`
                          : <span className="text-gray-400">-</span>
                        }
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">PDF Tax:</span>
                      <p className={`text-sm font-medium ${taxCodeDisplay === 'NA' ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                        {taxCodeDisplay}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">From PDF</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">NetSuite Tax Code:</span>
                      {/* Always show dropdown like expense items */}
                      <select
                        value={lineItemTaxCodes[item?.line_number] || item?.tax_code_id || ''}
                        onChange={(e) => handleLineItemTaxCodeChange(item?.line_number, e.target.value)}
                        className="w-full p-2 mt-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                        title="Select NetSuite tax code for this line item"
                      >
                        <option value="">-- No Tax Code --</option>
                        {allTaxCodes.length === 0 ? (
                          <option disabled>Loading tax codes...</option>
                        ) : (
                          (() => {
                            const options = [];

                            // Check if current line item's tax code is in the loaded list (match expenses logic)
                            const currentTaxExists = item?.tax_code_id &&
                              allTaxCodes.some(tc => String(tc.id) === String(item.tax_code_id));

                            // If tax code is set but not in list, check all_netsuite_tax_codes and add it
                            if (item?.tax_code_id && !currentTaxExists) {
                              const allTaxCodesFromBill = currentBillData?.all_netsuite_tax_codes;

                              if (allTaxCodesFromBill && Array.isArray(allTaxCodesFromBill)) {
                                const matchedTaxCode = allTaxCodesFromBill.find(tc => String(tc.id) === String(item.tax_code_id));
                                if (matchedTaxCode) {
                                  // Add the actual tax code from all_netsuite_tax_codes
                                  const itemId = matchedTaxCode.itemid || matchedTaxCode.name || `Tax Code ${item.tax_code_id}`;
                                  const description = matchedTaxCode.description || matchedTaxCode.itemid;
                                  const rate = matchedTaxCode.rate ? (parseFloat(matchedTaxCode.rate) * 100).toFixed(2) : parseFloat(item.tax_rate).toFixed(2);
                                  options.push(
                                    <option key={item.tax_code_id} value={item.tax_code_id}>
                                      {itemId} - {description} ({rate}%)
                                    </option>
                                  );
                                }
                              }
                            }

                            // Add all loaded tax codes
                            options.push(...allTaxCodes.map(taxCode => (
                              <option key={taxCode.id} value={taxCode.id}>
                                {taxCode.itemid} - {taxCode.description || taxCode.itemid} ({(parseFloat(taxCode.rate) * 100).toFixed(2)}%)
                              </option>
                            )));

                            return options;
                          })()
                        )}
                      </select>
                      {/* Show selected tax code confirmation - SIMPLE like expenses */}
                      {item?.tax_code_id && item?.tax_rate && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          ✓ Tax: {item.tax_rate}%
                        </p>
                      )}
                    </div>
                  </div>

                  {/* NetSuite Item Selection */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      NetSuite Item:
                    </label>

                    {isPOLinked ? (
                      <div className="bg-green-100 dark:bg-green-800 p-3 rounded border">
                        <p className="font-medium text-green-900 dark:text-green-100">
                          ✅ PO Item: {item?.item_name}
                        </p>
                        <p className="text-sm text-green-700 dark:text-green-200">
                          Rate: ${item?.rate} | Available: {item?.open_for_billing_quantity} | IR Date: {item?.latest_ir_date || 'Not received'}
                        </p>
                      </div>
                    ) : (
                    item?.available_items && item.available_items.length >= 1 ? (
                      <div className="space-y-2">
                        {/* Search Input and Load All Button */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="🔍 Search items..."
                            value={itemSearchTerms[item?.line_number] || ''}
                            onChange={(e) => setItemSearchTerms(prev => ({
                              ...prev,
                              [item?.line_number]: e.target.value
                            }))}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <button
                            onClick={() => handleLoadAllItemsForLine(item?.line_number)}
                            disabled={loadingLineItems[item?.line_number] || lineItemsAllItems[item?.line_number]}
                            className={`px-4 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                              lineItemsAllItems[item?.line_number]
                                ? 'bg-green-600 text-white cursor-not-allowed'
                                : loadingLineItems[item?.line_number]
                                ? 'bg-gray-400 text-white cursor-wait'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                            title={lineItemsAllItems[item?.line_number] ? 'All items loaded' : 'Load all NetSuite items'}
                          >
                            {loadingLineItems[item?.line_number] ? (
                              <>⏳ Loading...</>
                            ) : lineItemsAllItems[item?.line_number] ? (
                              <>✅ All Loaded</>
                            ) : (
                              <>📦 Load All</>
                            )}
                          </button>
                        </div>

                        <select
                          value={selectedItems[item?.line_number] || ''}
                          onChange={(e) => setSelectedItems(prev => ({
                            ...prev,
                            [item?.line_number]: e.target.value
                          }))}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                        >
                          {(() => {
                            const searchTerm = (itemSearchTerms[item?.line_number] || '').toLowerCase();
                            // Ensure itemsSource is always an array to prevent "filter is not a function" errors
                            const itemsSource = (Array.isArray(lineItemsAllItems[item?.line_number])
                              ? lineItemsAllItems[item?.line_number]
                              : Array.isArray(item.available_items)
                                ? item.available_items
                                : []);
                            const isAllLoaded = !!lineItemsAllItems[item?.line_number];

                            const filteredItems = itemsSource
                              .filter(nsItem => {
                                if (!nsItem) return false;
                                if (!searchTerm) return true;
                                const description = (nsItem?.item_description || nsItem?.description || '').toLowerCase();
                                const code = (nsItem?.item_name_code || nsItem?.name || '').toLowerCase();
                                return description.includes(searchTerm) || code.includes(searchTerm);
                              });

                            const displayText = isAllLoaded
                              ? `-- Select from ${itemsSource.length} items (All Items Loaded) --`
                              : `-- Select from ${itemsSource.length} items (Limited) --`;

                            return (
                              <>
                                <option value="">{displayText}</option>
                                {filteredItems.map(nsItem => (
                                  <option key={nsItem?.netsuite_item_id} value={nsItem?.netsuite_item_id}>
                                    {nsItem?.item_name_code} - {nsItem?.item_description ? `${nsItem.item_description.substring(0, 60)}${nsItem.item_description.length > 60 ? '...' : ''}` : 'No description'}{nsItem?.item_type ? ` [${nsItem.item_type}]` : ''}
                                  </option>
                                ))}
                              </>
                            );
                          })()}
                          <option value="create_new">➕ Create as New Item</option>
                        </select>

                        {(() => {
                          // Ensure itemsSource is always an array to prevent errors
                          const itemsSource = (Array.isArray(lineItemsAllItems[item?.line_number])
                            ? lineItemsAllItems[item?.line_number]
                            : Array.isArray(item.available_items)
                              ? item.available_items
                              : []);
                          const isAllLoaded = !!lineItemsAllItems[item?.line_number];
                          return (
                            <p className={`text-xs ${isAllLoaded ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                              {isAllLoaded ? (
                                <>✅ All {itemsSource.length} items loaded</>
                              ) : (
                                <>Showing {itemsSource.length} limited items - click "Load All" for complete list</>
                              )}
                            </p>
                          );
                        })()}
                      </div>
                    ) : (item?.matched_netsuite_item || selectedItems[item?.line_number]) ? (
                      <div className="bg-green-100 dark:bg-green-800 p-3 rounded border">
                        {/* Show PDF item name first, then matched/selected NetSuite item */}
                        {(item?.item_search?.extracted_sku_or_code || item?.description || item?.item_name) && (
                          <p className="text-xs text-green-700 dark:text-green-300 mb-1">
                            📄 PDF Item: {item?.item_search?.extracted_sku_or_code || item?.description || item?.item_name}
                          </p>
                        )}
                        {(() => {
                          // Find selected item details from available items or loaded items
                          const selectedItemId = selectedItems[item?.line_number];
                          const allItemsSource = lineItemsAllItems[item?.line_number] || item?.available_items || [];
                          const selectedItemDetails = selectedItemId
                            ? allItemsSource.find(i => String(i.netsuite_item_id) === String(selectedItemId) || String(i.id) === String(selectedItemId))
                            : null;

                          // Use selected item details if available, otherwise use AI-matched item
                          const displayItem = selectedItemDetails || item?.matched_netsuite_item;

                          return (
                            <>
                              <p className="font-medium text-green-900 dark:text-green-100">
                                ✅ {selectedItemDetails ? 'Selected' : 'Matched'}: {
                                  displayItem?.item_description ||
                                  displayItem?.netsuite_item_description ||
                                  displayItem?.item_display_name ||
                                  displayItem?.netsuite_item_display_name ||
                                  displayItem?.item_name_code ||
                                  displayItem?.netsuite_item_name_code ||
                                  (displayItem?.netsuite_item_id ? `Item ID: ${displayItem.netsuite_item_id}` : `Item ID: ${selectedItemId}`)
                                }
                              </p>
                              <p className="text-sm text-green-700 dark:text-green-200">
                                Code: {displayItem?.item_name_code || displayItem?.netsuite_item_name_code || selectedItemId || 'N/A'}
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="bg-yellow-100 dark:bg-yellow-800 p-2 rounded border border-yellow-300 dark:border-yellow-600">
                          <p className="text-xs font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                            ⚠️ No Match Found - Item Name/SKU:
                          </p>
                          <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                            {item?.item_search?.extracted_sku_or_code || item?.description || item?.item_name}
                          </p>
                          {item?.item_search?.extracted_sku_or_code && item?.description && (
                            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1" title={item.description}>
                              {item.description.substring(0, 80)}{item.description.length > 80 ? '...' : ''}
                            </p>
                          )}
                          {item?.quantity && (
                            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                              Qty: {item.quantity} | Amount: ${item?.amount || 0}
                            </p>
                          )}
                        </div>

                        {/* Search Input and Load All Button */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="🔍 Search NetSuite items by description, name or code..."
                            value={itemSearchTerms[item?.line_number] || ''}
                            onChange={(e) => setItemSearchTerms(prev => ({
                              ...prev,
                              [item?.line_number]: e.target.value
                            }))}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <button
                            onClick={() => handleLoadAllItemsForLine(item?.line_number)}
                            disabled={loadingLineItems[item?.line_number] || lineItemsAllItems[item?.line_number]}
                            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                              lineItemsAllItems[item?.line_number]
                                ? 'bg-green-600 text-white cursor-not-allowed'
                                : loadingLineItems[item?.line_number]
                                ? 'bg-gray-400 text-white cursor-wait'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                            title={lineItemsAllItems[item?.line_number] ? 'All items loaded' : 'Load all NetSuite items'}
                          >
                            {loadingLineItems[item?.line_number] ? (
                              <>⏳ Loading...</>
                            ) : lineItemsAllItems[item?.line_number] ? (
                              <>✅ All Loaded</>
                            ) : (
                              <>📦 Load All</>
                            )}
                          </button>
                        </div>
                        {itemSearchTerms[item?.line_number] && (
                          <button
                            onClick={() => setItemSearchTerms(prev => ({
                              ...prev,
                              [item?.line_number]: ''
                            }))}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Clear search
                          </button>
                        )}

                        <select
                          value={selectedItems[item?.line_number] || ''}
                          onChange={(e) => setSelectedItems(prev => ({
                            ...prev,
                            [item?.line_number]: e.target.value
                          }))}
                          className="w-full p-2 border border-yellow-300 dark:border-yellow-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm max-h-60 overflow-y-auto"
                        >
                          {(() => {
                            const searchTerm = (itemSearchTerms[item?.line_number] || '').toLowerCase();

                            // Use line-specific loaded items (from Load All button)
                            const itemsSource = lineItemsAllItems[item?.line_number] || [];
                            const isAllLoaded = !!lineItemsAllItems[item?.line_number];

                            // Filter by search term
                            const filteredItems = itemsSource
                              .filter(nsItem => {
                                if (!nsItem) return false;
                                if (!searchTerm) return true; // Show all if no search

                                const description = (nsItem?.item_description || nsItem?.description || '').toLowerCase();
                                const code = (nsItem?.item_name_code || nsItem?.name || '').toLowerCase();

                                return description.includes(searchTerm) || code.includes(searchTerm);
                              })
                              .slice(0, 10000); // Limit to 10000 for performance

                            const showingCount = filteredItems.length;
                            const totalCount = itemsSource.length;

                            const displayText = isAllLoaded
                              ? (searchTerm
                                  ? `-- Found ${showingCount} of ${totalCount} items (All Items Loaded) --`
                                  : `-- Select from ${totalCount} items (All Items Loaded) --`)
                              : `-- Click "Load All" button to load items --`;

                            return (
                              <>
                                <option value="">{displayText}</option>
                                {filteredItems.map(nsItem => {
                                  const description = nsItem?.item_description || nsItem?.description || 'No description';
                                  const code = nsItem?.item_name_code || nsItem?.name || '';
                                  const type = nsItem?.item_type ? ` [${nsItem.item_type}]` : '';

                                  return (
                                    <option key={nsItem?.netsuite_item_id || nsItem?.id} value={nsItem?.netsuite_item_id || nsItem?.id}>
                                      {code} - {description.substring(0, 70)}{description.length > 70 ? '...' : ''}{type}
                                    </option>
                                  );
                                })}
                              </>
                            );
                          })()}
                          <option value="create_new">➕ Create as New Item</option>
                        </select>

                        {(() => {
                          const itemsSource = lineItemsAllItems[item?.line_number] || [];
                          const isAllLoaded = !!lineItemsAllItems[item?.line_number];
                          return (
                            <p className={`text-xs mt-1 ${isAllLoaded ? 'text-green-600 dark:text-green-400 font-medium' : 'text-yellow-700 dark:text-yellow-300'}`}>
                              {isAllLoaded ? (
                                <>✅ All {itemsSource.length} items loaded - search or scroll to find your item</>
                              ) : (
                                <>⚠️ No items loaded - click "📦 Load All" button above to load all items</>
                              )}
                            </p>
                          );
                        })()}
                      </div>
                    ))}
                  </div>

                  {/* Location / Department / Class Selection for Line Items */}
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {/* Location */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          📍 Location
                        </label>
                        <button
                          type="button"
                          onClick={handleLoadAllLocations}
                          disabled={loadingLocations}
                          className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800/40 disabled:opacity-50"
                        >
                          {loadingLocations ? '⏳' : '🔄'} {allLocations.length > 0 ? `(${allLocations.length})` : 'Load'}
                        </button>
                      </div>
                      {/* 🔧 FIX: Use bill_data.all_locations (subsidiary-filtered) when available */}
                      <select
                        value={String(lineItemLocations[item?.line_number] || item?.location_id || item?.location?.id || '')}
                        onChange={(e) => handleLineItemLocationChange(item?.line_number, e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="">-- Select Location --</option>
                        {/* 🔧 FIX: Check array length, not just existence (empty array [] is truthy!) */}
                        {(getCurrentBillData()?.all_locations?.length ? getCurrentBillData()?.all_locations : getCurrentBillData()?.all_netsuite_locations?.length ? getCurrentBillData()?.all_netsuite_locations : allLocations).map(loc => (
                          <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Department */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          🏛️ Department
                        </label>
                        <button
                          type="button"
                          onClick={handleLoadAllDepartments}
                          disabled={loadingDepartments}
                          className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800/40 disabled:opacity-50"
                        >
                          {loadingDepartments ? '⏳' : '🔄'} {allDepartments.length > 0 ? `(${allDepartments.length})` : 'Load'}
                        </button>
                      </div>
                      {/* 🔧 FIX: Use bill_data.all_departments (subsidiary-filtered) when available */}
                      <select
                        value={String(lineItemDepartments[item?.line_number] || item?.department_id || item?.department?.id || '')}
                        onChange={(e) => handleLineItemDepartmentChange(item?.line_number, e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="">-- Select Department --</option>
                        {/* 🔧 FIX: Check array length, not just existence (empty array [] is truthy!) */}
                        {(getCurrentBillData()?.all_departments?.length ? getCurrentBillData()?.all_departments : getCurrentBillData()?.all_netsuite_departments?.length ? getCurrentBillData()?.all_netsuite_departments : allDepartments).map(dept => (
                          <option key={dept.id} value={String(dept.id)}>{dept.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Class */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          🏷️ Class
                        </label>
                        <button
                          type="button"
                          onClick={handleLoadAllClasses}
                          disabled={loadingClasses}
                          className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800/40 disabled:opacity-50"
                        >
                          {loadingClasses ? '⏳' : '🔄'} {allClasses.length > 0 ? `(${allClasses.length})` : 'Load'}
                        </button>
                      </div>
                      {/* 🔧 FIX: Use bill_data.all_classes (subsidiary-filtered) when available */}
                      <select
                        value={String(lineItemClasses[item?.line_number] || item?.class_id || item?.class?.id || '')}
                        onChange={(e) => handleLineItemClassChange(item?.line_number, e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="">-- Select Class --</option>
                        {/* 🔧 FIX: Check array length, not just existence (empty array [] is truthy!) */}
                        {(getCurrentBillData()?.all_classes?.length ? getCurrentBillData()?.all_classes : getCurrentBillData()?.all_netsuite_classes?.length ? getCurrentBillData()?.all_netsuite_classes : allClasses).map(cls => (
                          <option key={cls.id} value={String(cls.id)}>{cls.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          </div>

          {/* Expense Items Section */}
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  💰 Expense Items ({expenses.length})
                </h3>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="🔍 Search expense accounts..."
                    value={expenseAccountSearch}
                    onChange={(e) => setExpenseAccountSearch(e.target.value)}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-64"
                  />
                  <button
                    onClick={handleAddExpense}
                    className="px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm"
                  >
                    + Add Expense
                  </button>
                </div>
              </div>

              {expenses.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No expense items (e.g., Freight, Handling fees)
                </p>
              ) : (
                <>
                  {/* Summary Info */}
                  {expenses.some(e => e.source === 'pdf_detected') && (
                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400">ℹ️</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            {expenses.filter(e => e.source === 'pdf_detected').length} charge(s) detected from PDF
                            {(() => {
                              const highConf = expenses.filter(e => e.source === 'pdf_detected' && e.confidence === 'high').length;
                              const mediumConf = expenses.filter(e => e.source === 'pdf_detected' && e.confidence === 'medium').length;
                              if (highConf > 0 && mediumConf > 0) {
                                return ` (${highConf} likely expense${highConf > 1 ? 's' : ''}, ${mediumConf} needs review)`;
                              } else if (mediumConf > 0) {
                                return ` (needs review)`;
                              }
                              return '';
                            })()}
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            {isPOLinked
                              ? 'These charges were found in the PDF but are not part of the PO line items.'
                              : 'These charges were detected from the PDF based on keywords like freight, shipping, etc.'
                            }
                            <strong className="ml-1">Review each one carefully</strong> - they might be expenses (freight, fees, services) or billing errors.
                            You can keep, modify, or remove any of them before creating the bill.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                  {expenses.map((expense, index) => (
                    <div key={expense.id} className={`border rounded-lg p-4 ${
                      expense.source === 'pdf_detected'
                        ? expense.confidence === 'high'
                          ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
                        : expense.source === 'ai_added' || expense.added_by_ai
                          ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                          : 'border-orange-200 dark:border-orange-600 bg-white dark:bg-gray-700'
                    }`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Expense #{index + 1}
                          </span>
                          {expense.source === 'pdf_detected' && expense.confidence === 'high' && (
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 text-xs rounded border border-blue-300 dark:border-blue-600">
                              📄 Likely Expense
                            </span>
                          )}
                          {expense.source === 'pdf_detected' && expense.confidence === 'medium' && (
                            <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300 text-xs rounded border border-yellow-300 dark:border-yellow-600">
                              ⚠️ Needs Review
                            </span>
                          )}
                          {(expense.source === 'ai_added' || expense.added_by_ai) && (
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 text-xs rounded border border-green-300 dark:border-green-600">
                              🤖 Added by AI
                            </span>
                          )}
                          {expense.source === 'existing' && (
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 text-xs rounded border border-green-300 dark:border-green-600">
                              ✓ Confirmed
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveExpense(expense.id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                          title={expense.source === 'pdf_detected' ? "Remove this PDF-detected expense" : "Remove this expense"}
                        >
                          ✕ Remove
                        </button>
                      </div>
                      {expense.source === 'pdf_detected' && (
                        <div className={`mb-3 p-2 rounded text-xs ${
                          expense.confidence === 'high'
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        }`}>
                          {expense.confidence === 'high' ? (
                            <>💡 This looks like an expense (freight, fee, etc.) and is not in the PO. Review and confirm, or remove if not needed.</>
                          ) : (
                            <>⚠️ This charge from the PDF is not in the PO. It might be an expense or an error. Please verify before confirming.</>
                          )}
                        </div>
                      )}
                      {(expense.source === 'ai_added' || expense.added_by_ai) && (
                        <div className="mb-3 p-2 rounded text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          🤖 This expense was added by the AI assistant. Review the account and amount, then confirm or adjust if needed.
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        {/* Expense Account */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Expense Account *
                          </label>
                          {/* Search input for filtering expense accounts */}
                          <input
                            type="text"
                            placeholder="Search by account name or number..."
                            value={expenseAccountSearch}
                            onChange={(e) => setExpenseAccountSearch(e.target.value)}
                            className="w-full p-2 mb-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500"
                          />
                          <select
                            value={expense.account?.id || ''}
                            onChange={(e) => {
                              const selectedAccount = allExpenseAccounts.find(acc => acc.id === e.target.value);
                              handleExpenseChange(expense.id, 'account', { id: e.target.value });
                              if (selectedAccount) {
                                handleExpenseChange(expense.id, 'name', selectedAccount.accountsearchdisplaynamecopy);
                              }
                            }}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                          >
                            <option value="">-- Select Expense Account --</option>
                            {allExpenseAccounts.length === 0 ? (
                              <option disabled>Loading accounts...</option>
                            ) : (
                              (() => {
                                // Filter expense accounts based on search term
                                const filteredAccounts = expenseAccountSearch
                                  ? allExpenseAccounts.filter(account =>
                                      account.accountsearchdisplaynamecopy?.toLowerCase().includes(expenseAccountSearch.toLowerCase()) ||
                                      account.acctnumber?.toLowerCase().includes(expenseAccountSearch.toLowerCase()) ||
                                      account.description?.toLowerCase().includes(expenseAccountSearch.toLowerCase())
                                    )
                                  : allExpenseAccounts;

                                // Check if the current expense's account is in the list
                                const currentAccountExists = expense.account?.id &&
                                  allExpenseAccounts.some(acc => acc.id === expense.account.id);

                                // If account is set but not in the list, add it as a temporary option
                                const options = [];
                                if (expense.account?.id && !currentAccountExists && expense.name) {
                                  options.push(
                                    <option key={expense.account.id} value={expense.account.id}>
                                      {expense.name} (AI Selected)
                                    </option>
                                  );
                                }

                                // Add filtered accounts
                                if (filteredAccounts.length > 0) {
                                  options.push(...filteredAccounts.map(account => (
                                    <option key={account.id} value={account.id}>
                                      {account.accountsearchdisplaynamecopy} ({account.acctnumber}) [{account.accttype}]
                                    </option>
                                  )));
                                } else if (!expense.account?.id || !expense.name) {
                                  options.push(<option key="no-match" disabled>No accounts match "{expenseAccountSearch}"</option>);
                                }

                                return options;
                              })()
                            )}
                          </select>
                          {expense.name && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              ✓ {expense.name}
                            </p>
                          )}
                        </div>

                        {/* Amount */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Amount *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={expense.amount}
                            onChange={(e) => handleExpenseChange(expense.id, 'amount', e.target.value)}
                            placeholder="0.00"
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                          />
                        </div>

                        {/* Memo */}
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Memo / Description
                          </label>
                          <input
                            type="text"
                            value={expense.memo}
                            onChange={(e) => handleExpenseChange(expense.id, 'memo', e.target.value)}
                            placeholder="e.g., Freight charges"
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                          />
                        </div>

                        {/* Tax Code - Fetched from NetSuite */}
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Tax Code {currentBillData?.tax_rate && expense.tax_code_id ? '(Auto-applied from PDF)' : '(Optional)'}
                          </label>
                          <select
                            value={expense.tax_code_id || ''}
                            onChange={(e) => {
                              // Find selected tax code from fetched allTaxCodes (subsidiary-filtered)
                              const selectedTaxCode = allTaxCodes.find(tc => tc.id === e.target.value);
                              console.log(`🏷️ Tax code changed for expense ${expense.id}: ${e.target.value}`, selectedTaxCode);
                              handleExpenseChange(expense.id, 'tax_code_id', e.target.value);
                              if (selectedTaxCode && selectedTaxCode.rate) {
                                // Convert rate to percentage (0.09 -> 9)
                                handleExpenseChange(expense.id, 'tax_rate', parseFloat(selectedTaxCode.rate) * 100);
                              } else if (!e.target.value) {
                                // Clear tax rate if "No Tax Code" is selected
                                handleExpenseChange(expense.id, 'tax_rate', null);
                              }
                            }}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                          >
                            <option value="">-- No Tax Code --</option>
                            {allTaxCodes.length === 0 ? (
                              <option disabled>Loading tax codes...</option>
                            ) : (
                              (() => {
                                const options = [];

                                // 🔍 DEBUG: Log expense tax code and allTaxCodes for debugging
                                console.log(`🔍 Expense ${expense.id} tax_code_id:`, expense.tax_code_id);
                                console.log(`🔍 Expense ${expense.id} tax_rate:`, expense.tax_rate);
                                console.log(`🔍 Total allTaxCodes loaded:`, allTaxCodes.length);
                                if (expense.tax_code_id) {
                                  console.log(`🔍 Looking for tax code ${expense.tax_code_id} in allTaxCodes:`,
                                    allTaxCodes.find(tc => String(tc.id) === String(expense.tax_code_id)));
                                }

                                // Check if current expense's tax code is in the loaded list
                                const currentTaxExists = expense.tax_code_id &&
                                  allTaxCodes.some(tc => String(tc.id) === String(expense.tax_code_id));

                                // If tax code is set but not in list, add it as temporary option
                                if (expense.tax_code_id && !currentTaxExists) {
                                  const displayName = expense.tax_rate
                                    ? `${expense.tax_rate}% Tax (PO Tax Code ID: ${expense.tax_code_id})`
                                    : `Tax Code ${expense.tax_code_id} (Not in subsidiary - check PO)`;
                                  options.push(
                                    <option key={expense.tax_code_id} value={expense.tax_code_id}>
                                      {displayName}
                                    </option>
                                  );
                                  console.warn(`⚠️ Expense tax code ${expense.tax_code_id} not found in loaded tax codes (${allTaxCodes.length} codes). Showing as temporary option.`);
                                }

                                // Add all loaded tax codes
                                options.push(...allTaxCodes.map(taxCode => (
                                  <option key={taxCode.id} value={taxCode.id}>
                                    {taxCode.itemid} - {taxCode.description || taxCode.itemid} ({(parseFloat(taxCode.rate) * 100).toFixed(2)}%)
                                  </option>
                                )));

                                return options;
                              })()
                            )}
                          </select>
                          {expense.tax_code_id && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              ✓ Tax Code: {expense.tax_code_id}
                              {expense.tax_rate && ` (${expense.tax_rate}%)`}
                              {currentBillData?.tax_rate && parseFloat(expense.tax_rate) === parseFloat(currentBillData.tax_rate) ? ' (from PDF)' : ''}
                              {(() => {
                                const matchedTaxCode = allTaxCodes.find(tc => String(tc.id) === String(expense.tax_code_id));
                                return matchedTaxCode ? ` - ${matchedTaxCode.itemid}` : ' - Not in current subsidiary';
                              })()}
                            </p>
                          )}
                        </div>

                        {/* Location - 🔧 FIX: Use bill_data.all_locations (subsidiary-filtered) when available */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            📍 Location
                          </label>
                          <select
                            value={expense.location?.id || ''}
                            onChange={(e) => handleExpenseChange(expense.id, 'location', e.target.value ? { id: e.target.value } : null)}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                          >
                            <option value="">-- Select Location --</option>
                            {/* 🔧 FIX: Check array length, not just existence (empty array [] is truthy!) */}
                            {(getCurrentBillData()?.all_locations?.length ? getCurrentBillData()?.all_locations : getCurrentBillData()?.all_netsuite_locations?.length ? getCurrentBillData()?.all_netsuite_locations : allLocations).map(loc => (
                              <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Department - 🔧 FIX: Check array length, not just existence (empty array [] is truthy!) */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            🏛️ Department
                          </label>
                          <select
                            value={String(expense.department?.id || '')}
                            onChange={(e) => handleExpenseChange(expense.id, 'department', e.target.value ? { id: e.target.value } : null)}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                          >
                            <option value="">-- Select Department --</option>
                            {/* 🔧 FIX: Check array length, not just existence (empty array [] is truthy!) */}
                            {(getCurrentBillData()?.all_departments?.length ? getCurrentBillData()?.all_departments : getCurrentBillData()?.all_netsuite_departments?.length ? getCurrentBillData()?.all_netsuite_departments : allDepartments).map(dept => (
                              <option key={dept.id} value={String(dept.id)}>{dept.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Class - 🔧 FIX: Check array length, not just existence (empty array [] is truthy!) */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            🏷️ Class
                          </label>
                          <select
                            value={expense.class?.id || ''}
                            onChange={(e) => handleExpenseChange(expense.id, 'class', e.target.value ? { id: e.target.value } : null)}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                          >
                            <option value="">-- Select Class --</option>
                            {/* 🔧 FIX: Check array length, not just existence (empty array [] is truthy!) */}
                            {(getCurrentBillData()?.all_classes?.length ? getCurrentBillData()?.all_classes : getCurrentBillData()?.all_netsuite_classes?.length ? getCurrentBillData()?.all_netsuite_classes : allClasses).map(cls => (
                              <option key={cls.id} value={cls.id}>{cls.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Customer */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            👤 Customer
                          </label>
                          <select
                            value={expense.customer?.id || ''}
                            onChange={(e) => handleExpenseChange(expense.id, 'customer', e.target.value ? { id: e.target.value } : null)}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                          >
                            <option value="">-- Select Customer --</option>
                            {allCustomers.map(cust => (
                              <option key={cust.id} value={cust.id}>{cust.companyname || cust.entityid}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}

              {expenses.length > 0 && (
                <div className="mt-4 pt-3 border-t border-orange-200 dark:border-orange-700">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Total Expenses:
                    </span>
                    <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                      {getCurrencySymbol()}{expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

          {/* Bill Summary */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              💰 Bill Summary
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-500">Line Items Subtotal:</span>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {getCurrencySymbol()}{(() => {
                    if (isPOLinked) {
                      return getPoData()?.line_items
                        ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number))
                        .reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0)
                        .toFixed(2) || '0.00';
                    } else {
                      // For standalone: sum all non-removed line items
                      return (currentBillData?.line_items
                        ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number))
                        .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) || 0)
                        .toFixed(2);
                    }
                  })()}
                </p>
              </div>
              {(() => {
                // Show tax for both PO and standalone bills
                if (isPOLinked) {
                  // ✅ PO Bills: Calculate tax from PO line items
                  const poLineItems = getPoData()?.line_items
                    ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)) || [];

                  if (poLineItems.length === 0) return null;

                  const calculatedTax = poLineItems.reduce((sum, item) => {
                    const amount = parseFloat(item.total_amount || 0);

                    // Get tax rate for this PO line item
                    let taxRate = 0;
                    if (item.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                      const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                      if (matchingTaxCode && matchingTaxCode.rate) {
                        taxRate = parseFloat(matchingTaxCode.rate);
                      }
                    } else if (item.tax_rate) {
                      taxRate = parseFloat(item.tax_rate) / 100;
                    }

                    return sum + (amount * taxRate);
                  }, 0);

                  // Only show if there's actual tax
                  if (calculatedTax === 0) return null;

                  return (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Tax (from PO):</span>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {getCurrencySymbol()}{calculatedTax.toFixed(2)}
                      </p>
                    </div>
                  );
                } else {
                  // Standalone Bills: Calculate tax from line items
                  const activeLineItems = currentBillData.line_items
                    ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)) || [];

                  // Only show tax if there are active line items
                  if (activeLineItems.length === 0) return null;

                  // ✅ DYNAMIC TAX: Calculate tax per line item based on each item's tax_rate
                  const calculatedTax = activeLineItems.reduce((sum, item) => {
                    const amount = parseFloat(item.amount || 0);

                    // Get tax rate for this specific line item
                    let taxRate = 0;
                    if (item.tax_rate) {
                      taxRate = parseFloat(item.tax_rate) / 100; // tax_rate is stored as percentage (e.g., 18)
                    } else if (item.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                      // Dynamic lookup from allTaxCodes if tax_rate not set
                      const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                      if (matchingTaxCode && matchingTaxCode.rate) {
                        taxRate = parseFloat(matchingTaxCode.rate); // rate is decimal (e.g., 0.18)
                      }
                    } else if (currentBillData.tax_rate) {
                      // ✅ FALLBACK: Use global tax_rate for old bills (applied per item)
                      taxRate = parseFloat(currentBillData.tax_rate) / 100;
                    }

                    return sum + (amount * taxRate);
                  }, 0);

                  // Only show if there's actual tax
                  if (calculatedTax === 0) return null;

                  return (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Tax (calculated per item):</span>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {getCurrencySymbol()}{calculatedTax.toFixed(2)}
                      </p>
                    </div>
                  );
                }
              })()}
              {expenses.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Expenses Total (with tax):</span>
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                    {getCurrencySymbol()}{expenses.reduce((sum, exp) => {
                      const amount = parseFloat(exp.amount) || 0;

                      // ✅ DYNAMIC FIX: Look up tax rate from allTaxCodes if tax_rate not available
                      let taxRate = 0;
                      if (exp.tax_rate) {
                        taxRate = parseFloat(exp.tax_rate) / 100; // User-selected rate
                      } else if (exp.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                        const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(exp.tax_code_id));
                        if (matchingTaxCode && matchingTaxCode.rate) {
                          taxRate = parseFloat(matchingTaxCode.rate); // Dynamic lookup
                        }
                      }

                      const taxAmount = exp.tax_code_id ? (amount * taxRate) : 0;
                      return sum + amount + taxAmount;
                    }, 0).toFixed(2)}
                  </p>
                </div>
              )}
              {/* 💸 Global Discount Display - Only show if bill has global discount */}
              {(currentBillData?.discount_rate !== null && currentBillData?.discount_rate !== undefined) && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Discount Rate (Global):</span>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">
                    {currentBillData.discount_rate}%
                  </p>
                </div>
              )}
              {(currentBillData?.discount_amount !== null && currentBillData?.discount_amount !== undefined) && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Discount Amount (Global):</span>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">
                    {getCurrencySymbol()}{currentBillData.discount_amount}
                  </p>
                </div>
              )}
              <div className={expenses.length > 0 ? "col-span-2 pt-3 border-t border-purple-200 dark:border-purple-700" : ""}>
                <span className="text-sm font-medium text-gray-500">Grand Total:</span>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {getCurrencySymbol()}{(() => {
                    // 📄 USE PDF TOTAL: Display total_amount from PDF extraction
                    const displayData = getCurrentBillData();

                    // ✅ Use total_amount from PDF (constant value from extraction)
                    if (displayData.total_amount !== null && displayData.total_amount !== undefined) {
                      return formatAmount(displayData.total_amount);
                    }
                    if (displayData.bill_total_amount !== null && displayData.bill_total_amount !== undefined) {
                      return formatAmount(displayData.bill_total_amount);
                    }
                    let lineItemsSubtotal = 0;

                    if (isPOLinked) {
                      // ✅ PO Bills: Sum total_amount + tax from non-removed line items
                      const poLineItems = getPoData()?.line_items
                        ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)) || [];

                      lineItemsSubtotal = poLineItems.reduce((sum, item) => {
                        const amount = parseFloat(item.total_amount || 0);

                        // Calculate tax for this PO line item
                        let taxRate = 0;
                        if (item.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                          const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                          if (matchingTaxCode && matchingTaxCode.rate) {
                            taxRate = parseFloat(matchingTaxCode.rate);
                          }
                        } else if (item.tax_rate) {
                          taxRate = parseFloat(item.tax_rate) / 100;
                        }

                        const taxAmount = amount * taxRate;
                        return sum + amount + taxAmount;
                      }, 0);
                    } else {
                      // Standalone Bills: Sum line items + dynamically calculated tax
                      const activeLineItems = displayData.line_items
                        ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)) || [];

                      // ✅ DYNAMIC TAX PER LINE ITEM: Calculate items + tax for each line
                      lineItemsSubtotal = activeLineItems.reduce((sum, item) => {
                        const amount = parseFloat(item.amount || 0);

                        // Get tax rate for this specific line item
                        let taxRate = 0;
                        if (item.tax_rate) {
                          taxRate = parseFloat(item.tax_rate) / 100; // tax_rate is stored as percentage
                        } else if (item.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                          // Dynamic lookup from allTaxCodes if tax_rate not set
                          const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                          if (matchingTaxCode && matchingTaxCode.rate) {
                            taxRate = parseFloat(matchingTaxCode.rate); // rate is decimal
                          }
                        } else if (displayData.tax_rate) {
                          // ✅ FALLBACK: Use global tax_rate for old bills (applied per item)
                          taxRate = parseFloat(displayData.tax_rate) / 100;
                        }

                        const taxAmount = amount * taxRate;
                        return sum + amount + taxAmount;
                      }, 0);
                    }

                    // Calculate expenses total WITH TAX - DYNAMIC LOOKUP
                    const expensesTotal = expenses.reduce((sum, exp) => {
                      const amount = parseFloat(exp.amount) || 0;

                      // ✅ DYNAMIC FIX: Look up tax rate from allTaxCodes if tax_rate not available
                      let taxRate = 0;
                      if (exp.tax_rate) {
                        taxRate = parseFloat(exp.tax_rate) / 100; // User-selected rate
                      } else if (exp.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                        const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(exp.tax_code_id));
                        if (matchingTaxCode && matchingTaxCode.rate) {
                          taxRate = parseFloat(matchingTaxCode.rate); // Dynamic lookup
                        }
                      }

                      const taxAmount = exp.tax_code_id ? (amount * taxRate) : 0;
                      return sum + amount + taxAmount;
                    }, 0);

                    return formatAmount(lineItemsSubtotal + expensesTotal);
                  })()}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Bill Type:</span>
                <p className="text-gray-900 dark:text-white">
                  {isPOLinked ? `PO-Linked (${getPoData()?.po_number})` : 'Standalone Bill'}
                </p>
              </div>
            </div>
          </div>

          {/* NetSuite Bill Creation Fields */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              🔧 NetSuite Bill Creation Fields
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Subsidiary Section with Load All button */}
              <div className="col-span-2 pb-2 border-b border-blue-200 dark:border-blue-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-500">🏢 Subsidiary:</span>
                  <button
                    type="button"
                    onClick={handleLoadAllSubsidiaries}
                    disabled={loadingSubsidiaries}
                    className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800"
                  >
                    {loadingSubsidiaries ? (
                      <>⏳ Loading...</>
                    ) : (
                      <>🏢 Load All</>
                    )}
                  </button>
                </div>

                {/* Show dropdown if subsidiaries are loaded */}
                {allSubsidiaries.length > 0 ? (
                  <div className="mt-1">
                    <select
                      value={selectedSubsidiary?.id || getCurrentSubsidiary()?.subsidiary_id || ''}
                      onChange={handleSubsidiaryChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">-- Select Subsidiary --</option>
                      {allSubsidiaries.map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.name} {sub.country ? `(${sub.country})` : ''} - ID: {sub.id}
                        </option>
                      ))}
                    </select>
                    {selectedSubsidiary && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        ✅ Changed from AI selection to: {selectedSubsidiary.name}
                      </p>
                    )}
                  </div>
                ) : (
                  /* Show current subsidiary info if not loading all */
                  getCurrentSubsidiary() ? (
                    <div className="mt-1">
                      <p className="text-gray-900 dark:text-white font-semibold">
                        {getCurrentSubsidiary().subsidiary_name || getCurrentSubsidiary().name || `Subsidiary ID: ${getCurrentSubsidiary().subsidiary_id || getCurrentSubsidiary().id}`}
                        {(getCurrentSubsidiary().subsidiary_name || getCurrentSubsidiary().name) && (
                          <span className="ml-2 text-sm text-gray-500">
                            (ID: {getCurrentSubsidiary().subsidiary_id || getCurrentSubsidiary().id})
                          </span>
                        )}
                      </p>
                      {(getCurrentSubsidiary().subsidiary_legal_name || getCurrentSubsidiary().legalname) && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Legal Name: {getCurrentSubsidiary().subsidiary_legal_name || getCurrentSubsidiary().legalname}
                        </p>
                      )}
                      {getCurrentSubsidiary().country && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Country: {getCurrentSubsidiary().country}
                        </p>
                      )}
                      <p className="text-xs text-blue-500 mt-1">
                        💡 Click "Load All" to change subsidiary
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 italic">
                      Subsidiary not identified - Click "Load All" to select
                    </p>
                  )
                )}
              </div>

              <div>
                <span className="text-sm font-medium text-gray-500">Entity (Vendor):</span>
                <p className="text-gray-900 dark:text-white">{selectedVendor || getCurrentBillData().vendor?.entity_id}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Transaction Date:{isPOLinked && <span className="ml-1 text-xs text-gray-400">(from PO — editable)</span>}</span>
                <input
                  type="date"
                  value={billTranDate}
                  onChange={(e) => { setBillTranDate(e.target.value); setBillTranDateTouched(true); }}
                  className="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Transaction ID:</span>
                <p className="text-gray-900 dark:text-white">{isPOLinked ? getPoData()?.po_number : currentBillData?.invoice_number}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Payment Terms:</span>
                <p className="text-gray-900 dark:text-white">{getCurrentBillData().payment_terms || getCurrentBillData().vendor?.termname || getCurrentBillData().vendor?.termName || getCurrentBillData().vendor?.netsuite_term_name || 'Standard'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Due Date:<span className="ml-1 text-xs text-gray-400">(auto unless changed)</span></span>
                <input
                  type="date"
                  value={billDueDate}
                  onChange={(e) => { setBillDueDate(e.target.value); setBillDueDateTouched(true); }}
                  className="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500"
                />
                {!billDueDate && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    Enter a value only if you need to override the calculated date.
                  </p>
                )}
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Memo:</span>
                <input
                  type="text"
                  value={billMemo}
                  onChange={(e) => { setBillMemo(e.target.value); setBillMemoTouched(true); }}
                  placeholder="Optional memo"
                  className="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Bill Total Amount:</span>
                <p className="text-gray-900 dark:text-white font-semibold">
                  {getCurrencySymbol()}{(() => {
                    // 📄 USE PDF TOTAL: Display total_amount from PDF extraction (same as Grand Total)
                    const displayData = getCurrentBillData();

                    // ✅ Use total_amount from PDF (constant value from extraction)
                    if (displayData.total_amount !== null && displayData.total_amount !== undefined) {
                      return formatAmount(displayData.total_amount);
                    }
                    if (displayData.bill_total_amount !== null && displayData.bill_total_amount !== undefined) {
                      return formatAmount(displayData.bill_total_amount);
                    }
                    let lineItemsSubtotal = 0;

                    if (isPOLinked) {
                      // ✅ PO Bills: Sum total_amount + tax from non-removed line items
                      const poLineItems = getPoData()?.line_items
                        ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)) || [];

                      lineItemsSubtotal = poLineItems.reduce((sum, item) => {
                        const amount = parseFloat(item.total_amount || 0);

                        // Calculate tax for this PO line item
                        let taxRate = 0;
                        if (item.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                          const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                          if (matchingTaxCode && matchingTaxCode.rate) {
                            taxRate = parseFloat(matchingTaxCode.rate);
                          }
                        } else if (item.tax_rate) {
                          taxRate = parseFloat(item.tax_rate) / 100;
                        }

                        const taxAmount = amount * taxRate;
                        return sum + amount + taxAmount;
                      }, 0);
                    } else {
                      // Standalone Bills: Sum line items + dynamically calculated tax PER ITEM
                      const activeLineItems = displayData.line_items
                        ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)) || [];

                      // ✅ DYNAMIC TAX PER LINE ITEM: Calculate items + tax for each line
                      lineItemsSubtotal = activeLineItems.reduce((sum, item) => {
                        const amount = parseFloat(item.amount || 0);

                        // Get tax rate for this specific line item
                        let taxRate = 0;
                        if (item.tax_rate) {
                          taxRate = parseFloat(item.tax_rate) / 100;
                        } else if (item.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                          const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                          if (matchingTaxCode && matchingTaxCode.rate) {
                            taxRate = parseFloat(matchingTaxCode.rate);
                          }
                        } else if (displayData.tax_rate) {
                          // ✅ FALLBACK: Use global tax_rate for old bills (applied per item)
                          taxRate = parseFloat(displayData.tax_rate) / 100;
                        }

                        const taxAmount = amount * taxRate;
                        return sum + amount + taxAmount;
                      }, 0);
                    }

                    // Calculate expenses total WITH TAX - DYNAMIC LOOKUP
                    const expensesTotal = expenses.reduce((sum, exp) => {
                      const amount = parseFloat(exp.amount) || 0;

                      // ✅ DYNAMIC FIX: Look up tax rate from allTaxCodes if tax_rate not available
                      let taxRate = 0;
                      if (exp.tax_rate) {
                        taxRate = parseFloat(exp.tax_rate) / 100; // User-selected rate
                      } else if (exp.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                        const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(exp.tax_code_id));
                        if (matchingTaxCode && matchingTaxCode.rate) {
                          taxRate = parseFloat(matchingTaxCode.rate); // Dynamic lookup
                        }
                      }

                      const taxAmount = exp.tax_code_id ? (amount * taxRate) : 0;
                      return sum + amount + taxAmount;
                    }, 0);
                    return formatAmount(lineItemsSubtotal + expensesTotal);
                  })()}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Tax Amount: <span className="text-xs text-blue-600 dark:text-blue-400">(Auto-calculated by NetSuite)</span></span>
                <p className="text-gray-900 dark:text-white font-semibold">
                  {getCurrencySymbol()}{(() => {
                    const displayData = getCurrentBillData();
                    if (isPOLinked) {
                      // ✅ Calculate tax for PO-linked bills from line items
                      const poLineItems = getPoData()?.line_items
                        ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)) || [];

                      if (poLineItems.length === 0) {
                        return '0.00';
                      }

                      // Calculate tax per line item from PO
                      const calculatedTax = poLineItems.reduce((sum, item) => {
                        const amount = parseFloat(item.amount || item.total_amount || 0);
                        let taxRate = 0;

                        // Get tax rate from tax_code_id (as percentage, e.g., 9)
                        if (item.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                          const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                          if (matchingTaxCode && matchingTaxCode.rate) {
                            // Tax code rate is stored as decimal (0.09), convert to percentage (9)
                            taxRate = parseFloat(matchingTaxCode.rate) * 100;
                          }
                        } else if (item.tax_rate) {
                          // Fallback: use tax_rate from PO line item
                          taxRate = parseFloat(item.tax_rate);
                        }

                        // Calculate: amount * taxRate / 100
                        return sum + (amount * taxRate / 100);
                      }, 0);

                      return formatAmount(calculatedTax);
                    } else {
                      // 🔄 DYNAMIC TAX PER LINE ITEM: Recalculate based on active (non-removed) line items
                      const activeLineItems = displayData.line_items
                        ?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)) || [];

                      // If no active line items, tax should be 0
                      if (activeLineItems.length === 0) {
                        return '0.00';
                      }

                      // ✅ Calculate tax per line item
                      const calculatedTax = activeLineItems.reduce((sum, item) => {
                        const amount = parseFloat(item.amount || 0);

                        // Get tax rate for this specific line item (as percentage, e.g., 9)
                        let taxRate = 0;
                        if (item.tax_rate) {
                          taxRate = parseFloat(item.tax_rate);
                        } else if (item.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                          const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                          if (matchingTaxCode && matchingTaxCode.rate) {
                            // Tax code rate is stored as decimal (0.09), convert to percentage (9)
                            taxRate = parseFloat(matchingTaxCode.rate) * 100;
                          }
                        } else if (displayData.tax_rate) {
                          // ✅ FALLBACK: Use global tax_rate for old bills (applied per item)
                          taxRate = parseFloat(displayData.tax_rate);
                        }

                        // Calculate: amount * taxRate / 100
                        return sum + (amount * taxRate / 100);
                      }, 0);

                      return calculatedTax.toFixed(2);
                    }
                  })()}
                </p>
              </div>

              {(modifiedBillData || billData).po_reference && (
                <div>
                  <span className="text-sm font-medium text-gray-500">PO Reference:</span>
                  <p className="text-gray-900 dark:text-white">{(modifiedBillData || billData).po_reference}</p>
                </div>
              )}
            </div>

            <div className="mt-4">
              {/* ✅ MISMATCH CASE: Show both PO and PDF line items for comparison */}
              {/* Only show comparison when there are ACTUAL discrepancies detected */}
              {hasLineItemMismatch() ? (
                <>
                  {/* PO Line Items Section */}
                  <div className="mb-6">
                    <h4 className="text-md font-medium text-blue-900 dark:text-blue-300 mb-2 flex items-center gap-2">
                      <span className="text-lg">📋</span>
                      PO Line Items (Purchase Order):
                    </h4>
                    <div className="space-y-2 bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-700">
                      {getPoData()?.line_items?.map((item, index) => (
                        <div key={index} className="bg-white dark:bg-gray-700 p-3 rounded border">
                          <div className="grid grid-cols-5 gap-2 text-sm">
                            <div>
                              <span className="font-medium">Item:</span>
                              <p className="text-gray-900 dark:text-white mt-1">{item?.item_name}</p>
                              {item?.item_description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                  {item.item_description}
                                </p>
                              )}
                            </div>
                            <div><span className="font-medium">Qty:</span> {effectiveQtyFor(item)}</div>
                            <div><span className="font-medium">Rate:</span> ${item?.rate}</div>
                            <div>
                              <span className="font-medium">Tax:</span>
                              {(() => {
                                // First check if there's a tax_code_id and look up the full tax code info
                                if (item?.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                                  const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                                  if (matchingTaxCode) {
                                    const rate = matchingTaxCode.rate ? (parseFloat(matchingTaxCode.rate) * 100).toFixed(2) : item?.tax_rate;
                                    return (
                                      <div className="text-xs mt-1">
                                        <div className="font-semibold text-gray-900 dark:text-white">
                                          {matchingTaxCode.itemid || 'Tax Code'}
                                        </div>
                                        {rate && <div className="text-gray-600 dark:text-gray-400">{rate}%</div>}
                                      </div>
                                    );
                                  }
                                }
                                // Fallback to just showing tax_rate percentage
                                if (item?.tax_rate) {
                                  return ` ${item.tax_rate}%`;
                                }
                                return ' N/A';
                              })()}
                            </div>
                            <div><span className="font-medium">Amount:</span> ${item?.total_amount}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* PDF Line Items Section */}
                  <div className="mb-4">
                    <h4 className="text-md font-medium text-orange-900 dark:text-orange-300 mb-2 flex items-center gap-2">
                      <span className="text-lg">📄</span>
                      PDF Line Items (Actual Bill):
                    </h4>
                    <div className="space-y-2 bg-orange-50 dark:bg-orange-900/20 p-3 rounded border border-orange-200 dark:border-orange-700">
                      {getCurrentBillData().original_pdf_line_items?.map((item, index) => (
                        <div key={index} className="bg-white dark:bg-gray-700 p-3 rounded border">
                          <div className="grid grid-cols-5 gap-2 text-sm">
                            <div>
                              <span className="font-medium">Description:</span>
                              <p className="text-gray-900 dark:text-white mt-1 text-xs line-clamp-3">
                                {item?.description}
                              </p>
                            </div>
                            <div><span className="font-medium">Qty:</span> {item?.quantity}</div>
                            <div><span className="font-medium">Rate:</span> ${item?.unit_price}</div>
                            <div>
                              <span className="font-medium">Tax:</span>
                              {(() => {
                                // First check if there's a tax_code_id and look up the full tax code info
                                if (item?.tax_code_id && allTaxCodes && allTaxCodes.length > 0) {
                                  const matchingTaxCode = allTaxCodes.find(tc => String(tc.id) === String(item.tax_code_id));
                                  if (matchingTaxCode) {
                                    const rate = matchingTaxCode.rate ? (parseFloat(matchingTaxCode.rate) * 100).toFixed(2) : item?.tax_rate;
                                    return (
                                      <div className="text-xs mt-1">
                                        <div className="font-semibold text-gray-900 dark:text-white">
                                          {matchingTaxCode.itemid || 'Tax Code'}
                                        </div>
                                        {rate && <div className="text-gray-600 dark:text-gray-400">{rate}%</div>}
                                      </div>
                                    );
                                  }
                                }
                                // Fallback to just showing tax_rate percentage
                                if (item?.tax_rate) {
                                  return ` ${item.tax_rate}%`;
                                }
                                return ' N/A';
                              })()}
                            </div>
                            <div><span className="font-medium">Amount:</span> ${item?.amount || item?.extended_price}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mismatch Notice */}
                  <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      ⚠️ <strong>Line Item Mismatch Detected:</strong> The PO has {getPoData()?.line_items?.length} line item(s)
                      but the PDF has {getCurrentBillData().original_pdf_line_items?.length} line item(s).
                      Review both lists above and use the AI chat to resolve discrepancies.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* Normal case: Show single list */}
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-2">Line Items:</h4>
                </>
              )}

              {/* Regular line items display (for non-mismatch or standalone bills) */}
              {!(isPOLinked && getCurrentBillData().original_pdf_line_items && getPoData()?.line_items &&
                 getPoData()?.line_items?.length > 0) && (
                <div className="space-y-2">
                  {/* ✅ Read from modifiedBillData if available (AI chat updates) */}
                  {(isPOLinked ? getPoData()?.line_items : getCurrentBillData().line_items)?.filter(item => item !== null && item !== undefined && !removedLineItems.has(item?.line_number)).map((item, index) => (
                  <div key={index} className="bg-white dark:bg-gray-700 p-3 rounded border">
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className="font-medium">Item:</span>
                        <div className="mt-1">
                          {isPOLinked ? (
                            <p className="text-gray-900 dark:text-white">{item?.item_name}</p>
                          ) : (
                            <>
                              <p className="text-gray-900 dark:text-white font-medium">
                                {/* ✅ SAFE: Check matched NetSuite item first (AI-updated), handle array/object, then fall back */}
                                {(() => {
                                  const matchedItem = (item?.matched_netsuite_item && typeof item.matched_netsuite_item === 'object')
                                    ? (Array.isArray(item.matched_netsuite_item) ? item.matched_netsuite_item[0] : item.matched_netsuite_item)
                                    : null;

                                  return matchedItem?.item_name_code ||
                                         matchedItem?.netsuite_item_name_code ||
                                         matchedItem?.item_display_name ||
                                         item?.item_search?.extracted_sku_or_code ||
                                         item?.description ||
                                         item?.item_name ||
                                         'N/A';
                                })()}
                              </p>
                              {(() => {
                                const matchedItem = (item?.matched_netsuite_item && typeof item.matched_netsuite_item === 'object')
                                  ? (Array.isArray(item.matched_netsuite_item) ? item.matched_netsuite_item[0] : item.matched_netsuite_item)
                                  : null;
                                const desc = matchedItem?.item_description || item?.description;

                                return desc ? (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate" title={desc}>
                                    {desc.substring(0, 40)}{desc.length > 40 ? '...' : ''}
                                  </p>
                                ) : null;
                              })()}
                              {selectedItems[item?.line_number] === 'create_new' && (
                                <span className="text-xs text-blue-600 dark:text-blue-400">→ Will create as new item</span>
                              )}
                              {(() => {
                                const selectedItemId = selectedItems[item?.line_number];
                                // Check if user manually selected an item (not matched, not create_new)
                                if (selectedItemId && selectedItemId !== 'create_new') {
                                  // Find selected item details from available sources - SAFE: validate arrays
                                  const itemsSource = (Array.isArray(lineItemsAllItems[item?.line_number])
                                    ? lineItemsAllItems[item?.line_number]
                                    : (Array.isArray(item.available_items) ? item.available_items : []));
                                  const selectedItem = itemsSource.find(nsItem =>
                                    String(nsItem?.netsuite_item_id) === String(selectedItemId)
                                  );

                                  if (selectedItem) {
                                    return (
                                      <div className="mt-1 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
                                        <p className="text-xs font-semibold text-green-700 dark:text-green-300">✓ Selected NetSuite Item:</p>
                                        <p className="text-xs font-bold text-green-900 dark:text-green-100 mt-0.5">
                                          {selectedItem.item_name_code}
                                        </p>
                                        <p className="text-xs text-green-800 dark:text-green-200">
                                          {(selectedItem.item_description || selectedItem.item_display_name || '').substring(0, 80)}
                                        </p>
                                      </div>
                                    );
                                  }
                                }
                                // Show matched item if AI auto-matched it
                                else if (item?.matched_netsuite_item) {
                                  return (
                                    <div className="mt-1 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
                                      <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">✓ AI Matched Item:</p>
                                      <p className="text-xs font-bold text-blue-900 dark:text-blue-100 mt-0.5">
                                        {item.matched_netsuite_item.item_name_code || item.matched_netsuite_item.netsuite_item_name_code}
                                      </p>
                                      <p className="text-xs text-blue-800 dark:text-blue-200">
                                        {(item.matched_netsuite_item.item_description || item.matched_netsuite_item.item_display_name || '').substring(0, 80)}
                                      </p>
                                    </div>
                                  );
                                }
                                // ✅ FIX: Check if AI assistant selected an item via chat (selected_netsuite_item_id field)
                                else if (item?.selected_netsuite_item_id) {
                                  return (
                                    <div className="mt-1 p-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded">
                                      <p className="text-xs font-semibold text-purple-700 dark:text-purple-300">✓ AI Assistant Selected</p>
                                      <p className="text-xs text-purple-800 dark:text-purple-200">
                                        Item ID: {item.selected_netsuite_item_id}
                                      </p>
                                    </div>
                                  );
                                }
                                // Show warning if nothing selected
                                else if (!selectedItemId) {
                                  return (
                                    <span className="text-xs text-red-600 dark:text-red-400 block mt-1">⚠️ NOT SELECTED</span>
                                  );
                                }
                                return null;
                              })()}
                            </>
                          )}
                        </div>
                      </div>
                      <div><span className="font-medium">Qty:</span> {isPOLinked ? effectiveQtyFor(item) : (item?.quantity || item?.open_for_billing_quantity)}</div>
                      <div><span className="font-medium">Rate:</span> ${isPOLinked ? item?.rate : (item?.unit_price || item?.rate || '0.00')}</div>
                      <div><span className="font-medium">Amount:</span> ${isPOLinked ? item?.total_amount : (item?.extended_price || item?.amount || '0.00')}</div>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>

            {/* Expense Items (if any) */}
            {expenses.length > 0 && (
              <div className="mt-4">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-2">Expense Items:</h4>
                <div className="space-y-2">
                  {expenses.map((expense, index) => (
                    <div key={expense.id} className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded border border-orange-200 dark:border-orange-700">
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <span className="font-medium">Account:</span> {expense.account?.id || 'N/A'}
                          {expense.name && <span className="text-xs text-gray-500 ml-1">({expense.name})</span>}
                        </div>
                        <div><span className="font-medium">Amount:</span> ${parseFloat(expense.amount || 0).toFixed(2)}</div>
                        <div className="col-span-2"><span className="font-medium">Memo:</span> {expense.memo || 'N/A'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Chat Assistant for Bill Modifications */}
        <div className="border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setChatOpen(!chatOpen)}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">💬</span>
              <span className="font-medium text-gray-900 dark:text-white">
                AI Assistant - Modify Bill Data
              </span>
              {modifiedBillData && (
                <span className="text-xs bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 px-2 py-1 rounded">
                  Modified
                </span>
              )}
            </div>
            <span className="text-gray-500">{chatOpen ? '▼' : '▶'}</span>
          </button>

          {chatOpen && (
            <div className="p-4 bg-gray-50 dark:bg-gray-900 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Ask me to modify any bill details like dates, amounts, vendor, line items, etc.
                <br />
                <span className="text-xs italic">Example: "Change the bill date to March 15, 2025" or "Update line item 1 quantity to 5"</span>
              </p>

              {/* Chat Messages */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto p-3 space-y-2">
                {chatMessages.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No messages yet. Start a conversation to modify bill data.
                  </p>
                ) : (
                  chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded ${
                        msg.role === 'user'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 ml-8'
                          : msg.role === 'assistant'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-100 mr-8'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-900 dark:text-yellow-100 text-center'
                      }`}
                    >
                      <div className="text-xs font-semibold mb-1">
                        {msg.role === 'user' ? '👤 You' : msg.role === 'assistant' ? '🤖 AI' : '⚙️ System'}
                      </div>
                      <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                    </div>
                  ))
                )}
                <div ref={chatMessagesEndRef} />
              </div>

              {/* Chat Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !chatLoading) {
                      handleSendChatMessage();
                    }
                  }}
                  placeholder="Type your request here... (e.g., 'Change bill date to tomorrow')"
                  disabled={chatLoading}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                />
                <button
                  onClick={handleSendChatMessage}
                  disabled={chatLoading || !chatInput.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {chatLoading ? '⏳' : '📤 Send'}
                </button>
              </div>

              {/* Create Modified Bill Button */}
              {modifiedBillData && (
                <div className="pt-2 border-t border-gray-300 dark:border-gray-600">
                  <button
                    onClick={handleConfirmModifiedBill}
                    disabled={loading}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? '⏳ Creating Modified Bill...' : '✅ Create Bill with AI Modifications'}
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                    This will create the bill using the AI-modified data. Or use the "Create NetSuite Bill" button below for the original data.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bill Creation Status Display */}
        {billCreationStatus && (
          <div className={`p-6 border-t border-gray-200 dark:border-gray-700 ${
            billCreationStatus === 'success'
              ? 'bg-green-50 dark:bg-green-900/20'
              : 'bg-red-50 dark:bg-red-900/20'
          }`}>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 text-2xl">
                {billCreationStatus === 'success' ? '✅' : '❌'}
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-semibold mb-2 ${
                  billCreationStatus === 'success'
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  {billCreationStatus === 'success' ? 'Bill Created Successfully!' : 'Bill Creation Failed'}
                </h3>
                <p className={`text-sm mb-3 ${
                  billCreationStatus === 'success'
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-red-700 dark:text-red-300'
                }`}>
                  {billCreationMessage}
                </p>

                {/* NetSuite Bill URL */}
                {billCreationStatus === 'success' && netSuiteBillUrl && (
                  <div className="mt-3">
                    <a
                      href={netSuiteBillUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      🔗 Open Bill in NetSuite
                      <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}

                {/* Error - Retry Instructions */}
                {billCreationStatus === 'error' && (
                  <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      💡 <strong>You can fix the issue:</strong>
                    </p>
                    <ul className="text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside mt-2 space-y-1">
                      <li>Use the AI Assistant chat to modify bill data</li>
                      <li>Or manually edit the fields above</li>
                      <li>Then click "Create NetSuite Bill" again to retry</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Clear Status Button */}
              {billCreationStatus === 'success' && (
                <button
                  onClick={() => {
                    setBillCreationStatus(null);
                    setBillCreationMessage('');
                    setNetSuiteBillUrl('');
                  }}
                  className="flex-shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Clear status"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Partial-receipt (IR) warning — repeated near the actions, in red */}
        {!isMinimized && getCurrentBillData()?.partial_ir && getCurrentBillData()?.partial_ir_remark && (
          <div className="px-6 py-3 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30">
            <div className="flex items-start gap-2 text-red-700 dark:text-red-300">
              <span className="text-lg leading-none">⚠️</span>
              <div className="text-sm">
                <span className="font-semibold">Partial receipt (IR): </span>
                {getCurrentBillData().partial_ir_remark.replace(/^Partial receipt \(IR\):\s*/, '')}
                <div className="text-xs mt-1 opacity-80">Only the received quantity will be billed — review before proceeding.</div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-4">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {loading ? 'Cancelling...' : 'Cancel'}
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleConfirmBill();
            }}
            disabled={loading || (billCreationStatus === 'success')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={billCreationStatus === 'success' ? 'Bill already created successfully' : ''}
          >
            {loading ? 'Creating Bill...' : billCreationStatus === 'error' ? 'Retry Bill Creation' : 'Create NetSuite Bill'}
          </button>
        </div>
          </>
        )}

        {/* Minimized View - Show status only */}
        {isMinimized && billCreationStatus && (
          <div className="p-4">
            <div className={`flex items-center space-x-2 text-sm ${
              billCreationStatus === 'success'
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}>
              <span className="text-lg">{billCreationStatus === 'success' ? '✅' : '❌'}</span>
              <span className="font-medium">
                {billCreationStatus === 'success' ? 'Bill Created!' : 'Creation Failed - Click to expand'}
              </span>
            </div>
            {billCreationStatus === 'success' && netSuiteBillUrl && (
              <a
                href={netSuiteBillUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                🔗 Open in NetSuite
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PreBillCreationModal;